import {
  Grid3X3,
  PhoneCall,
  StopCircle,
  CheckCircle,
  Clock,
  MessageSquare,
  Mail,
  Calendar,
  Bell,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  SkipForward,
  PhoneOutgoing,
  User,
  Volume2,
  Loader2,
  Pause,
  Play,
  MapPin,
  ExternalLink,
  Users,
  UserPlus,
  Hash,
  Ban,
  StickyNote,
  ChevronDown,
  ChevronRight,
  Shield,
  XCircle,
  Eye,
  EyeOff,
  Voicemail,
  PhoneForwarded,
} from "lucide-react";
import { InlineAddPartyPanel } from '@/app/_module/components/InlineAddPartyPanel';
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useToast } from "@/app/_module/hooks/use-toast";
import InlineTransferPanel from "@/app/_module/components/InlineTransferPanel";
import { QuickDispositionButtons } from "@/app/_module/components/QuickDispositionButtons";
import { useDncConfirm } from "@/app/_module/hooks/useDncConfirm";
import { useRouter } from "next/navigation";
import { getSupabaseClient } from "../utils/supabaseClient";

import { type Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import { useUpdateLeadStatus, useLead } from "@/app/(authenticated)/leads/_modules/hooks/queries";
import NoSaleReasonModal from "@/app/(authenticated)/leads/_modules/components/NoSaleReasonModal";
import { LeadStatus } from "@/app/_module/constants/leadStatus";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { LeadDetailsPanel } from "@/app/(authenticated)/leads/_modules/components/LeadDetailsPanel";
import { InsuraABotKeyGrabber } from "@/app/(authenticated)/communication/_modules/components/InsuraABotKeyGrabber";
import { DTMFPollingWrapper, type DTMFPollingWrapperRef } from "@/app/(authenticated)/communication/_modules/components/DTMFPollingWrapper";
import type { DTMFDigit } from "@/app/_module/services/dtmfService";
import { SendSmsModal } from "@/app/(authenticated)/communication/_modules/components/SendSmsModal";
import { SendEmailModal } from "@/app/(authenticated)/communication/_modules/components/SendEmailModal";
import { NewAppointmentDialog } from "@/app/(authenticated)/appointments/_modules/components/NewAppointmentDialog";
import { NewReminderDialog } from "@/app/(authenticated)/reminders/_modules/components/NewReminderDialog";
import { Badge } from "@/app/_module/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/app/_module/components/ui/tooltip";
import { Button } from "@/app/_module/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Progress } from "@/app/_module/components/ui/progress";
import { ScrollArea } from "@/app/_module/components/ui/scroll-area";
import { useTwilioDevice } from "@/app/_module/hooks/useTwilioDevice";
import { isManualCallPauseRequested, clearManualCallPauseRequest, shouldCancelCall } from "@/app/_module/contexts/TwilioDeviceContext";
import { formatPhoneNumberDisplay } from "@/app/_module/utils/phoneUtils";
import { formatDurationShort } from "@/app/_module/utils/formatters";
import { useNotificationSound } from "@/app/_module/hooks/use-notification-sound";
import callService, { type MultiLineCallResponse, type RaceToAnswerResponse } from "@/app/_module/services/callService";
import { AudioDeviceSelector } from "@/app/_module/components/AudioDeviceSelector";
import { LeadDetailsCard } from "./LeadDetailsCard";
import { QuickNotesPanel } from "./QuickNotesPanel";
import { BulkAreaCodeRecommendationNotification } from "@/app/_module/components/BulkAreaCodeRecommendationNotification";
import { useRealtimeRejoin } from "@/app/_module/services/realtimeHealth";
import { usePhoneNumberManager } from "@/app/_module/hooks/usePhoneNumberPricing";
import { dialerSessionApi } from "@/app/_module/services/dialerSessionService";
import { useDirect130Host } from "@/app/_module/utils/direct130HostContext";
import { removeRealtimeChannel } from "../../../../_module/services/realtimeTeardown";
import { dialClockSeconds, startDialClock, switchDialClock } from "../utils/dialClock";

interface InsuraABotKeyCode {
  id: string;
  code: string;
  timestamp: Date;
  duration: number;
  callId: string;
}

interface MultiLineDialerSessionProps {
  leads: Lead[];
  aiMatchingEnabled?: boolean;
  onStop: () => void;
  onComplete: (stats?: { answered: number; called?: number; totalDialTime: number; talkSeconds?: number; conversationSeconds?: number; appointments?: number; underwriting?: number; sold?: number; wasStopped?: boolean }) => void;
  hideLeadDetails?: boolean; // When true, shows compact widget view
  autoOpenLeadDetails?: boolean; // Auto-open lead details when call is answered
  resumeOffset?: number; // Number of leads already completed from previous session (for resume)
  totalLeadsOriginal?: number; // Original total leads count (for resume — includes already-completed leads)
}

// Storage key for persisting multi-line session stats across page refresh
const MULTILINE_SESSION_STATS_KEY = 'multiline-dialer-session-stats';

/**
 * Fingerprint of the lead set a session was started with. Persisted alongside
 * the session stats so a remount can tell "same session re-mounting after a
 * refresh" (restore progress) from "new session on a different list" (stale
 * stats from an abandoned run must NOT leak in — that leak silently shrank
 * every previously-touched pipeline to a handful of leads: the
 * "multi-line only works for certain pipelines" bug, 2026-08-10).
 */
const leadSetSignature = (list: Array<{ id: string }>): string =>
  `${list.length}:${list[0]?.id || ''}:${list[list.length - 1]?.id || ''}`;

// Line states that count as dialing time in full (see dialClock).
const DIAL_CLOCK_BUSY = new Set(["dialing", "ringing", "connecting", "connected", "on-hold"]);

/** Stored stats, but ONLY if they belong to this same session's lead set (or
 *  an explicit backend resume, where the server already vetted the queue). */
const readOwnSessionStats = (
  leads: Array<{ id: string }>,
  resumeOffset: number,
): Record<string, any> | null => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(MULTILINE_SESSION_STATS_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (resumeOffset > 0) return parsed;
    if (parsed.leadSetSignature && parsed.leadSetSignature === leadSetSignature(leads)) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

interface LineState {
  lineNumber: number;
  lead: Lead;
  // "connecting" = call answered, waiting for AMD to confirm human (optimistic UI state)
  status: "idle" | "dialing" | "ringing" | "connecting" | "connected" | "on-hold" | "disconnected" | "failed" | "no-answer" | "voicemail";
  callSid?: string;
  callId?: string;
  startTime?: Date;
  duration?: number;
  error?: string;
}

function PreparingBatchIndicator({
  isPreparingNextBatch,
  preparingBatchTooLong,
  dialingCount,
  onSkipRetry,
  compact = false,
}: {
  isPreparingNextBatch: boolean;
  preparingBatchTooLong: boolean;
  dialingCount: number;
  onSkipRetry: () => void;
  compact?: boolean;
}) {
  if (!isPreparingNextBatch || dialingCount > 0) return null;

  return (
    <div className={compact ? "space-y-1.5" : "space-y-2 mb-3"}>
      <div
        className={`flex items-center justify-center gap-2 ${compact ? "py-2.5 px-3" : "py-3 px-4"} bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg text-sm text-blue-700 animate-pulse`}
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="font-medium">
          Preparing next batch — dialing shortly...
        </span>
      </div>
      {preparingBatchTooLong && (
        <Button
          size="sm"
          variant="outline"
          className={`w-full ${compact ? "h-7 text-xs" : ""} bg-white hover:bg-orange-50 border-orange-200 text-orange-600`}
          onClick={onSkipRetry}
        >
          <SkipForward
            className={`${compact ? "h-3 w-3" : "h-4 w-4"} mr-1`}
          />
          Taking too long? Skip & retry
        </Button>
      )}
    </div>
  );
}

export const MultiLineDialerSession = function ({
  leads,
  aiMatchingEnabled = false,
  onStop,
  onComplete,
  hideLeadDetails = false,
  autoOpenLeadDetails = false,
  resumeOffset = 0,
  totalLeadsOriginal,
}: MultiLineDialerSessionProps) {
  const router = useRouter();
  // Queue system: process all leads in batches of up to 4
  const MAX_CONCURRENT_LINES = 4;

  // Cross-session resume: restore which leads were already called from localStorage
  // This MUST be declared before filteredLeads/leadQueue so they can filter processed leads out
  //
  // ONLY on an explicit resume (resumeOffset > 0 — set solely by the widget's
  // Resume flow, like the server fallback below). The stats key survives
  // unclean exits (tab close, crash, refresh mid-run), and unconditionally
  // restoring it made every FRESH session silently drop any lead ever touched
  // by an abandoned run: "multi-line only works for certain pipelines" — the
  // fresh import dialed fine while older lists shrank to 3-4 leads and
  // instantly completed (Mark Gurraj, 2026-08-10).
  const restoredProcessedIds = useMemo(() => {
    const parsed = readOwnSessionStats(leads, resumeOffset);
    if (parsed && Array.isArray(parsed.processedLeadIds) && parsed.processedLeadIds.length > 0) {
      return new Set<string>(parsed.processedLeadIds);
    }
    return new Set<string>();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Different lead set and not an explicit resume → the stored stats belong to
  // some abandoned run. Purge them so they can't shrink this or any future
  // session. (Same-set remounts — i.e. refresh-recovery — keep their stats.)
  useEffect(() => {
    if (resumeOffset === 0 && readOwnSessionStats(leads, resumeOffset) === null && typeof window !== 'undefined') {
      try { localStorage.removeItem(MULTILINE_SESSION_STATS_KEY); } catch {}
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const processedLeadIdsRef = useRef<Set<string>>(restoredProcessedIds);
  const [processedCount, setProcessedCount] = useState(restoredProcessedIds.size);
  // Guarded rejoin: exponential backoff + a hard attempt cap. Without the cap a
  // permanently failing channel re-subscribes in a tight loop and hangs the tab.
  const { rejoinToken, handleStatus } = useRealtimeRejoin("multiline-dialer");

  // Filter out already-processed leads for cross-session resume
  const filteredLeads = useMemo(() => {
    if (restoredProcessedIds.size === 0) return leads;
    return leads.filter(l => !restoredProcessedIds.has(l.id));
  }, [leads, restoredProcessedIds]);

  // Track which leads are in the queue (not yet dialed)
  const [leadQueue, setLeadQueue] = useState<Lead[]>(filteredLeads.slice(MAX_CONCURRENT_LINES));
  const leadQueueRef = useRef<Lead[]>(filteredLeads.slice(MAX_CONCURRENT_LINES)); // Ref for stale closure prevention

  // SERVER-SIDE FALLBACK: If localStorage has no processedLeadIds but this is a resume
  // (resumeOffset > 0), fetch the backend's lead_queue to figure out which leads remain.
  // This handles: new browser, cleared cache, different device.
  const serverFallbackApplied = useRef(false);
  useEffect(() => {
    if (serverFallbackApplied.current) return;
    if (restoredProcessedIds.size > 0) return; // localStorage worked, no need for fallback
    if (resumeOffset === 0) return; // Fresh session, not a resume

    serverFallbackApplied.current = true;

    // Fetch latest resumable session from backend
    (async () => {
      try {
        const response = await api.get<any>({
          url: getApiUrl("/communication/dialer-sessions/latest-resumable"),
          auth: true,
        });
        const session = response?.data?.data;
        if (!session?.leadQueue || session.leadQueue.length === 0) return;

        // The backend's leadQueue contains leads that were NOT yet called.
        // Build processedLeadIds by diffing: all leads - backend remaining = already called
        const remainingLeadIds = new Set(session.leadQueue.map((l: any) => l.leadId));
        const alreadyCalled = leads.filter(l => !remainingLeadIds.has(l.id)).map(l => l.id);

        if (alreadyCalled.length === 0) return;

        // Update processedLeadIds
        const newProcessed = new Set(alreadyCalled);
        processedLeadIdsRef.current = newProcessed;
        setProcessedCount(newProcessed.size);

        // Re-filter lead queue to exclude already-called leads
        const remaining = leads.filter(l => !newProcessed.has(l.id));
        const newQueue = remaining.slice(MAX_CONCURRENT_LINES);
        leadQueueRef.current = newQueue;
        setLeadQueue(newQueue);

        // Persist to localStorage for next time
        try {
          const stored = localStorage.getItem(MULTILINE_SESSION_STATS_KEY);
          const parsed = stored ? JSON.parse(stored) : {};
          localStorage.setItem(MULTILINE_SESSION_STATS_KEY, JSON.stringify({
            ...parsed,
            leadSetSignature: leadSetSignature(leads),
            processedLeadIds: alreadyCalled,
            processedCount: alreadyCalled.length,
          }));
        } catch {}

        console.log(`[MultiLine] Server fallback: restored ${alreadyCalled.length} processed leads from backend`);
      } catch {
        // Backend fetch failed — start fresh (better than crashing)
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track leads that were dialed but didn't answer when another person answered first (for re-queuing)
  const [reQueuedLeads, setReQueuedLeads] = useState<Lead[]>([]);
  const reQueuedLeadsRef = useRef<Lead[]>([]); // Ref for stale closure prevention

  // Total leads for progress tracking (use original total if resuming)
  const totalLeads = totalLeadsOriginal || leads.length;

  // Track how many leads have been successfully connected (answered)
  // Initialize from localStorage if available (survives page refresh)
  const [totalAnsweredCount, setTotalAnsweredCount] = useState(() => {
    const parsed = readOwnSessionStats(leads, resumeOffset);
    return parsed?.totalAnsweredCount || 0;
  });
  const totalAnsweredCountRef = useRef(0); // Ref for stale closure prevention

  // Take first batch (up to 4 leads) for initial simultaneous calling
  // Cross-session resume: use filtered leads so we skip already-called ones
  const initialLeads = filteredLeads.slice(0, MAX_CONCURRENT_LINES);

  const [lines, setLines] = useState<LineState[]>(
    initialLeads.map((lead, index) => ({
      lineNumber: index + 1,
      lead,
      status: "idle" as const,
    }))
  );

  // Ref to track if we're currently dialing next leads (prevent double-dial)
  const isDialingNextRef = useRef(false);

  // Auto-pause for disposition after 60s+ connected calls on multi-line
  const [awaitingMultiLineDisposition, setAwaitingMultiLineDisposition] = useState(false);
  const awaitingMultiLineDispositionRef = useRef(false);

  // UI state: shows "Preparing next batch..." between batches so agent knows it's not stuck
  const [isPreparingNextBatch, setIsPreparingNextBatch] = useState(false);
  const [preparingBatchTooLong, setPreparingBatchTooLong] = useState(false);
  const preparingBatchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Show fallback skip button if "preparing next batch" takes too long (8s)
  useEffect(() => {
    if (isPreparingNextBatch) {
      setPreparingBatchTooLong(false);
      preparingBatchTimerRef.current = setTimeout(() => setPreparingBatchTooLong(true), 8000);
    } else {
      setPreparingBatchTooLong(false);
      if (preparingBatchTimerRef.current) clearTimeout(preparingBatchTimerRef.current);
    }
    return () => {
      if (preparingBatchTimerRef.current) clearTimeout(preparingBatchTimerRef.current);
    };
  }, [isPreparingNextBatch]);

  // Redial limit: track how many times each lead has been re-queued
  // Each lead gets dialed max 2 times total (original + 1 redial), then moves on to fresh leads
  const redialCountRef = useRef<Record<string, number>>({});
  const MAX_FRONTEND_REDIALS = 1;

  // Pause state - when paused, don't auto-dial next leads from queue
  const [isPaused, setIsPaused] = useState(false);
  // Ref to track paused state for synchronous access (avoids stale closure issues)
  const isPausedRef = useRef(false);
  // NUCLEAR PAUSE FIX: When pausing, we drain the lead queues into this ref.
  // This makes dialNextLeads physically unable to create new batches even if it
  // bypasses all isPausedRef checks (race conditions). Restored on resume.
  const pausedLeadQueueBackup = useRef<{ leads: Lead[], reQueued: Lead[] } | null>(null);

  // Anti-spam: Track last dial time to prevent rapid successive calls
  const lastDialTimeRef = useRef<number>(0);
  const DIAL_DEBOUNCE_MS = 2000; // 2 second minimum between dial attempts

  // Anti-spam: Track last skip time to prevent rapid skip button clicks
  const lastSkipTimeRef = useRef<number>(0);
  const SKIP_DEBOUNCE_MS = 500; // 500ms minimum between skip clicks

  // Visual cooldown state for skip/end buttons
  const [skipCooldownActive, setSkipCooldownActive] = useState(false);
  const [endCooldownActive, setEndCooldownActive] = useState(false);
  const skipCooldownTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const endCooldownTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Ref to track if session has been stopped - prevents any dialing after session ends
  const sessionStoppedRef = useRef(false);
  // TRUE only when the HUMAN pressed Stop (set solely in handleStopSession,
  // cleared on resume/new session). Unlike sessionStoppedRef — which stale
  // polls/batch transitions may set and dialNextLeads may reset — this flag
  // is never inferred, so a user stop can't be resurrected into a redial loop.
  const userStoppedRef = useRef(false);
  const handleStopSessionRef = useRef<(() => void) | null>(null);

  // Post-call guard: When agent ends a call, block new customer_answered events from auto-connecting
  // until the next batch is explicitly dialed by dialNextLeads(). Without this, backend AMD promotion
  // or late webhooks can show a new "answered" lead immediately after the agent hangs up.
  const isPostCallRef = useRef(false);

  // Ref for batch completion debounce timer - moved here so handleStopSession can access it
  const batchCompletionTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Track the outbound caller ID from the race-to-answer response
  const fromPhoneNumberRef = useRef<string | undefined>(undefined);

  // Ref for dialNextLeads to avoid stale closures in the status poll handler
  const dialNextLeadsRef = useRef<((availableLineIndices: number[]) => Promise<void>) | null>(null);

  // Initialize sessionId from localStorage for page refresh recovery
  const [sessionId, setSessionId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(MULTILINE_SESSION_STATS_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          return parsed.sessionId || null;
        }
      } catch (e) {
        // Invalid data, use default
      }
    }
    return null;
  });
  // Track if we're recovering from a page refresh (had an active session)
  // We use the same sessionId check as the useState initializer above
  const isRecoveringRef = useRef<boolean>(
    (() => {
      if (typeof window !== 'undefined') {
        try {
          const stored = localStorage.getItem(MULTILINE_SESSION_STATS_KEY);
          if (stored) {
            const parsed = JSON.parse(stored);
            return !!parsed.sessionId;
          }
        } catch (e) {
          // Invalid data
        }
      }
      return false;
    })()
  );
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [selectedLineIndex, setSelectedLineIndex] = useState<number | null>(null);
  // Track which line is connected (separate from selectedLineIndex for stability)
  // This prevents rapid re-renders when polling updates line statuses
  const [connectedLineIndex, setConnectedLineIndex] = useState<number | null>(null);
  const [appleScreeningDetected, setAppleScreeningDetected] = useState(false);

  // Brandon's-bug fix: pickup chime.
  // When a line transitions from ringing to "connecting" (customer just picked
  // up, AMD running), play a brief notification sound so the agent notices
  // immediately — the cyan color change alone is easy to miss across 5+
  // pulsing lines. Dedup per-callSid so polling (which runs every 800ms)
  // doesn't fire the chime repeatedly for the same line.
  const { playSound: playPickupChime } = useNotificationSound({ volume: 0.45 });
  const chimeFiredForCallSidsRef = useRef<Set<string>>(new Set());
  const [insuraABotKeyCodes, setInsuraABotKeyCodes] = useState<InsuraABotKeyCode[]>([]);
  const [callTimer, setCallTimer] = useState(0);
  const callTimerRef = useRef<NodeJS.Timeout | null>(null);
  const callTimerValueRef = useRef(0); // Stable ref for handleEndCall (avoids re-render every second)

  // Modal states for quick actions
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [showNoSaleModal, setShowNoSaleModal] = useState(false);
  const noSaleLeadIdRef = useRef<string | null>(null);

  // Track the last active lead to show details after call ends
  const [lastActiveLeadId, setLastActiveLeadId] = useState<string | null>(null);

  // Track the incoming caller's info captured at emergency-pause time, so the
  // post-call disposition view can show "you just talked to <incoming>" instead
  // of the literal fallback "Lead". The incoming caller isn't in the multi-line
  // queue, so `lastActiveLeadId` lookup misses them. Cleared on Resume.
  const [incomingPauseInfo, setIncomingPauseInfo] = useState<{
    name?: string;
    number?: string;
    leadId?: string;
  } | null>(null);

  // Call control states
  const [isDTMFListening, setIsDTMFListening] = useState(false);
  const [isSpeechProcessing, setIsSpeechProcessing] = useState(false);

  // 3-way calling state
  const [showAddPartyPanel, setShowAddPartyPanel] = useState(false);
  const [showTransferPanel, setShowTransferPanel] = useState(false);
  const onDirect130 = useDirect130Host(); // owner 2026-11-02: no call transfer on Direct 130's address
  const [loadingParticipantSid, setLoadingParticipantSid] = useState<string | null>(null);

  // Quick Notes panel state
  const [showQuickNotes, setShowQuickNotes] = useState(false);

  // Track if we've started the session
  const hasStarted = useRef(false);
  // Session talk-time accumulators. `totalDialTime` (dialClockRef) answers "how
  // long was I dialing", not "how much of that was a conversation". These sum the
  // per-call timer as each call ends so the completion card can show the same
  // dialer / talking / real-conversation split as the agency dashboard.
  const sessionTalkSecondsRef = useRef<number>(0);
  const sessionConversationSecondsRef = useRef<number>(0);
  // Total dial time. Restored only when a refresh reopens this same run (same leads, same resume
  // point). It used to be a stored start time that also carried into the next run, so Friday
  // 10-16 counted 7:59–8:16 pm on two Session History rows.
  const dialClockRun = `${leadSetSignature(leads)}:${resumeOffset}`;
  const dialClockRef = useRef(
    (() => {
      const now = Date.now();
      if (typeof window !== 'undefined') {
        try {
          const parsed = JSON.parse(localStorage.getItem(MULTILINE_SESSION_STATS_KEY) || 'null');
          if (parsed?.dialClockRun === dialClockRun) {
            return { countedMs: Number(parsed.dialClockMs) || 0, mode: 'idle' as const, since: Number(parsed.dialClockSavedAt) || now };
          }
        } catch (e) {
          // Invalid data, start fresh
        }
      }
      return startDialClock(now);
    })()
  );
  const getDialSeconds = () => dialClockSeconds(dialClockRef.current, Date.now());
  const dialClockBusy = isSessionActive && lines.some((l) => DIAL_CLOCK_BUSY.has(l.status));
  useEffect(() => {
    dialClockRef.current = switchDialClock(dialClockRef.current, isPaused ? 'paused' : dialClockBusy ? 'busy' : 'idle', Date.now());
  }, [isPaused, dialClockBusy]);
  const sessionIdRef = useRef<string | null>(sessionId); // Ref for stale closure prevention in Supabase handler
  // Track the FIRST session ID for progress reporting — continuation batches create new backend
  // sessions, but we report all progress to the original one so session history shows 1 entry
  const primarySessionIdRef = useRef<string | null>(null);
  const disconnectRef = useRef<(() => void) | null>(null); // Ref for stale closure prevention in polling interval
  const statusPollRef = useRef<NodeJS.Timeout | null>(null);
  const startStatusPollingRef = useRef<((sessionId: string) => void) | null>(null);

  // Track lead status changes for session stats (Appt, Underwriting, Sold)
  // Initialize from localStorage if available (survives page refresh)
  const [leadStatuses, setLeadStatuses] = useState<Record<string, string>>(() => {
    const parsed = readOwnSessionStats(leads, resumeOffset);
    return parsed?.leadStatuses || {};
  });
  const leadStatusesRef = useRef<Record<string, string>>({});

  // Ref for DTMFPollingWrapper to clear digits
  const dtmfPollingRef = useRef<DTMFPollingWrapperRef>(null);

  // Ref to track lines for callbacks (to avoid stale closure issues)
  const linesRef = useRef<LineState[]>(lines);

  // Ref to track connected line index for callbacks (prevents duplicate setConnectedLineIndex calls)
  const connectedLineIndexRef = useRef<number | null>(null);

  const { user } = useUserContext();
  const updateLeadStatusMutation = useUpdateLeadStatus();

  // Phone number manager for area codes
  const { phoneNumbers } = usePhoneNumberManager();

  // Derive user's owned area codes from phone numbers (handle both camelCase and snake_case from backend)
  const userAreaCodes = phoneNumbers
    .filter((phone: any) => phone.areaCode || phone.area_code)
    .map((phone: any) => phone.areaCode || phone.area_code)
    .filter((areaCode: string, index: number, self: string[]) => self.indexOf(areaCode) === index);

  // Keep leadStatusesRef in sync with state for use in callbacks
  useEffect(() => {
    leadStatusesRef.current = leadStatuses;
  }, [leadStatuses]);

  // Sync session progress to the PRIMARY session for history (fire-and-forget)
  // Always update the first session created — continuation batches create new backend sessions
  // but session history should show one unified entry with full progress.
  //
  // IMPORTANT: leadQueue is filtered to UN-PROCESSED leads only. If the user stops the
  // session and resumes it later (from Session History), we'll load exactly these
  // remaining leads — zero re-dials of already-completed calls. In-flight leads that
  // haven't finished yet are still in the queue (they'll be retried on resume, correctly).
  useEffect(() => {
    const targetSessionId = primarySessionIdRef.current || sessionId;
    if (!targetSessionId) return;
    const remainingLeads = leads.filter(l => !processedLeadIdsRef.current.has(l.id));
    dialerSessionApi
      .updateProgress(targetSessionId, {
        currentLeadIndex: processedCount + resumeOffset,
        completedCalls: processedCount + resumeOffset,
        totalLeadsToCall: totalLeads,
        totalAnswered: totalAnsweredCount,
        totalFailed: processedCount - totalAnsweredCount,
        totalDialTime: getDialSeconds(),
        talkSeconds: sessionTalkSecondsRef.current,
        conversationSeconds: sessionConversationSecondsRef.current,
        leadQueue: remainingLeads.map(l => ({
          leadId: l.id,
          name: l.name || '',
          phone: l.phone || '',
        })),
      })
      .catch(() => {}); // Silently ignore
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedCount, totalAnsweredCount]);

  // Keep queue refs in sync with state for stale closure prevention in timeout callbacks
  useEffect(() => {
    leadQueueRef.current = leadQueue;
  }, [leadQueue]);

  useEffect(() => {
    reQueuedLeadsRef.current = reQueuedLeads;
  }, [reQueuedLeads]);

  useEffect(() => {
    totalAnsweredCountRef.current = totalAnsweredCount;
  }, [totalAnsweredCount]);

  // Persist session stats to localStorage for page refresh recovery
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Only persist if session has started (has meaningful data)
      if (hasStarted.current || totalAnsweredCount > 0 || Object.keys(leadStatuses).length > 0) {
        localStorage.setItem(MULTILINE_SESSION_STATS_KEY, JSON.stringify({
          // Ownership stamp — lets a remount distinguish "same session after a
          // refresh" (restore) from "new session, stale stats" (purge).
          leadSetSignature: leadSetSignature(leads),
          totalAnsweredCount,
          leadStatuses,
          dialClockRun,
          dialClockMs: getDialSeconds() * 1000,
          dialClockSavedAt: Date.now(),
          sessionId, // CRITICAL: Persist sessionId for page refresh recovery
          // Cross-session resume: persist which leads were already called so we don't double-call
          processedLeadIds: Array.from(processedLeadIdsRef.current),
          processedCount: processedLeadIdsRef.current.size,
        }));
      }
    }
  }, [totalAnsweredCount, leadStatuses, sessionId, processedCount]);

  // Twilio Device hook for browser-based calling (for connecting to answered call)
  const {
    activeCall,
    activeCallSid,
    isDeviceReady,
    isDeviceRegistering,
    connect,
    disconnect,
    mute,
    unmute,
    isMuted,
    isOnHold: twilioIsOnHold,
    setIsOnHold,
    callStatus: twilioCallStatus,
    callDuration,
    remoteAudioRef,
    audioInputDevices,
    audioOutputDevices,
    selectedInputDeviceId,
    selectedOutputDeviceId,
    setAudioInputDevice,
    setAudioOutputDevice,
    refreshAudioDevices,
    microphoneAudioLevel,
    isHardwareMuted,
    // Audio gain/volume controls
    microphoneGain,
    speakerVolume,
    setMicrophoneGain,
    setSpeakerVolume,
    conferenceParticipants,
    isAddingParticipant,
    addParticipantToCall,
    removeParticipantFromCall,
    muteParticipant,
    holdParticipant,
    setActiveConference,
    sendDigits,
    stopRingback,
    playRingback,
    setIsInMultiLineMode,
    callMetadata,
    activeCallSource,
    updateCallMetadata,
    startAudioMonitoring,
    stopAudioMonitoring,
  } = useTwilioDevice();

  // Use twilioIsOnHold from hook (renamed to isOnHold for easier usage)
  const isOnHold = twilioIsOnHold;

  const { toast } = useToast();

  // Microphone detection — warn agent if mic is missing
  const [hasMicrophone, setHasMicrophone] = useState<boolean | null>(null);
  useEffect(() => {
    const checkMicrophone = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        setHasMicrophone(true);
      } catch {
        setHasMicrophone(false);
        toast({
          title: "Microphone Not Detected",
          description: "Please connect a microphone to start making calls. Check your system settings and browser permissions.",
          variant: "destructive",
          duration: 10000,
        });
      }
    };
    checkMicrophone();
  }, [toast]);

  // Dial pad state
  const [showDialPad, setShowDialPad] = useState(false);
  const [showCallerId, setShowCallerId] = useState(true);

  // Expandable lead info during connected call
  const [showLeadInfo, setShowLeadInfo] = useState(false);

  // Get currently selected lead (for lead details panel)
  const selectedLine = selectedLineIndex !== null ? lines[selectedLineIndex] : null;
  const selectedLead = selectedLine?.lead;

  // Store a stable lead ID for the connected line to prevent UI flicker
  // This is set when connectedLineIndex changes and cleared when connection ends
  const [stableConnectedLeadId, setStableConnectedLeadId] = useState<string | null>(null);

  // Track if we've already opened lead details for this connection
  const autoOpenedForConnectionRef = useRef<number | null>(null);

  // Update stableConnectedLeadId when connectedLineIndex changes.
  //
  // ALWAYS navigate to the answered lead's profile in multi-line — the
  // `autoOpenLeadDetails` preference is intentionally ignored here.
  //
  // Why: in multi-line you fire 2-20 dials at once and can't predict who
  // picks up first. The Active Call widget was already updating to the
  // answered lead, but the main page profile panel stayed on whoever the
  // agent was viewing when they hit dial — so agents would talk to Helen
  // while staring at Michael's notes (Brandon's reported bug, also
  // confirmed by Twilio's call-pattern analysis: 42.8% of multi-line
  // bursts had ≥2 lines connect simultaneously, exactly the race window
  // for this UI mismatch). The Twilio recommendation: when a call
  // connects, the profile panel must move with it. We respect the
  // setting only for single-line / manual dials; multi-line always nav.
  useEffect(() => {
    if (connectedLineIndex !== null) {
      const leadId = lines[connectedLineIndex]?.lead?.id;
      if (leadId) {
        setStableConnectedLeadId(leadId);

        // Auto-open lead details on connect (only once per connection)
        if (autoOpenedForConnectionRef.current !== connectedLineIndex) {
          autoOpenedForConnectionRef.current = connectedLineIndex;
          router.push(`/leads/${leadId}`);
        }
      }
    } else {
      setStableConnectedLeadId(null);
      autoOpenedForConnectionRef.current = null;
    }
  // Only depend on connectedLineIndex, NOT lines - we want to keep the lead ID stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedLineIndex, router]);

  // Get connected line - but use stableConnectedLeadId for conditional rendering
  // This prevents the UI from flickering when polling updates the lines array
  const connectedLine = connectedLineIndex !== null ? lines[connectedLineIndex] ?? null : null;

  // For JSX conditional rendering, use the stable lead ID presence
  // This ensures LeadDetailsPanel doesn't mount/unmount during polling updates
  const stableConnectedLine = stableConnectedLeadId !== null && connectedLine !== null ? connectedLine : null;

  // Fetch full lead details when connected (same as manual dialer)
  // The leads from selectedLeads may not have all fields populated
  const { data: fetchedConnectedLeadData } = useLead(stableConnectedLeadId || '');
  const connectedLeadDetails = useMemo(() => {
    if (!stableConnectedLine?.lead) return null;
    const baseLead = stableConnectedLine.lead;
    if (!fetchedConnectedLeadData) return baseLead;
    // Extract lead from response - useLead returns { data: Lead, message: string }
    const fetched = ('data' in fetchedConnectedLeadData && fetchedConnectedLeadData.data)
      ? (fetchedConnectedLeadData.data as Lead)
      : ('name' in fetchedConnectedLeadData)
        ? (fetchedConnectedLeadData as unknown as Lead)
        : null;
    if (!fetched) return baseLead;
    // Merge: fetched data takes priority, fallback to base lead
    return { ...baseLead, ...fetched };
  }, [stableConnectedLine?.lead, fetchedConnectedLeadData]);

  // Handle DTMF updates from polling wrapper (customer keypresses)
  const handleDTMFUpdate = useCallback((digits: DTMFDigit[]) => {
    const callSidToUse = connectedLine?.callSid || activeCallSid;
    if (!callSidToUse) {
      return;
    }

    // Convert DTMFDigit[] to InsuraABotKeyCode[] format
    const convertedCodes: InsuraABotKeyCode[] = digits.map((digit, digitIndex) => ({
      // Index is the tiebreaker: timestamp.toString() is only SECOND-resolution, so
      // two digits pressed inside the same second produced identical React keys
      // ("Encountered two children with the same key"). The digit list is
      // append-only, so the index is stable across renders.
      id: `${callSidToUse}-${digit.timestamp.toString()}-${digitIndex}`,
      code: digit.digit,
      timestamp: new Date(digit.timestamp),
      duration: digit.callDuration || 0,
      callId: callSidToUse,
    }));

    // Replace all codes with the new set from backend
    setInsuraABotKeyCodes(convertedCodes);
  }, [connectedLine?.callSid, activeCallSid]);

  // Callback to clear codes when InsuraABotKeyGrabber starts a new collection
  const handleClearCodes = useCallback(() => {
    setInsuraABotKeyCodes([]);
    dtmfPollingRef.current?.clearDigits();
  }, []);

  // Calculate statistics
  const dialingCount = lines.filter(l => l.status === "dialing" || l.status === "ringing").length;
  const connectedCount = lines.filter(l => l.status === "connected").length;
  const completedCount = lines.filter(l => ["disconnected", "failed", "no-answer"].includes(l.status)).length;
  const progress = (completedCount / lines.length) * 100;

  // Format phone number for calling
  const formatPhoneNumber = (phone: string): string => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    return phone;
  };

  // Simple phone validation
  const isValidPhone = (phone: string | undefined | null): boolean => {
    if (!phone) return false;
    const digits = phone.replace(/\D/g, "");
    return digits.length >= 10;
  };

  // Get the next lead from the queue (prioritize re-queued leads, then main queue)
  const getNextLead = useCallback((): Lead | null => {
    // First check re-queued leads (leads that were dialing when someone else answered)
    if (reQueuedLeads.length > 0) {
      return reQueuedLeads[0];
    }
    // Then check main queue
    if (leadQueue.length > 0) {
      return leadQueue[0];
    }
    return null;
  }, [reQueuedLeads, leadQueue]);

  // Remove a lead from the appropriate queue after dialing
  const removeFromQueue = useCallback((leadId: string) => {
    setReQueuedLeads(prev => prev.filter(l => l.id !== leadId));
    setLeadQueue(prev => prev.filter(l => l.id !== leadId));
  }, []);

  // Add leads back to the re-queue (for leads that were dialing when someone else answered)
  const addToReQueue = useCallback((leadsToReQueue: Lead[]) => {
    if (leadsToReQueue.length === 0) return;
    // Deduplicate: skip leads already in the re-queue
    const existingIds = new Set(reQueuedLeadsRef.current.map(l => l.id));
    // Filter out duplicates and leads that exceeded the frontend redial limit
    const eligible = leadsToReQueue.filter(lead => {
      // Skip duplicates already in queue
      if (existingIds.has(lead.id)) return false;
      // Skip leads that exceeded redial limit (matches backend MAX_REDIAL_ATTEMPTS)
      const count = redialCountRef.current[lead.id] || 0;
      if (count >= MAX_FRONTEND_REDIALS) return false;
      redialCountRef.current[lead.id] = count + 1;
      return true;
    });
    if (eligible.length === 0) return;
    // CRITICAL: Update ref IMMEDIATELY before state - this ensures the batch completion
    // effect sees the queued leads even if state update hasn't propagated yet
    reQueuedLeadsRef.current = [...reQueuedLeadsRef.current, ...eligible];
    setReQueuedLeads(prev => [...prev, ...eligible]);
  }, []);

  // Dial the next batch of leads from the queue to fill available lines
  const dialNextLeads = useCallback(async (availableLineIndices: number[]) => {
    // CRITICAL: Check if session has been stopped BY THE USER - prevents dialing after manual stop.
    // NOTE: Do NOT check sessionStoppedRef here for continuation batches — it may have been
    // incorrectly set by a stale poll seeing "completed" for the previous batch's backend session.
    // Instead, only respect it if the user explicitly called handleStopSession.
    if (sessionStoppedRef.current && !isDialingNextRef.current) {
      // INCIDENT FIX (July 9): a USER stop must NEVER be resurrected. The old
      // logic inferred intent from queue length ("leads remain → stale flag →
      // reset"), so completion/stop paths that left leads queued got revived
      // by the next dialNextLeads → end-session/create-session churn (1,992
      // stopped sessions in 8h). userStoppedRef is set ONLY in
      // handleStopSession, so genuine stops stay stopped while stale-poll
      // flags still self-heal.
      if (userStoppedRef.current) {
        return; // the human pressed Stop — final.
      }
      // Double-check: if there are still leads, this might be a stale flag from batch transition
      const allQueued = [...reQueuedLeadsRef.current, ...leadQueueRef.current]
        .filter(l => !processedLeadIdsRef.current.has(l.id));
      if (allQueued.length === 0) {
        return; // Genuinely done
      }
      // Reset stale flag — there are still leads to dial
      sessionStoppedRef.current = false;
    }

    // CRITICAL: Don't start a new batch if a human is still connected or if any line
    // is still in a non-terminal state (ringing, connecting, etc).
    // AMD confirmation can arrive after voicemail lines complete — the batch completion
    // effect may fire before the human line is marked as "connected".
    if (connectedLineIndexRef.current !== null) {
      return;
    }
    // Don't dial if awaiting disposition (agent had 60s+ call, taking notes)
    if (awaitingMultiLineDispositionRef.current) {
      return;
    }
    const activeStatuses = ["dialing", "ringing", "connecting", "connected", "on-hold"];
    const hasActiveLine = linesRef.current.some(l => activeStatuses.includes(l.status));
    if (hasActiveLine) {
      return;
    }
    // CRITICAL: Don't advance if a line still shows as "connected" in linesRef.
    // connectedLineIndexRef can be cleared by stale Supabase events (race condition),
    // but a line with status="connected" means the agent is mid-conversation.
    // BUG FIX: This prevents the conference collapse that killed Brandon's CA81cacf call.
    // The agent was mid-conversation but connectedLineIndexRef was cleared by a stale
    // event, so dialNextLeads created a new session, disconnected the agent from the
    // old conference, and Twilio collapsed it (endConferenceOnExit on sole participant).
    // NOTE: Can't use `activeCall` here — the agent's Device is always connected to the
    // conference even between batches, so activeCall is always truthy during multi-line.
    const hasConnectedLine = linesRef.current.some(l => l.status === "connected");
    if (hasConnectedLine) {
      return;
    }

    // Clear post-call guard — agent is now ready for a new batch
    isPostCallRef.current = false;

    // Clear selectedLineIndex so the post-call "Call Ended" panel doesn't flash the NEW
    // batch's lead. Without this, lines[selectedLineIndex] points to the old line index
    // but gets overwritten with a new lead when setLines runs below → the post-call panel
    // briefly shows the wrong lead's details
    setSelectedLineIndex(null);

    // Anti-spam: Check if already dialing
    if (isDialingNextRef.current) {
      return;
    }

    // Anti-spam: Debounce - prevent rapid successive calls
    const now = Date.now();
    if (now - lastDialTimeRef.current < DIAL_DEBOUNCE_MS) {
      return;
    }

    // MANUAL CALL AUTO-PAUSE: If a manual call was initiated from lead detail
    // (e.g., "Call John" on the lead page), PAUSE the session — don't end it.
    // Brandon reported: "paused and dialed someone separately, couldn't come
    // back to session." That was because the old code called handleStopSession
    // which ended the session server-side (irreversible). Now we call
    // handlePause which just sets isPaused and clears isInMultiLineMode so the
    // manual call can go through. Agent clicks Resume after the manual call to
    // pick up where they left off.
    if (isManualCallPauseRequested()) {
      if (!isPausedRef.current) {
        isPausedRef.current = true;
        isDialingNextRef.current = false;
        handlePause();
        clearManualCallPauseRequest();
      }
      return;
    }

    // Anti-spam: Check paused state using ref for synchronous access
    if (isPausedRef.current) {
      return;
    }

    // CALL-WAITING GUARD: if the agent is mid hold/merge handoff, do NOT
    // start a new batch. The existing race conference is preserved for the
    // held customer; dialing new leads now would end the session and drop
    // the handoff mid-flight (user saw "incoming gets killed" + session
    // rejected late answer). Resume dialing after the incoming call ends
    // and the agent manually hits Resume.
    if (typeof window !== 'undefined' && sessionStorage.getItem('__callwaiting_active') === '1') {
      return;
    }

    // Get available leads from queue — use REFS for immediate consistency
    // State (reQueuedLeads, leadQueue) may be stale when called from effects/callbacks
    // Filter out already-processed leads to prevent re-dialing after Resume
    const allQueuedLeads = [...reQueuedLeadsRef.current, ...leadQueueRef.current]
      .filter(l => !processedLeadIdsRef.current.has(l.id));
    if (allQueuedLeads.length === 0 || availableLineIndices.length === 0) {
      return;
    }
    // Update last dial time for debounce
    lastDialTimeRef.current = now;

    isDialingNextRef.current = true;

    // Hoist outside try so catch block can access for rollback
    const leadsToCall = allQueuedLeads.slice(0, availableLineIndices.length);

    // CRITICAL: Stop the polling interval BEFORE any async work.
    // Race condition: POST /end-session marks old session as "completed" on the backend.
    // If the polling interval fires while we're awaiting initiateRaceToAnswer, it sees
    // "completed" status and sessionIdRef still points to the old session → triggers full
    // teardown (disconnect, onComplete) killing the new batch mid-creation.
    // Nullify sessionIdRef so any in-flight poll callback that already fired sees
    // sessionIdToPoll !== sessionIdRef.current and ignores the stale "completed" status.
    const previousSessionId = sessionIdRef.current;
    if (statusPollRef.current) {
      clearInterval(statusPollRef.current);
      statusPollRef.current = null;
    }
    sessionIdRef.current = null; // Prevents stale polls from matching old session

    try {

      // CRITICAL: Mark the old session as completed BEFORE creating the new one.
      // This prevents the backend's handleRaceAnswerCallEnd from independently trying to
      // dial leads for the old session (race condition: webhook fires, finds session ACTIVE,
      // tries to do batch progression — resulting in double-dialed leads).
      // Awaited with a 2s timeout so we don't block forever if the call fails.
      //
      // CALL-WAITING GUARD: if the agent is mid hold/merge handoff, DO NOT
      // end the session — that would stop the race conference and reject new
      // pickups (user saw "incoming gets killed" symptom because session was
      // stopped mid-handoff by a stale poll callback). Session stays ACTIVE
      // during call-waiting; resumes normally after the incoming ends.
      const callWaitingActive = typeof window !== 'undefined'
        && (sessionStorage.getItem('__callwaiting_active') === '1');
      if (previousSessionId && !callWaitingActive) {
        try {
          await Promise.race([
            api.post({
              url: getApiUrl(`/api/communication/multi-line-conference/${previousSessionId}/end-session`),
              body: { reason: "batch_transition" },
              auth: true,
            }),
            new Promise(resolve => setTimeout(resolve, 2000)), // 2s timeout - don't block forever
          ]);
        } catch {
          // Non-critical: old session will auto-complete when conference dies
        }
      }

      // RE-CHECK pause after async work — user may have clicked Pause during the await.
      // Without this, the race condition: dialNextLeads starts → user pauses → old session
      // ends → new session starts dialing anyway. Two session IDs, only old one paused.
      if (isPausedRef.current || sessionStoppedRef.current) {
        isDialingNextRef.current = false;
        // Restore sessionIdRef so resume can find the session
        if (!sessionIdRef.current && previousSessionId) {
          sessionIdRef.current = previousSessionId;
        }
        return;
      }

      // Start ringback audio since we're dialing new customers
      playRingback();

      // Remove leads from queue before dialing — update BOTH refs and state
      const leadsToCallIds = new Set(leadsToCall.map(l => l.id));
      reQueuedLeadsRef.current = reQueuedLeadsRef.current.filter(l => !leadsToCallIds.has(l.id));
      leadQueueRef.current = leadQueueRef.current.filter(l => !leadsToCallIds.has(l.id));
      setReQueuedLeads(prev => prev.filter(l => !leadsToCallIds.has(l.id)));
      setLeadQueue(prev => prev.filter(l => !leadsToCallIds.has(l.id)));

      // Clear "preparing next batch" indicator — new leads are loading
      setIsPreparingNextBatch(false);

      // Update lines with new leads and set to dialing
      setLines(prev => {
        const updated = [...prev];
        leadsToCall.forEach((lead, i) => {
          const lineIndex = availableLineIndices[i];
          if (lineIndex !== undefined) {
            updated[lineIndex] = {
              ...updated[lineIndex],
              lead,
              status: "dialing",
              callSid: undefined,
              startTime: new Date(),
              error: undefined,
            };
          }
        });
        return updated;
      });

      // Build the remaining lead queue for the backend (so it can also progress independently)
      const remainingQueueForBackend = [...reQueuedLeadsRef.current, ...leadQueueRef.current]
        .filter(l => !processedLeadIdsRef.current.has(l.id))
        .map(lead => ({
          phone: formatPhoneNumber(lead.phone || ""),
          leadId: lead.id,
          name: lead.name,
        }));

      // FINAL pause check before creating the new session — last chance to abort
      if (isPausedRef.current || sessionStoppedRef.current) {
        isDialingNextRef.current = false;
        stopRingback();
        return;
      }

      // Call the API to initiate race-to-answer for these leads
      const response: RaceToAnswerResponse = await callService.initiateRaceToAnswer({
        phoneNumbers: leadsToCall.map(lead => ({
          phone: formatPhoneNumber(lead.phone || ""),
          leadId: lead.id,
          name: lead.name,
        })),
        fromPhoneNumber: "", // Backend will auto-select
        metadata: {
          source: "multi_line_dialer_continuation",
          isMultiLine: true,
          leadCount: leadsToCall.length,
          aiMatchingEnabled,
          isContinuation: true,
        },
        // Pass remaining queue so backend can advance independently if frontend stalls
        fullLeadQueue: remainingQueueForBackend.length > 0 ? remainingQueueForBackend : undefined,
      });

      // TCPA: tell the agent if any leads were skipped for calling hours.
      if (response.skippedOutsideHours && response.skippedOutsideHours > 0) {
        toast({
          title: `${response.skippedOutsideHours} lead${response.skippedOutsideHours === 1 ? "" : "s"} skipped — outside calling hours`,
          description: "Calls are limited to 8am–9pm in the lead's local time.",
          duration: 6000,
        });
      }

      // Store outbound caller ID from new batch
      fromPhoneNumberRef.current = response.fromPhoneNumber;

      // Update the session ID to the new session
      // CRITICAL: Always update sessionId for continuation batches
      // Each batch creates a new session, and hold/unhold endpoints need the current sessionId
      // Update BOTH ref (immediately) and state (async) — ref must be set before polling restarts
      // so the poll handler recognizes this as the current session
      if (response.sessionId) {
        sessionIdRef.current = response.sessionId;
        setSessionId(response.sessionId);

        // Hide continuation sessions from Session History.
        // Only the primary session tracks overall progress (totalLeadsToCall = 50).
        // Continuation sessions are created by the backend for each batch (totalLeadsToCall = 4).
        // Setting totalLeadsToCall = 0 makes listSessions filter them out (WHERE total_leads_to_call > 0).
        dialerSessionApi.updateProgress(response.sessionId, {
          totalLeadsToCall: 0,
        }).catch(() => {});
      }

      // Update lines with call SIDs.
      // Filter null callSids — backend returns null for dials Twilio rejected
      // (multi-line-conference.service.ts:1118). Without filtering, every lead
      // after the rejected one gets the wrong callSid (Brandon's bug, but for
      // continuation batches).
      setLines(prev => {
        const updated = [...prev];
        const surviving = (Array.isArray(response.callSids) ? response.callSids : [])
          .filter((sid): sid is string => typeof sid === 'string' && sid.length > 0);
        leadsToCall.forEach((lead, i) => {
          const lineIndex = availableLineIndices[i];
          const callSid = surviving[i];
          if (lineIndex !== undefined) {
            updated[lineIndex] = {
              ...updated[lineIndex],
              status: "ringing",
              callSid,
            };
          }
        });
        return updated;
      });

      // CRITICAL: Reconnect the agent to the new conference
      // The agent is still on the old conference (we no longer disconnect on call end).
      // We need to disconnect from the old conference and join the new one.
      const conferenceToJoin = response.conferenceName || `race-${response.sessionId}`;
      let connectAttempts = 0;
      const MAX_CONNECT_RETRIES = 2;

      while (connectAttempts <= MAX_CONNECT_RETRIES) {
        try {
          if (connectAttempts === 0 && activeCall) {
            // Disconnect from old conference before joining new one
            disconnect();
            // Small delay to allow Twilio to clean up
            await new Promise(resolve => setTimeout(resolve, 100));
          } else if (connectAttempts > 0) {
            // Retry delay with exponential backoff: 500ms, 1000ms
            await new Promise(resolve => setTimeout(resolve, connectAttempts * 500));
          }
          const agentCall = await connect({
            to: `conference:${conferenceToJoin}`,
            // Don't pass 'from' — real per-lead caller ID arrives via 'customer_answered' event
            leadId: leadsToCall[0]?.id,
            metadata: {
              source: "multi_line_dialer_continuation",
              isMultiLine: true,
              sessionId: response.sessionId,
              conferenceName: conferenceToJoin,
              isContinuation: true,
            },
          });

          // CRITICAL: Restart ringback after reconnecting to new conference
          // disconnect() internally calls stopRingback(), killing the playRingback() from earlier.
          // We must restart it so the agent hears ringing while new calls are connecting.
          playRingback();
          break; // Success, exit retry loop
        } catch (connectError) {
          connectAttempts++;
          if (connectAttempts > MAX_CONNECT_RETRIES) {
            console.error('[MultiLineDialer] Failed to connect to conference after retries:', connectError);
            // Last resort: don't break the session, polling will still work
            // Agent won't have audio but can still see UI and end the session
            toast({ title: "Audio connection lost — end session and restart", variant: "destructive", duration: 8000 });
          }
        }
      }

      // Start polling for the new session
      if (response.sessionId) {
        startStatusPollingRef.current?.(response.sessionId);
      }

    } catch (error: any) {
      // Rollback: restore sessionIdRef so the old session's polling can still work
      if (!sessionIdRef.current && previousSessionId) {
        sessionIdRef.current = previousSessionId;
        // Restart polling for the old session since new batch failed
        startStatusPollingRef.current?.(previousSessionId);
      }
      // Rollback: revert lines to idle and re-add leads to queue
      // Without this, lines stay stuck in "dialing" forever with no recovery.
      setLines(prev => {
        const updated = [...prev];
        availableLineIndices.forEach((lineIndex) => {
          if (updated[lineIndex]?.status === "dialing") {
            updated[lineIndex] = { ...updated[lineIndex], status: "idle" as const, callSid: undefined };
          }
        });
        return updated;
      });
      // Re-add leads to the requeue so they can be retried
      reQueuedLeadsRef.current = [...reQueuedLeadsRef.current, ...leadsToCall];
      setReQueuedLeads(prev => [...prev, ...leadsToCall]);
      stopRingback();
      console.error("Failed to dial next leads:", error?.message);
      // A trial cap is a 403 with a plain-English message from the server.
      // Showing "try again" for that sends the agent in circles — they cannot
      // fix it by retrying, and they can't tell why. Surface the real reason.
      const capMsg = /limit/i.test(error?.message || "") ? error.message : null;
      toast({
        title: capMsg ? "Trial limit reached" : "Failed to start calls — try again",
        description: capMsg || undefined,
        variant: "destructive",
        duration: capMsg ? 10000 : 4000,
      });
    } finally {
      isDialingNextRef.current = false;
    }
  }, [sessionId, aiMatchingEnabled, formatPhoneNumber, playRingback, connect, activeCall, disconnect]);

  // Keep dialNextLeads ref in sync for stale closure prevention in status poll handler
  dialNextLeadsRef.current = dialNextLeads;

  // Start multi-line calling session using Race-to-Answer (conference-based)
  // This ensures both agent and lead can hear each other via a shared conference
  const startMultiLineSession = useCallback(async () => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    // Clear stale manual call pause flag from previous session
    clearManualCallPauseRequest();
    // INCIDENT FIX (July 9): also clear a stale call-waiting flag. If any
    // incoming-call-end path missed its removeItem, '__callwaiting_active'
    // stayed '1' for the WHOLE TAB (sessionStorage survives soft-nav AND
    // session restarts) — every dialNextLeads then bailed early, sessions
    // died after ~1 batch, and only a full logout/login (fresh tab state)
    // recovered. Clearing at session start makes the stall self-healing.
    try { sessionStorage.removeItem('__callwaiting_active'); } catch { /* noop */ }
    // Fresh session — clear the user-stop latch from any previous session.
    userStoppedRef.current = false;

    // CRITICAL FIX: Check if we're recovering from a page refresh
    // If we have an existing sessionId, DON'T start new calls - just recover the session
    if (sessionId) {
      setIsSessionActive(true);

      // Connect agent to the existing conference
      const conferenceName = `race-${sessionId}`;
      try {
        const agentCall = await connect({
          to: `conference:${conferenceName}`,
          from: fromPhoneNumberRef.current,
          leadId: initialLeads[0]?.id,
          metadata: {
            source: "multi_line_dialer",
            isMultiLine: true,
            sessionId: sessionId,
            conferenceName: conferenceName,
            isRecovery: true,
          },
        });

        if (agentCall) {
          // Start polling to get current session status
          startStatusPollingRef.current?.(sessionId);
        }
      } catch (error: any) {
        // Clear session data and show error
        if (typeof window !== 'undefined') {
          localStorage.removeItem(MULTILINE_SESSION_STATS_KEY);
        }
        setLines(prev => prev.map(line => ({
          ...line,
          status: "failed" as const,
          error: "Session recovery failed",
        })));
        setIsSessionActive(false);
      }
      return;
    }

    // No existing session - start fresh
    // Update all lines to dialing status
    setLines(prev => prev.map(line => ({
      ...line,
      status: "dialing" as const,
      startTime: new Date(),
    })));

    setIsSessionActive(true);

    try {
      // Step 1: Initiate race-to-answer calls to all leads
      // This creates a conference and calls all leads simultaneously
      // First to answer joins the conference, others get canceled

      // Get user's phone numbers for the from number
      // We'll use the first lead's area code matching via backend
      //
      // BATCH PROGRESSION: Pass the full lead queue to the backend
      // The backend will store remaining leads and automatically dial next batch
      // when current batch + redials complete
      const response: RaceToAnswerResponse = await callService.initiateRaceToAnswer({
        phoneNumbers: initialLeads.map(lead => ({
          phone: formatPhoneNumber(lead.phone || ""),
          leadId: lead.id,
          name: lead.name,
        })),
        fromPhoneNumber: "", // Backend will auto-select best matching number
        metadata: {
          source: "multi_line_dialer",
          isMultiLine: true,
          leadCount: initialLeads.length,
          totalLeadsInQueue: leads.length, // Total leads including queue
          aiMatchingEnabled,
        },
        // Pass full lead queue for backend batch progression
        // Backend stores leads[MAX_CONCURRENT_LINES:] and dials next batch automatically
        fullLeadQueue: leads.map(lead => ({
          phone: formatPhoneNumber(lead.phone || ""),
          leadId: lead.id,
          name: lead.name,
        })),
      });

      // TCPA: tell the agent if any leads were skipped for calling hours.
      if (response.skippedOutsideHours && response.skippedOutsideHours > 0) {
        toast({
          title: `${response.skippedOutsideHours} lead${response.skippedOutsideHours === 1 ? "" : "s"} skipped — outside calling hours`,
          description: "Calls are limited to 8am–9pm in the lead's local time.",
          duration: 6000,
        });
      }

      setSessionId(response.sessionId);
      fromPhoneNumberRef.current = response.fromPhoneNumber;

      // Track the first session as the primary for progress reporting
      if (!primarySessionIdRef.current) {
        primarySessionIdRef.current = response.sessionId;
      }

      // Save lead queue to backend session for resume (fire-and-forget).
      // Filter out already-processed leads so resume loads only un-called leads.
      if (response.sessionId) {
        const remainingLeads = leads.filter(l => !processedLeadIdsRef.current.has(l.id));
        dialerSessionApi.updateProgress(response.sessionId, {
          leadQueue: remainingLeads.map(l => ({
            leadId: l.id,
            name: l.name || '',
            phone: l.phone || '',
          })),
          totalLeadsToCall: leads.length,
        }).catch(() => {});
      }

      // Update line statuses - all are now ringing.
      //
      // Backend can return null entries in callSids when Twilio rejects a dial
      // (sub-account permission, anti-spam, dead number) — see
      // multi-line-conference.service.ts:1118 returning null on validation
      // failure. Naive `response.callSids[index]` then offsets every subsequent
      // line by one slot — exactly Brandon's wrong-name bug seeded into
      // line.callId at dial time. Filter nulls so survivors map 1:1 to the
      // frontend lines that actually got dialed.
      setLines(prev => {
        const surviving = (Array.isArray(response.callSids) ? response.callSids : [])
          .filter((sid): sid is string => typeof sid === 'string' && sid.length > 0);
        const updated = prev.map((line, index) => {
          const callSid = surviving[index] || undefined;
          return {
            ...line,
            status: "ringing" as const,
            callSid,
          };
        });
        return updated;
      });

      // Start ringback audio immediately — leads are now being dialed by the backend
      playRingback();

      // Step 2: Connect the agent to the conference via browser WebRTC
      // IMPORTANT: This happens AFTER calls are initiated because the conference
      // is created by the backend when the first call is initiated.
      // The agent joining will hear ringing audio until someone answers.
      // Safety: Disconnect any stale activeCall first (matches the guard in continuation batch)
      if (activeCall) {
        disconnect();
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      const agentCall = await connect({
        to: `conference:${response.conferenceName}`,
        // Don't pass 'from' here — this is the agent joining the conference, not an outbound call.
        // The actual per-lead caller ID will arrive via 'customer_answered' Supabase event.
        // Passing fromPhoneNumberRef (user's primary number) would display the wrong from number
        // before the real AI-matched caller ID arrives.
        leadId: initialLeads[0]?.id,
        metadata: {
          source: "multi_line_dialer",
          isMultiLine: true,
          sessionId: response.sessionId,
          conferenceName: response.conferenceName,
        },
      });

      if (!agentCall) {
        throw new Error("Failed to connect agent to conference");
      }

      // Restart ringback after connect — disconnect() above may have called stopRingback()
      playRingback();

      // Start polling for status updates to detect when someone answers
      if (response.sessionId) {
        startStatusPollingRef.current?.(response.sessionId);
      }

    } catch (error: any) {
      console.error("Failed to initiate multi-line calls:", error);
      setLines(prev => prev.map(line => ({
        ...line,
        status: "failed" as const,
        error: error.message || "Failed to initiate call",
      })));
      setIsSessionActive(false);
      setIsInMultiLineMode(false); // Clear in case connect() set it before the error
      {
        // Same as above: a 403 trial-cap message must not be reported as a
        // connection problem.
        const capMsg = /limit/i.test((error as any)?.message || "") ? (error as any).message : null;
        toast({
          title: capMsg ? "Trial limit reached" : "Calls failed to start — check your connection",
          description: capMsg || undefined,
          variant: "destructive",
          duration: capMsg ? 10000 : 5000,
        });
      }
    }
  }, [initialLeads, aiMatchingEnabled, connect, sessionId, activeCall, disconnect]);

  // Track if we're currently auto-connecting
  const isAutoConnecting = useRef(false);

  // Auto-connect to a line when someone answers
  const autoConnectToLine = useCallback(async (lineIndex: number, callSid: string) => {
    if (isAutoConnecting.current || activeCall) {
      return;
    }

    isAutoConnecting.current = true;
    // Use linesRef for fresh state to avoid stale closure
    const line = linesRef.current[lineIndex];


    setSelectedLineIndex(lineIndex);

    // Update line status to connected
    setLines(prev => prev.map((l, idx) =>
      idx === lineIndex
        ? { ...l, status: "connected" as const }
        : l.status === "ringing" || l.status === "dialing"
          ? { ...l, status: "no-answer" as const } // Cancel other ringing calls
          : l
    ));

    try {
      // Connect browser to the call via conference
      const call = await connect({
        to: `conference:${callSid}`,
        from: fromPhoneNumberRef.current,
        leadId: line.lead.id,
        metadata: {
          source: "multi_line_dialer",
          leadName: line.lead.name,
        },
      });

      if (call) {
        // Start call timer
        setCallTimer(0); callTimerValueRef.current = 0;
        if (callTimerRef.current) clearInterval(callTimerRef.current);
        callTimerRef.current = setInterval(() => {
          setCallTimer(prev => { callTimerValueRef.current = prev + 1; return prev + 1; });
        }, 1000);
      }
    } catch (error: any) {
      console.error("Failed to auto-connect to line:", error);
      toast({ title: "Failed to connect to call — try clicking again", variant: "destructive", duration: 4000 });
      setSelectedLineIndex(null);
    } finally {
      isAutoConnecting.current = false;
    }
  // IMPORTANT: Do NOT include 'lines' in deps - use linesRef instead
  }, [activeCall, connect]);

  // Poll for call status updates
  const pollFailCountRef = useRef(0);
  const startStatusPolling = useCallback((sessionIdToPoll: string) => {
    if (statusPollRef.current) {
      clearInterval(statusPollRef.current);
    }
    pollFailCountRef.current = 0;
    // Per interval (2026-10-09): this poll stops only itself. A slow response from the previous
    // batch's poll used to clear statusPollRef — by then the NEW batch's interval — so the new
    // batch ran with no status polling, and reset the shared in-flight flag under it.
    let inFlight = false;
    const stopThisPoll = () => {
      clearInterval(intervalId);
      if (statusPollRef.current === intervalId) statusPollRef.current = null;
    };

    const intervalId = setInterval(async () => {
      // Skip polling while paused or manual call auto-stop in progress
      if (isPausedRef.current || isManualCallPauseRequested()) return;
      // Never stack overlapping polls — on a slow network a stale response can
      // land AFTER a newer one and briefly regress badges to older statuses
      if (inFlight) return;
      inFlight = true;

      try {
        const status = await callService.getMultiLineSessionStatus(sessionIdToPoll);

        // Track if the currently connected line just disconnected (client hung up)
        let clientDisconnectedLineIndex: number | null = null;
        // NOTE: We no longer detect "answered" via polling status because AMD takes time
        // Human answers are detected via Supabase customer_answered event instead
        // This prevents false positives when voicemail systems "answer"

        // Update line statuses.
        //
        // Backend's getMultiLineSessionStatus returns ALL session_calls for the
        // session (ordered by createdAt ASC) — including lines from PREVIOUS
        // batches that have already disconnected. Frontend's `lines` array only
        // holds the visible current-batch slots (4 max). So `status.lines[index]`
        // POSITIONAL alignment misaligns whenever there's any prior-batch row in
        // the response.
        //
        // Brandon's "Mildred shown / Michael answered" bug:
        //   batch 1: Mildred at line_number=4 (no-answer)
        //   batch 2: Mcneight, Mooney, Michael, Rura at line_numbers 5–8
        //   status.lines = [Mildred(4), Mcneight(5), Mooney(6), Michael(7), Rura(8)]
        //   prev (frontend) = [Mcneight, Mooney, Michael, Rura]   (4 entries)
        //   prev[0]=Mcneight aligned with status[0]=Mildred → wrong status applied
        //   prev[2]=Michael aligned with status[2]=Mooney → Michael never marked connected
        //   prev[3]=Rura aligned with status[3]=Michael → Rura's slot got AMD=human
        // Then Mildred's slot was effectively kept "connected" elsewhere because
        // her per-batch lineNumber was reused.
        //
        // Fix: match by stable identifiers (callSid first, then leadId) instead
        // of array position. Falls back to lineNumber-as-property only if both
        // miss — never positional index.
        const matchBackendStatus = (frontendLine: any) => {
          if (!Array.isArray(status.lines)) return null;
          // Backend response shape (multi-line-conference.service.ts:914):
          //   { lineId, lineNumber, leadId, customerCallSid, status, ... }
          // We try most→least reliable identifiers.
          if (frontendLine.callSid) {
            const byCallSid = status.lines.find(
              (s: any) => s?.customerCallSid === frontendLine.callSid || s?.callSid === frontendLine.callSid,
            );
            if (byCallSid) return byCallSid;
          }
          if (frontendLine.lead?.id) {
            const byLead = status.lines.find((s: any) => s?.leadId === frontendLine.lead.id);
            if (byLead) return byLead;
          }
          if (typeof frontendLine.lineNumber === 'number') {
            const byLineNumber = status.lines.find((s: any) => s?.lineNumber === frontendLine.lineNumber);
            if (byLineNumber) return byLineNumber;
          }
          return null;
        };

        setLines(prev => {
          const updated = prev.map((line, index) => {
            const lineStatus = matchBackendStatus(line);
            if (!lineStatus) return line;

            // Map backend status to our status
            let newStatus = line.status;
            // Cast to string since backend may return extended status types like "busy", "canceled"
            const backendStatus = lineStatus.status as string;
            // SKIP FLICKER GUARD: never resurrect a terminal line back to a pre-answer
            // state. When the agent skips, the UI optimistically marks the line
            // "no-answer" while the cancel API is still in flight — a poll response
            // fetched before the cancel landed still says "ringing" and would flip
            // the badge back to blue "Ringing..." for ~1s before the next poll
            // corrects it. A canceled/completed call can never legitimately return
            // to dialing/ringing, so terminal states only move forward from here.
            const isTerminalLineStatus = ["no-answer", "voicemail", "failed", "disconnected"].includes(line.status);
            switch (backendStatus) {
              case "dialing":
              case "initiated":
              case "queued":
              case "ready-to-dial":
                if (isTerminalLineStatus) break;
                newStatus = "dialing";
                break;
              case "ringing":
                if (isTerminalLineStatus) break;
                newStatus = "ringing";
                break;
              case "connected":
              case "answered":
              case "in-progress":
                // These statuses mean the call was "answered" but we DON'T KNOW if it's human or voicemail yet
                // AMD detection takes a few seconds. Don't treat as fully connected until we're sure it's human.
                // PRIMARY: We rely on customer_answered Supabase event (which only fires for humans)
                // FALLBACK: If Supabase event missed, check amdStatus from polling - if human/unknown, it's connected
                //
                // ALSO check connectedLineIndexRef — the customer_answered handler sets
                // this ref SYNCHRONOUSLY but setLines state flushes async. If polling
                // runs in that narrow window, `line.status` lags behind the ref,
                // and without this check we'd flip "connected" back to "connecting"
                // (Brandon's name-flickering symptom).
                if (line.status === "connected" || connectedLineIndexRef.current === index) {
                  // Already connected (human answered via customer_answered event) - keep it
                  newStatus = "connected";
                } else if (lineStatus.amdStatus === "human" || lineStatus.amdStatus === "unknown") {
                  // PICKUP CHIME: fire once when we detect a line just picked
                  // up (AMD confirmed this line's a candidate winner). Helps
                  // the agent realize they need to start talking NOW even
                  // though the AMD-is-human broadcast is still ~2s out.
                  if (line.callSid && !chimeFiredForCallSidsRef.current.has(line.callSid)) {
                    chimeFiredForCallSidsRef.current.add(line.callSid);
                    try { playPickupChime(); } catch {}
                  }
                  // FALLBACK: AMD confirmed human but customer_answered event was missed
                  // This can happen if Supabase Realtime has connection issues
                  // Transition to connected and trigger the connection flow
                  newStatus = "connected";
                } else if (isTerminalLineStatus) {
                  // SKIP FLICKER GUARD (auto-skip path): the line is already finalized
                  // (voicemail auto-hangup / skipped / ended) but a stale poll row still
                  // says "in-progress" — e.g. fetched in the instant a VM picked up,
                  // landing after the customer_disconnected broadcast set "voicemail".
                  // Without this, the box flips Voicemail -> "Ringing..." -> Voicemail
                  // and fires a false pickup chime. AMD human/unknown above still wins
                  // (an answer that beat a skip is a live call and must surface).
                  break;
                } else if (line.status === "connecting") {
                  // Already in "connecting" state - keep it
                  // Wait for customer_answered (human) or customer_disconnected (voicemail)
                  newStatus = "connecting";
                } else {
                  // NEW: Show "connecting" immediately for better UX when polling detects answered
                  // This gives users immediate feedback that someone picked up, even before AMD confirms human
                  newStatus = "connecting";
                  // PICKUP CHIME on the ring → connecting transition. This is
                  // the earliest possible signal — ~800ms after real pickup
                  // (polling cadence). Brandon hears a chime, looks at the
                  // screen, says "Hi Sandra!" — customer doesn't hang up.
                  if (line.callSid && !chimeFiredForCallSidsRef.current.has(line.callSid)) {
                    chimeFiredForCallSidsRef.current.add(line.callSid);
                    try { playPickupChime(); } catch {}
                  }
                }
                break;
              case "on-hold":
                newStatus = "on-hold";
                break;
              case "disconnected":
              case "completed":
                // Check if this line WAS connected (client hung up)
                if (line.status === "connected") {
                  clientDisconnectedLineIndex = index;
                }
                newStatus = "disconnected";
                break;
              case "busy":
              case "failed":
                newStatus = "failed";
                break;
              case "canceled":
                // In race-to-answer, canceled means another line answered first — not a failure.
                // Show as "no-answer" so the UI doesn't alarm the agent with red "Failed" badges.
                newStatus = "no-answer";
                break;
              case "no-answer":
              case "missed":
                newStatus = "no-answer";
                break;
              case "voicemail":
                // AMD confirmed it's voicemail - mark as voicemail (not connected)
                // This overrides any previous "ringing" state
                newStatus = "voicemail";
                break;
              case "idle":
                if (line.status === "ringing" || line.status === "dialing") {
                  newStatus = "no-answer";
                }
                break;
              default:
                // Unknown status - keep current
                break;
            }

            // Only return new object if something changed
            const newCallSid = lineStatus.customerCallSid || line.callSid;
            if (newStatus === line.status && newCallSid === line.callSid) {
              return line; // No change, return same reference
            }
            return {
              ...line,
              status: newStatus,
              // Use customerCallSid (Twilio SID starting with "CA") for cancellation, not lineId (UUID)
              callSid: newCallSid,
            };
          });

          // Only return new array if something actually changed
          const hasChanges = updated.some((line, idx) => line !== prev[idx]);
          return hasChanges ? updated : prev;
        });

        // FALLBACK: Check if AMD confirmed human but Supabase event was missed.
        // This handles cases where Supabase Realtime connection has issues.
        //
        // CRITICAL: Iterate `status.lines` (backend ordered by createdAt) and
        // resolve each to a frontend index via leadId/callSid — NOT via array
        // position. Backend returns ALL session_calls including prior batches;
        // positional iteration was Brandon's "Mildred shown / Michael answered"
        // bug — the i-th status row pointed at a different agent slot than
        // the i-th frontend line, and AMD=human on Michael got attributed to
        // the slot at index 3 (which wasn't Michael).
        if (connectedLineIndexRef.current === null && !isPostCallRef.current && !sessionStoppedRef.current) {
          for (let i = 0; i < status.lines.length; i++) {
            const lineStatus: any = status.lines[i];
            if (!(lineStatus?.amdStatus === "human" || lineStatus?.amdStatus === "unknown")) continue;

            // Resolve to a frontend line by stable identifier.
            const frontendIndex = linesRef.current.findIndex((l: any) =>
              (l.callSid && (l.callSid === lineStatus.customerCallSid || l.callSid === lineStatus.callSid))
              || (l.lead?.id && l.lead.id === lineStatus.leadId)
            );
            if (frontendIndex === -1) continue;

            const currentLine = linesRef.current[frontendIndex];
            if (!currentLine || ["disconnected", "failed", "no-answer", "voicemail", "idle"].includes(currentLine.status)) {
              continue;
            }

            // Update connectedLineIndex ref FIRST (same as Supabase handler)
            connectedLineIndexRef.current = frontendIndex;

            // Set the active conference — use sessionIdRef to avoid stale closure
            if (sessionIdRef.current) {
              setActiveConference(`race-${sessionIdRef.current}`);
            }

            // Increment answered count (both state AND ref)
            setTotalAnsweredCount((prev: number) => prev + 1);
            totalAnsweredCountRef.current += 1;

            // Select this line and show controls
            setSelectedLineIndex(frontendIndex);
            setConnectedLineIndex(frontendIndex);

              // Track lead for showing details after call ends
              const leadIdForTracking = linesRef.current[frontendIndex]?.lead?.id;
              if (leadIdForTracking) setLastActiveLeadId(leadIdForTracking);

              // Update outbound caller ID + winning lead from polling data (fallback for missed Supabase event)
              const fbLine = linesRef.current[frontendIndex];
              const fbUpdates: Record<string, any> = {};
              if (lineStatus.from) fbUpdates.outboundCallerId = lineStatus.from;
              if (fbLine?.lead?.name) fbUpdates.leadName = fbLine.lead.name;
              if (fbLine?.lead?.id) fbUpdates.leadId = fbLine.lead.id;
              if (fbLine?.lead?.phone) fbUpdates.phoneNumber = fbLine.lead.phone;
              if (Object.keys(fbUpdates).length > 0) {
                updateCallMetadata(fbUpdates);
              }

              // Stop ringback since someone answered
              stopRingback();

              // Start call timer
              setCallTimer(0); callTimerValueRef.current = 0;
              if (callTimerRef.current) clearInterval(callTimerRef.current);
              callTimerRef.current = setInterval(() => {
                setCallTimer(prev => { callTimerValueRef.current = prev + 1; return prev + 1; });
              }, 1000);

              // Mark other lines for re-queue (they lost the race)
              // CRITICAL: Include "no-answer" status too — the polling handler itself may have
              // already transitioned canceled calls to "no-answer" before reaching this fallback.
              const reQueueStatuses = ["dialing", "ringing", "connecting", "no-answer", "voicemail", "failed"];
              const otherLines = linesRef.current.filter(
                (line, idx) => idx !== frontendIndex && line.lead && reQueueStatuses.includes(line.status)
              );

              // Re-queue leads that lost the race (same as Supabase handler)
              const fallbackLeadsToReQueue = otherLines
                .map(line => line.lead)
                .filter((lead): lead is Lead => lead != null);
              if (fallbackLeadsToReQueue.length > 0) {
                addToReQueue(fallbackLeadsToReQueue);
              }

              // Cancel calls that are still ringing on Twilio's side
              const otherRingingLines = otherLines.filter(line =>
                (line.status === "dialing" || line.status === "ringing") && line.callSid
              );
              if (otherRingingLines.length > 0) {
                Promise.all(
                  otherRingingLines.map(async (line) => {
                    if (!line.callSid || !shouldCancelCall(line.callSid)) return;
                    try {
                      await api.post({
                        url: getApiUrl(`/api/communication/calls/${line.callSid}/cancel`),
                        body: { reason: "another_line_answered_fallback" },
                        auth: true,
                      });
                    } catch (error) {
                      console.error(`[MultiLineDialer] Failed to cancel call ${line.callSid}:`, error);
                    }
                  })
                );
              }

              break; // Only one winner
          }
        }

        // If client disconnected from a connected line, update the UI state
        // Only handle if we haven't already cleared the connected state (Supabase may have beat us here)
        if (clientDisconnectedLineIndex !== null && connectedLineIndexRef.current !== null) {
          // Stop call timer
          if (callTimerRef.current) {
            clearInterval(callTimerRef.current);
            callTimerRef.current = null;
          }
          // CRITICAL: Do NOT disconnect the agent from the conference here.
          // The agent should stay on the conference so remaining lines can still
          // ring/connect, and so the next batch can transition cleanly.
          // The agent's conference leg is properly disconnected later by:
          // - startMultiLineSession() when the next batch starts
          // - The session completion handler when there are no more leads

          // Clear connected line (but keep selectedLineIndex to show lead details)
          connectedLineIndexRef.current = null; // Update ref immediately
          setConnectedLineIndex(null); setAppleScreeningDetected(false);
          setCallTimer(0); callTimerValueRef.current = 0;

          // Mark this lead as processed (unique tracking via Set prevents double-counting)
          const disconnectedLead = linesRef.current[clientDisconnectedLineIndex]?.lead;
          if (disconnectedLead?.id && !processedLeadIdsRef.current.has(disconnectedLead.id)) {
            processedLeadIdsRef.current.add(disconnectedLead.id);
            setProcessedCount(processedLeadIdsRef.current.size);
          }
        }

        // Check if session is completed — trigger full teardown + onComplete
        // CRITICAL: Only act on "completed" if this poll is for the CURRENT session.
        // Race condition: When dialNextLeads() creates a new session, an in-flight poll
        // for the OLD session may return "completed" (backend auto-completed it because
        // the agent disconnected from the old conference). Without this check, the handler
        // would kill the NEW agent call via disconnectRef.current() and end the entire session.
        if ((status.status === "completed" || status.status === "cancelled") &&
            sessionIdToPoll === sessionIdRef.current) {

          // CRITICAL: Check if the frontend still has leads to dial before tearing down.
          // The backend auto-completes sessions when all calls in the batch end and the
          // conference dies — but it doesn't know about the frontend's lead queue.
          // If we still have leads, trigger the next batch instead of ending the session.
          const remainingLeads = [...reQueuedLeadsRef.current, ...leadQueueRef.current]
            .filter(l => !processedLeadIdsRef.current.has(l.id)).length;
          if (remainingLeads > 0) {
            // Still have leads — trigger next batch regardless of connected/disposition state.
            // If agent is still connected or awaiting disposition, the dialNextLeads guards
            // will handle it (return early). But do NOT fall through to sessionStopped = true,
            // because that permanently kills all future batch attempts.
            if (connectedLineIndexRef.current === null && !awaitingMultiLineDispositionRef.current && !isPausedRef.current) {
              // Safe to dial now (and not paused — paused sessions should NOT auto-dial)
              stopThisPoll();
              const availableLines = linesRef.current
                .map((line, index) => ({ line, index }))
                .filter(({ line }) => ["failed", "no-answer", "voicemail", "disconnected", "idle"].includes(line.status))
                .map(({ index }) => index);
              if (availableLines.length > 0) {
                dialNextLeadsRef.current?.(availableLines);
              }
            }
            // Either way, don't tear down — there are still leads
            return;
          }

          // No more leads — full teardown
          if (sessionStoppedRef.current) {
            return;
          }
          // INCIDENT FIX (July 9): NEVER tear down while a line is CONNECTED.
          // A stale/racing backend "completed" (conference quorum blip, old
          // session's batch_transition end) could land here mid-conversation
          // and disconnect the agent's LIVE call, dropping the UI to "ready".
          // Same guard dialNextLeads already uses — mirrored here.
          if (
            connectedLineIndexRef.current !== null ||
            linesRef.current?.some((l: any) => l.status === 'connected')
          ) {
            return; // live conversation — let its own end flow do the teardown
          }
          sessionStoppedRef.current = true;

          stopThisPoll();
          // Full teardown: disconnect Twilio, clear state, stop timers
          if (callTimerRef.current) {
            clearInterval(callTimerRef.current);
            callTimerRef.current = null;
          }
          if (batchCompletionTimerRef.current) {
            clearTimeout(batchCompletionTimerRef.current);
            batchCompletionTimerRef.current = null;
          }
          stopRingback();
          try { if (disconnectRef.current) disconnectRef.current(); } catch (e) { /* may already be disconnected */ }
          connectedLineIndexRef.current = null;
          setConnectedLineIndex(null); setAppleScreeningDetected(false);
          setCallTimer(0); callTimerValueRef.current = 0;
          setIsSessionActive(false);
          setIsInMultiLineMode(false); // Allow incoming calls again

          // Clear persisted session stats
          if (typeof window !== 'undefined') {
            localStorage.removeItem(MULTILINE_SESSION_STATS_KEY);
          }

          // Calculate and report session stats via onComplete callback
          const totalDialTime = getDialSeconds();
          const statuses = Object.values(leadStatusesRef.current);
          onComplete({
            answered: totalAnsweredCountRef.current,
            called: processedLeadIdsRef.current.size + resumeOffset,
            totalDialTime,
            talkSeconds: sessionTalkSecondsRef.current,
            conversationSeconds: sessionConversationSecondsRef.current,
            appointments: statuses.filter(s => s === 'Appt').length,
            underwriting: statuses.filter(s => s === 'Underwriting').length,
            sold: statuses.filter(s => s === 'Sold & Approved').length,
          });
        } else if ((status.status === "completed" || status.status === "cancelled") &&
                   sessionIdToPoll !== sessionIdRef.current) {
          // Stale poll response for an old session — ignore and stop this interval
          stopThisPoll();
        }
        pollFailCountRef.current = 0; // Reset on success
      } catch (error) {
        console.error("Failed to poll session status:", error);
        pollFailCountRef.current++;
        if (pollFailCountRef.current === 5) {
          toast({ title: "Connection issue — status updates may be delayed", variant: "destructive", duration: 5000 });
        }
      } finally {
        inFlight = false;
      }
    }, 800); // Poll for status updates - reduced from 1500ms for faster feedback
    statusPollRef.current = intervalId;
  // IMPORTANT: Do NOT include 'lines' in deps - use linesRef instead to prevent callback recreation on every state change
  // Note: addToReQueue is used in the fallback handler but has stable identity (useCallback with [] deps)
  // IMPORTANT: Do NOT include 'disconnect' in deps - it's not useCallback-memoized (new identity every render).
  // The polling callback uses disconnectRef.current() instead of disconnect() directly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCall]);

  // Keep the ref in sync with the latest callback
  startStatusPollingRef.current = startStatusPolling;

  // End call on current line.
  // opts.skipCustomerHangup: advance the agent WITHOUT sending end-line. Used by
  // the manual voicemail drop — the drop's own <Play>…<Hangup/> ends the customer
  // leg after the audio airs, so hanging up here would cut the voicemail off.
  // (onClick passes a MouseEvent, which has no skipCustomerHangup → normal hangup.)
  const handleEndCall = useCallback((opts?: { skipCustomerHangup?: boolean }) => {
    const skipCustomerHangup = opts?.skipCustomerHangup === true;
    // Spam protection: Debounce rapid clicks
    const now = Date.now();
    if (now - lastSkipTimeRef.current < SKIP_DEBOUNCE_MS || endCooldownActive) {
      return;
    }
    lastSkipTimeRef.current = now;

    // Visual cooldown feedback
    setEndCooldownActive(true);
    if (endCooldownTimer.current) clearTimeout(endCooldownTimer.current);
    endCooldownTimer.current = setTimeout(() => setEndCooldownActive(false), 1500);
    // (rest of original handleEndCall continues below)

    // Stop call timer
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }

    // CRITICAL: End the customer's call on the backend (fire and forget - no await)
    // Since agent has endConferenceOnExit: false (for crash recovery),
    // we must explicitly tell backend to end the customer's call
    const currentLine = selectedLineIndex !== null ? linesRef.current[selectedLineIndex] : null;
    if (sessionId && currentLine?.lineNumber && !skipCustomerHangup) {
      api.post({
        url: getApiUrl(`/api/communication/multi-line-conference/${sessionId}/end-line`),
        body: { lineNumber: currentLine.lineNumber },
        auth: true,
      }).catch(() => {
        // Failed to end call on backend - call may have already ended
      });
    }

    // CRITICAL: Do NOT call disconnect() here!
    // In multi-line mode, the agent must remain on the conference after ending one customer's call.
    // disconnect() would kill the agent's entire conference connection (WebRTC + sends cancel API),
    // making the agent unable to hear remaining ringing calls or connect to the next batch.
    // The agent's conference leg is properly disconnected later by:
    // - startMultiLineSession() when the next batch starts (disconnect + reconnect)
    // - The session completion handler when there are no more leads

    // Clear active conference (for add party functionality)
    setActiveConference(null);

    // CRITICAL: Capture call duration BEFORE clearing the timer ref
    // The batch completion effect needs this to check if the call was 60s+
    const finalCallDuration = callTimerValueRef.current;
    // Accumulate session talk time. Safe at both hangup sites: whichever
    // runs first zeroes callTimerValueRef, so the other adds 0. The 60s
    // threshold matches the dashboard's "real conversation" definition so
    // the two surfaces cannot drift.
    if (finalCallDuration > 0) {
      sessionTalkSecondsRef.current += finalCallDuration;
      if (finalCallDuration >= 60) {
        sessionConversationSecondsRef.current += finalCallDuration;
      }
    }


    // CRITICAL: Clear connected line ref BEFORE updating lines state
    // This ensures the batch completion effect sees the call as ended
    // and can trigger dialing of queued leads
    connectedLineIndexRef.current = null;
    setConnectedLineIndex(null); setAppleScreeningDetected(false);
    setCallTimer(0); callTimerValueRef.current = 0;

    // POST-CALL GUARD: Block late customer_answered events from auto-connecting a new lead.
    // Backend AMD promotion or late webhooks can fire after agent hangs up — this prevents
    // the UI from showing a new "answered" lead until the next batch is explicitly dialed.
    isPostCallRef.current = true;

    // Mark this lead as processed (unique tracking via Set prevents double-counting)
    const currentLead = selectedLineIndex !== null ? linesRef.current[selectedLineIndex]?.lead : null;
    if (currentLead?.id && !processedLeadIdsRef.current.has(currentLead.id)) {
      processedLeadIdsRef.current.add(currentLead.id);
      setProcessedCount(processedLeadIdsRef.current.size);
    }

    // Update line status and track the lead for showing details after call ends
    // Use finalCallDuration captured above (callTimerValueRef is already cleared to 0)
    if (selectedLineIndex !== null) {
      const leadId = linesRef.current[selectedLineIndex]?.lead?.id;
      if (leadId) setLastActiveLeadId(leadId);

      // CRITICAL: Update linesRef SYNCHRONOUSLY so the batch completion effect
      // can see the duration immediately. setLines is batched by React and may
      // not be visible when the effect runs.
      linesRef.current = linesRef.current.map((line, idx) =>
        idx === selectedLineIndex
          ? { ...line, status: "disconnected" as const, duration: finalCallDuration }
          : line
      );

      setLines(prev => prev.map((line, idx) =>
        idx === selectedLineIndex
          ? { ...line, status: "disconnected" as const, duration: finalCallDuration }
          : line
      ));
    }

    // Hangup attribution toast — only when an actual connected call was active
    // (currentLine is set to the connected line and we just ended that call).
    // Skipped when no line is selected (e.g., between batches).
    if (currentLine?.lead || currentLine?.lineNumber) {
      const displayName = currentLine.lead?.name?.trim()
        || currentLine.lead?.phone
        || `Line ${currentLine.lineNumber}`;
      const dur = finalCallDuration;
      toast({
        title: `You ended the call with ${displayName}`,
        description: dur > 0 ? `Talk time: ${formatDurationShort(dur)}.` : 'Hung up before connection.',
        duration: 4000,
        // Green stripe = agent completed a call. Gray stripe = cancelled before connection.
        className: dur > 0 ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-gray-300',
      });
    }

  }, [selectedLineIndex, setActiveConference, sessionId, toast]);

  // ── Manual "Drop Voicemail" ──────────────────────────────────
  // Plays the resolved drop (tag → stage → source → default) on the connected
  // call and hangs up — same primitive as auto-drop — then advances like End
  // Call. Covers the case AMD misses (a voicemail that answered as "human").
  // Gated on the agent having ≥1 voicemail drop configured.
  const [hasVoicemailDrops, setHasVoicemailDrops] = useState(false);
  const [droppingVm, setDroppingVm] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get<any>({ url: getApiUrl("/communication/voicemail-drops"), auth: true });
        const drops = r?.data?.data || r?.data || [];
        if (!cancelled) setHasVoicemailDrops(Array.isArray(drops) && drops.length > 0);
      } catch { /* leave hidden on error */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleDropVoicemail = useCallback(() => {
    const idx = connectedLineIndexRef.current;
    const lineCallSid = idx !== null ? linesRef.current[idx]?.callSid : undefined;
    const effectiveCallSid = lineCallSid || activeCallSid;
    if (!effectiveCallSid || droppingVm) return;
    const leadId = stableConnectedLeadId
      || (idx !== null ? linesRef.current[idx]?.lead?.id : undefined);
    setDroppingVm(true);
    // Optimistically flip THIS line's badge to "Voicemail" immediately so the
    // grid doesn't sit on "Answered" until the VM finishes playing and the
    // disconnect webhook lands. 'voicemail' is a terminal status, so the status
    // poll respects it (won't revert to answered). Update linesRef synchronously
    // too so the batch/watchdog logic sees it right away.
    if (idx !== null) {
      linesRef.current = linesRef.current.map((l, i) =>
        i === idx ? { ...l, status: "voicemail" as const } : l,
      );
      setLines((prev) => prev.map((l, i) =>
        i === idx ? { ...l, status: "voicemail" as const } : l,
      ));
    }
    // FIRE-AND-FORGET: the backend plays the voicemail + hangs up the customer
    // leg on its own. We do NOT await it — the agent advances INSTANTLY and the
    // drop runs fully in the background. (skipCustomerHangup so the advance
    // never sends end-line, which would cut the voicemail off.)
    api.post({
      url: getApiUrl(`/communication/calls/${effectiveCallSid}/drop-voicemail`),
      body: { leadId, sessionId },
      auth: true,
    })
      .catch(() => {
        toast({ title: "Voicemail drop may have failed", variant: "destructive", duration: 3000 });
      })
      .finally(() => setDroppingVm(false));
    toast({ title: "Voicemail dropping in background", duration: 2000 });
    handleEndCall({ skipCustomerHangup: true });
  }, [activeCallSid, stableConnectedLeadId, sessionId, droppingVm, toast, handleEndCall]);

  // Handle hold toggle - use multi-line specific endpoints for proper hold music
  const handleHoldToggle = useCallback(async () => {
    // Multi-line dialer needs to use the session-aware hold endpoints
    // which properly handle the race-{sessionId} conference pattern
    if (!sessionId || connectedLineIndex === null) {
      return;
    }

    const lineNumber = connectedLineIndex + 1; // lineNumber is 1-indexed
    const newHoldState = !isOnHold;
    const endpoint = isOnHold
      ? `/communication/multi-line-conference/${sessionId}/unhold-line`
      : `/communication/multi-line-conference/${sessionId}/hold-line`;

    try {
      await api.post({
        url: getApiUrl(endpoint),
        body: { lineNumber },
        auth: true,
      });

      // Update the hold state in the context
      setIsOnHold(newHoldState);
    } catch (error) {
      console.error('[MultiLineDialer] Failed to toggle hold:', error);
      toast({ title: "Hold failed — try again", variant: "destructive", duration: 3000 });
    }
  }, [isOnHold, sessionId, connectedLineIndex, setIsOnHold, toast]);

  // 3-Way Calling Handlers
  const handleAddParticipant = async (phoneNumber: string, name?: string, role?: 'agent' | 'customer' | 'consultant') => {
    const success = await addParticipantToCall(phoneNumber, name, role);
    return success;
  };

  const handleMuteToggle = async (participantSid: string, currentlyMuted: boolean) => {
    setLoadingParticipantSid(participantSid);
    await muteParticipant(participantSid, !currentlyMuted);
    setLoadingParticipantSid(null);
  };

  const handleParticipantHoldToggle = async (participantSid: string, currentlyOnHold: boolean) => {
    setLoadingParticipantSid(participantSid);
    await holdParticipant(participantSid, !currentlyOnHold);
    setLoadingParticipantSid(null);
  };

  const handleRemoveParticipant = async (participantSid: string) => {
    setLoadingParticipantSid(participantSid);
    await removeParticipantFromCall(participantSid);
    setLoadingParticipantSid(null);
  };

  // DNC asks first (and offers to block their calls and texts).
  const { confirmDnc, modal: dncModal } = useDncConfirm();

  // Handler for updating lead status (Bad #, DNC, etc.)
  const handleStatusUpdate = useCallback(async (status: string, noSaleReason?: string, blockInbound?: boolean) => {
    const leadId = stableConnectedLine?.lead?.id || noSaleLeadIdRef.current;
    if (!leadId) return;

    // Intercept No Sale to show reason modal
    if (status === LeadStatus.NO_SALE && !noSaleReason) {
      noSaleLeadIdRef.current = leadId; // Capture lead ID before modal opens
      setShowNoSaleModal(true);
      return;
    }

    try {
      await updateLeadStatusMutation.mutateAsync({
        leadId,
        status: status as LeadStatus,
        ...(noSaleReason ? { noSaleReason } : {}),
        ...(blockInbound ? { blockInbound } : {}),
      });

      // Track lead status for session stats (Appt, Underwriting, Sold & Approved)
      setLeadStatuses(prev => ({
        ...prev,
        [leadId]: status,
      }));
      // Also update the ref immediately to ensure it's captured in completion timeout
      leadStatusesRef.current = { ...leadStatusesRef.current, [leadId]: status };
    } catch (error) {
      console.error("Failed to update lead status:", error);
      toast({ title: "Failed to save status — try again", variant: "destructive", duration: 4000 });
    } finally {
      if (status === LeadStatus.NO_SALE) {
        noSaleLeadIdRef.current = null;
      }
    }
  }, [stableConnectedLine?.lead?.id, updateLeadStatusMutation]);

  const handleNoSaleConfirm = useCallback(async (reason: string) => {
    await handleStatusUpdate(LeadStatus.NO_SALE, reason);
    handleEndCall();
  }, [handleStatusUpdate, handleEndCall]);

  // Bridge: minimized dialer's End Call button can't reach our handleEndCall
  // directly (different component tree), so we listen for a custom event it
  // dispatches. Without this, clicking End on the minimized widget while in
  // multi-line only ended the agent's browser leg — the customer stayed on
  // the line because the conference has endConferenceOnExit:false on the
  // agent participant.
  useEffect(() => {
    const onMinimizedEnd = () => handleEndCall();
    window.addEventListener('multiline:end-current-call', onMinimizedEnd);
    return () => window.removeEventListener('multiline:end-current-call', onMinimizedEnd);
  }, [handleEndCall]);

  // STUCK-STATE WATCHDOG: If connectedLineIndex stays set but the line's
  // backend status flips to a terminal state (disconnected/voicemail/no-answer/
  // failed/canceled/completed), the customer_disconnected broadcast was likely
  // missed (network drop, supabase channel hiccup). Without intervention the
  // UI freezes on "active call" with no audio — Mark/Bruce had to click End
  // manually to recover. Auto-clear after 2s of mismatch so the next batch
  // can render.
  useEffect(() => {
    if (connectedLineIndex === null) return;
    const TERMINAL_STATUSES = ['disconnected', 'voicemail', 'no-answer', 'failed', 'canceled', 'completed'] as const;
    const watchdog = setInterval(() => {
      const line = linesRef.current[connectedLineIndex];
      if (line && TERMINAL_STATUSES.includes(line.status as any)) {
        // eslint-disable-next-line no-console
        console.warn('[MultiLine] Stuck connectedLineIndex — line is terminal, clearing UI', {
          connectedLineIndex, status: line.status,
        });
        connectedLineIndexRef.current = null;
        setConnectedLineIndex(null);
        setAppleScreeningDetected(false);
      }
    }, 2000);
    return () => clearInterval(watchdog);
  }, [connectedLineIndex]);

  // ABSOLUTE FALLBACK: If connectedLineIndex stays set for >15s and the
  // line's local status flipped to "voicemail" — clear it. The 2s watchdog
  // above catches the common case (status mismatch); this catches the rarer
  // case where status updates take longer to propagate.
  useEffect(() => {
    if (connectedLineIndex === null) return;
    const fallback = setTimeout(() => {
      const line = linesRef.current[connectedLineIndex];
      if (line?.status === 'voicemail' && connectedLineIndexRef.current === connectedLineIndex) {
        // eslint-disable-next-line no-console
        console.warn('[MultiLine] 15s fallback — voicemail winner stuck connected, force-clearing', {
          connectedLineIndex, status: line?.status,
        });
        connectedLineIndexRef.current = null;
        setConnectedLineIndex(null);
        setAppleScreeningDetected(false);
      }
    }, 15000);
    return () => clearTimeout(fallback);
  }, [connectedLineIndex]);

  // 90s ABSOLUTE TIMEOUT: covers the ~1-2% case where Twilio's AMD webhook
  // never fires for the winning line. The line stays in 'connected' /
  // 'ringing' forever because neither watchdog above has anything terminal
  // to react to. After 90s of no resolution we force-fail the line —
  // session can advance to the next batch, agent isn't stuck.
  // 90s is generous (real conversations last longer) but caps the worst
  // case for a wedged AMD pipeline.
  useEffect(() => {
    if (connectedLineIndex === null) return;
    const startedAt = Date.now();
    const safety = setTimeout(() => {
      if (connectedLineIndexRef.current !== connectedLineIndex) return;
      const line = linesRef.current[connectedLineIndex];
      // Only force-fail if the line is still in non-conversational status.
      // Don't kill an actual long conversation (status='connected' with
      // audio flowing is the agent's productive call).
      const wedged = line && ['ringing', 'dialing', 'connecting', 'on-hold'].includes(line.status);
      if (wedged) {
        // eslint-disable-next-line no-console
        console.warn('[MultiLine] 90s safety — line wedged in pre-conversation state, force-clearing', {
          connectedLineIndex, status: line?.status, ageMs: Date.now() - startedAt,
        });
        connectedLineIndexRef.current = null;
        setConnectedLineIndex(null);
        setAppleScreeningDetected(false);
      }
    }, 90000);
    return () => clearTimeout(safety);
  }, [connectedLineIndex]);

  // Mark as Bad # and end call
  const handleMarkBadNumber = useCallback(async () => {
    // Spam protection: Debounce rapid clicks (500ms minimum between clicks)
    const now = Date.now();
    if (now - lastSkipTimeRef.current < SKIP_DEBOUNCE_MS) {
      return;
    }
    lastSkipTimeRef.current = now;

    await handleStatusUpdate(LeadStatus.BAD_NUMBER);
    handleEndCall();
  }, [handleStatusUpdate, handleEndCall]);

  const handleEndCallRef = useRef(handleEndCall);
  useEffect(() => {
    handleEndCallRef.current = handleEndCall;
  }, [handleEndCall]);

  // Mark as DNC and end call. The line can drop and the next batch connect while the DNC
  // dialog is open, so the call is only ended if it's still this lead's.
  const handleMarkDNC = useCallback(async () => {
    // Spam protection: Debounce rapid clicks (500ms minimum between clicks)
    const now = Date.now();
    if (now - lastSkipTimeRef.current < SKIP_DEBOUNCE_MS) {
      return;
    }
    lastSkipTimeRef.current = now;

    const leadId = stableConnectedLine?.lead?.id;
    const blockInbound = await confirmDnc();
    if (blockInbound === null) return;
    await handleStatusUpdate(LeadStatus.DO_NOT_CALL, undefined, blockInbound);
    const idx = connectedLineIndexRef.current;
    if (leadId && idx !== null && linesRef.current[idx]?.lead?.id === leadId) handleEndCallRef.current();
  }, [stableConnectedLine?.lead?.id, confirmDnc, handleStatusUpdate]);

  // The agent's own disposition buttons: same as Bad #.
  const handleQuickDisposition = useCallback(async (status: string) => {
    const now = Date.now();
    if (now - lastSkipTimeRef.current < SKIP_DEBOUNCE_MS) {
      return;
    }
    lastSkipTimeRef.current = now;

    await handleStatusUpdate(status);
    handleEndCall();
  }, [handleStatusUpdate, handleEndCall]);

  // Post-call card between batches: file the last lead (no call left to end).
  const fileLastLead = useCallback(async (leadId: string | undefined, status: string) => {
    if (!leadId) return;
    let blockInbound = false;
    if (status === LeadStatus.DO_NOT_CALL) {
      const answer = await confirmDnc();
      if (answer === null) return;
      blockInbound = answer;
    }
    try {
      await updateLeadStatusMutation.mutateAsync({ leadId, status, ...(blockInbound ? { blockInbound } : {}) });
    } catch {}
  }, [confirmDnc, updateLeadStatusMutation]);

  // Skip ALL ringing lines at once
  const handleSkipAllRinging = useCallback(async () => {
    // Spam protection: Debounce rapid clicks (500ms minimum between clicks)
    const now = Date.now();
    if (now - lastSkipTimeRef.current < SKIP_DEBOUNCE_MS || skipCooldownActive) {
      return;
    }
    lastSkipTimeRef.current = now;

    // Visual cooldown feedback
    setSkipCooldownActive(true);
    if (skipCooldownTimer.current) clearTimeout(skipCooldownTimer.current);
    skipCooldownTimer.current = setTimeout(() => setSkipCooldownActive(false), 1500);

    // Use linesRef for fresh state
    const ringingLines = linesRef.current.filter(l => l.status === "dialing" || l.status === "ringing");

    // Stop ringback audio immediately when skipping all
    stopRingback();

    // If no lines are ringing, skip straight to force-advance check below
    if (ringingLines.length === 0) {
      const doneStatuses = ["disconnected", "failed", "no-answer", "voicemail"];
      const allDone = linesRef.current.every(l => doneStatuses.includes(l.status) || l.status === "idle");
      const totalQueued = reQueuedLeadsRef.current.length + leadQueueRef.current.length;
      if (allDone && totalQueued > 0 && !sessionStoppedRef.current && !isPausedRef.current && !isManualCallPauseRequested()) {
        connectedLineIndexRef.current = null;
        setConnectedLineIndex(null);
        isPostCallRef.current = false;
        awaitingMultiLineDispositionRef.current = false;
        setAwaitingMultiLineDisposition(false);
        isDialingNextRef.current = false;
        lastDialTimeRef.current = 0;
        setIsPreparingNextBatch(true);
        linesRef.current = linesRef.current.map(l => ({ ...l, status: "idle" as const, duration: undefined }));
        setLines(prev => prev.map(l => ({ ...l, status: "idle" as const, duration: undefined })));
        const availableIndices = linesRef.current.map((_, i) => i);
        dialNextLeadsRef.current?.(availableIndices);
        setTimeout(() => setIsPreparingNextBatch(false), 3000);
      }
      return;
    }

    // Update all ringing lines to no-answer immediately
    setLines(prev => prev.map(l =>
      l.status === "dialing" || l.status === "ringing"
        ? { ...l, status: "no-answer" as const }
        : l
    ));

    // Cancel all calls on Twilio in parallel — sequential awaits left the last
    // line audibly ringing ~1s longer than the first on a 3-line skip
    await Promise.all(
      ringingLines.map(async (line) => {
        if (!line.callSid || !shouldCancelCall(line.callSid)) return;
        try {
          await api.post({
            url: getApiUrl(`/api/communication/calls/${line.callSid}/cancel`),
            body: { reason: "skipped_all_by_agent" },
            auth: true,
          });
        } catch (error) {
          // Failed to cancel call - may have already ended
        }
      })
    );

    // BUG FIX: After skipping, if ALL lines are now in a done state (including lines that
    // were already disconnected/failed before skip), force-advance to the next batch.
    // The batch completion effect may not fire if it already ran for the previously-ended lines.
    const doneStatuses = ["disconnected", "failed", "no-answer", "voicemail"];
    const allLinesDone = linesRef.current.every(l => doneStatuses.includes(l.status));
    const totalQueuedLeads = reQueuedLeadsRef.current.length + leadQueueRef.current.length;
    if (allLinesDone && totalQueuedLeads > 0 && !sessionStoppedRef.current && !isPausedRef.current && !isManualCallPauseRequested()) {
      // Clear guards that could block dialNextLeads
      connectedLineIndexRef.current = null;
      setConnectedLineIndex(null);
      isPostCallRef.current = false;
      awaitingMultiLineDispositionRef.current = false;
      setAwaitingMultiLineDisposition(false);
      isDialingNextRef.current = false;
      lastDialTimeRef.current = 0;
      setIsPreparingNextBatch(true);
      // Force all lines to idle so dialNextLeads sees them as available
      linesRef.current = linesRef.current.map(l => ({ ...l, status: "idle" as const, duration: undefined }));
      setLines(prev => prev.map(l => ({ ...l, status: "idle" as const, duration: undefined })));
      const availableIndices = linesRef.current.map((_, i) => i);
      dialNextLeadsRef.current?.(availableIndices);
      setTimeout(() => setIsPreparingNextBatch(false), 3000);
    }
  }, [stopRingback]);

  // Skip a line - cancel the Twilio call and update status
  const handleSkipLine = useCallback(async (lineIndex: number) => {
    // Spam protection: Debounce rapid clicks (500ms minimum between clicks)
    const now = Date.now();
    if (now - lastSkipTimeRef.current < SKIP_DEBOUNCE_MS) {
      return;
    }
    lastSkipTimeRef.current = now;

    // Get the line directly from linesRef for fresh state
    const lineToSkip = linesRef.current[lineIndex];

    if (!lineToSkip) {
      return;
    }

    // Update UI immediately
    setLines(prev => prev.map((l, idx) =>
      idx === lineIndex
        ? { ...l, status: "no-answer" as const }
        : l
    ));

    // If the line has a callSid, cancel the call on Twilio
    if (lineToSkip.callSid && shouldCancelCall(lineToSkip.callSid)) {
      try {
        await api.post({
          url: getApiUrl(`/api/communication/calls/${lineToSkip.callSid}/cancel`),
          body: { reason: "skipped_by_agent" },
          auth: true,
        });
      } catch (error: any) {
        // Failed to cancel call - may have already ended
      }
    }
  // IMPORTANT: Do NOT include 'lines' in deps - use linesRef instead
  }, []);

  // Pause session - disconnects active call and all ringing calls
  const handlePause = useCallback(async () => {
    // Spam protection: Debounce rapid clicks
    const now = Date.now();
    if (now - lastSkipTimeRef.current < SKIP_DEBOUNCE_MS) {
      return;
    }
    lastSkipTimeRef.current = now;

    // ============================================================
    // STEP 1: FREEZE EVERYTHING SYNCHRONOUSLY — before any awaits.
    // Everything below this line runs in the same microtask, so no
    // events, effects, or callbacks can fire between these lines.
    // ============================================================
    isPausedRef.current = true;
    setIsPaused(true);
    setIsPreparingNextBatch(false);

    // Stop call timer
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }

    // Stop polling IMMEDIATELY
    if (statusPollRef.current) {
      clearInterval(statusPollRef.current);
      statusPollRef.current = null;
    }

    // Clear multi-line mode IMMEDIATELY so manual calls work
    setIsInMultiLineMode(false);

    // NUCLEAR: Drain lead queues IMMEDIATELY — no leads = no new batches
    pausedLeadQueueBackup.current = {
      leads: [...leadQueueRef.current],
      reQueued: [...reQueuedLeadsRef.current],
    };
    leadQueueRef.current = [];
    reQueuedLeadsRef.current = [];
    setLeadQueue([]);
    setReQueuedLeads([]);

    // Update lines to no-answer IMMEDIATELY
    linesRef.current = linesRef.current.map(l =>
      l.status === "dialing" || l.status === "ringing"
        ? { ...l, status: "no-answer" as const }
        : l
    );
    setLines(prev => prev.map(l =>
      l.status === "dialing" || l.status === "ringing"
        ? { ...l, status: "no-answer" as const }
        : l
    ));

    // Clear connected line state
    connectedLineIndexRef.current = null;
    setConnectedLineIndex(null); setAppleScreeningDetected(false);
    setCallTimer(0); callTimerValueRef.current = 0;

    // Block dialNextLeads even if it's mid-execution
    isDialingNextRef.current = false;

    // ============================================================
    // STEP 2: ASYNC CLEANUP — session is already frozen above,
    // these are fire-and-forget cleanup tasks.
    // ============================================================

    // Pause backend session (fire-and-forget — frontend is already frozen)
    if (sessionId) {
      try {
        await api.post({
          url: getApiUrl(`/api/communication/multi-line-conference/${sessionId}/pause-session`),
          body: { reason: "user_paused" },
          auth: true,
        });
      } catch (error) {
        console.error('Failed to pause backend session:', error);
      }
    }

    // Disconnect agent from conference (agent paused the session).
    if (activeCall) {
      disconnect({ userInitiated: true });
    }

  }, [activeCall, disconnect, sessionId]);

  // AUTO-PAUSE: When a manual call starts from lead detail while multi-line is running,
  // automatically pause the session — same behavior as power dialer.
  // Without this, the user has to manually click Pause before calling from lead detail.
  useEffect(() => {
    if (activeCallSource === 'manual' && isSessionActive && !isPausedRef.current) {
      // CALL-WAITING GUARD: during a Hold & Answer / Merge handoff the
      // new incoming / resumed leg lands here with activeCallSource='manual'
      // because it's a generic conference join (not flagged as multi-line).
      // Without this guard, auto-pause fires → handlePause → disconnect()
      // on the active leg → cascade that kills the handoff. Emergency-pause
      // (dispatched at answerCall start) already froze the session, so
      // auto-pause is redundant here. Skip if we're mid call-waiting.
      let inCallWaitingTransition = false;
      try { inCallWaitingTransition = sessionStorage.getItem('__callwaiting_active') === '1'; } catch {}
      if (inCallWaitingTransition) {
        return;
      }

      // Auto-pause triggered by manual call detection
      // Use a microtask to avoid calling handlePause during render
      Promise.resolve().then(() => {
        if (!isPausedRef.current) {
          handlePause();
        }
      });
    }
  }, [activeCallSource, isSessionActive, handlePause]);

  // Resume session - calls backend to set ACTIVE, which triggers next batch server-side
  const handleResume = useCallback(async () => {
    // Spam protection: Debounce rapid clicks
    const now = Date.now();
    if (now - lastSkipTimeRef.current < SKIP_DEBOUNCE_MS) {
      return;
    }
    lastSkipTimeRef.current = now;

    // Check if session was stopped BY USER (handleStopSession). If sessionStoppedRef was
    // set by a stale poll during pause, reset it — the user explicitly wants to resume.
    const totalQueuedOnResume = reQueuedLeadsRef.current.length + leadQueueRef.current.length;
    if (sessionStoppedRef.current && totalQueuedOnResume > 0) {
      // Stale flag from batch completion during pause — reset it
      sessionStoppedRef.current = false;
    } else if (sessionStoppedRef.current) {
      return; // Genuinely stopped with no leads left
    }
    // Resuming is an explicit human action — clear the user-stop latch.
    userStoppedRef.current = false;
    // Same stuck-flag class as '__callwaiting_active': if the session was
    // auto-paused by a manual call (activeCallSource effect), the
    // '__multiline_manual_pause' flag survives into Resume — the fallback
    // dialNextLeads then immediately re-pauses ("Resume needs two clicks").
    // Clear it here like startMultiLineSession does.
    clearManualCallPauseRequest();
    // And the call-waiting flag itself: the dialNextLeads gate (~937) is a
    // BARE return — it never consumes the flag. A mid-session leak therefore
    // wedged the CURRENT session until a full restart. Pressing Resume is the
    // human saying "keep dialing" — any lingering call-waiting flag is stale
    // by definition at that moment, so clear it and let dialing proceed.
    try { sessionStorage.removeItem('__callwaiting_active'); } catch { /* noop */ }

    // Update ref FIRST (synchronous) so local checks see updated value immediately
    isPausedRef.current = false;
    setIsPaused(false);

    // Restore multi-line mode (was cleared during pause to allow manual calls)
    setIsInMultiLineMode(true);

    // NUCLEAR PAUSE FIX: Restore lead queues that were drained during pause
    if (pausedLeadQueueBackup.current) {
      leadQueueRef.current = pausedLeadQueueBackup.current.leads;
      reQueuedLeadsRef.current = pausedLeadQueueBackup.current.reQueued;
      setLeadQueue(pausedLeadQueueBackup.current.leads);
      setReQueuedLeads(pausedLeadQueueBackup.current.reQueued);
      pausedLeadQueueBackup.current = null;
    }

    // Resume the backend session — this sets status back to ACTIVE and triggers batch progression
    if (sessionId) {
      try {
        await api.post({
          url: getApiUrl(`/api/communication/multi-line-conference/${sessionId}/resume-session`),
          body: {},
          auth: true,
        });
      } catch (error) {
        console.error('Failed to resume backend session:', error);
      }
    }

    // Restart status polling (was stopped during pause)
    if (sessionId && startStatusPollingRef.current) {
      startStatusPollingRef.current(sessionId);
    }

    // Also trigger local dial as fallback
    setTimeout(() => {
      if (sessionStoppedRef.current || isPausedRef.current) return;

      const totalQueuedLeads = reQueuedLeads.length + leadQueue.length;
      if (totalQueuedLeads > 0) {
        const doneStatuses = ["disconnected", "failed", "no-answer", "voicemail", "idle"];
        const availableLines = linesRef.current
          .map((line, index) => ({ line, index }))
          .filter(({ line }) => doneStatuses.includes(line.status))
          .map(({ index }) => index);

        if (availableLines.length > 0) {
          dialNextLeads(availableLines);
        }
      }
    }, 500);
  }, [reQueuedLeads.length, leadQueue.length, dialNextLeads, sessionId]);

  // Stop entire session - disconnects all calls and ends session
  const handleStopSession = useCallback(async () => {
    // Spam protection: Debounce rapid clicks
    const now = Date.now();
    if (now - lastSkipTimeRef.current < SKIP_DEBOUNCE_MS) {
      return;
    }
    lastSkipTimeRef.current = now;
    // The human pressed Stop — final; dialNextLeads must never resurrect it.
    userStoppedRef.current = true;

    // CRITICAL: Mark session as stopped FIRST to prevent any pending timers from dialing
    sessionStoppedRef.current = true;
    isPausedRef.current = true;
    setIsPaused(true);
    setIsPreparingNextBatch(false);

    // Stop ringback audio immediately when stopping session
    stopRingback();

    // Clear polling
    if (statusPollRef.current) {
      clearInterval(statusPollRef.current);
      statusPollRef.current = null;
    }

    // Clear timer
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }

    // Clear batch completion timer to prevent it from firing after stop
    if (batchCompletionTimerRef.current) {
      clearTimeout(batchCompletionTimerRef.current);
      batchCompletionTimerRef.current = null;
    }

    // CRITICAL: End the backend session FIRST — this marks the session as COMPLETED
    // and prevents handleRaceAnswerCallEnd from redialing when call-status webhooks fire.
    // Must happen BEFORE disconnect() because disconnect() kills the conference, which
    // triggers call-status webhooks. If session isn't COMPLETED yet, those webhooks
    // would trigger new batches (redials).
    if (sessionId) {
      try {
        await api.post({
          url: getApiUrl(`/api/communication/multi-line-conference/${sessionId}/end-session`),
          body: { reason: "user_stopped" },
          auth: true,
        });
      } catch (error) {
        // Failed to end backend session - session may have already ended
      }
    }

    // Disconnect agent from conference AFTER backend session is ended
    // (agent explicitly stopped the multi-line session).
    if (activeCall) {
      disconnect({ userInitiated: true });
    }

    // Clear connected line state
    connectedLineIndexRef.current = null;
    setConnectedLineIndex(null); setAppleScreeningDetected(false);
    setCallTimer(0); callTimerValueRef.current = 0;

    // Also cancel any ringing/dialing calls individually (belt and suspenders)
    const ringingLines = linesRef.current.filter(l =>
      (l.status === "dialing" || l.status === "ringing") && l.callSid
    );

    if (ringingLines.length > 0) {
      // Cancel all ringing calls in parallel
      await Promise.allSettled(
        ringingLines.map(async (line) => {
          if (!line.callSid || !shouldCancelCall(line.callSid)) return;
          try {
            await api.post({
              url: getApiUrl(`/api/communication/calls/${line.callSid}/cancel`),
              body: { reason: "session_stopped" },
              auth: true,
            });
          } catch (error) {
            // Failed to cancel call - may have already ended
          }
        })
      );
    }

    // Calculate session stats
    // Use totalAnsweredCountRef (tracks across ALL batches) instead of counting current lines
    // (which only shows the last batch's 4 lines, not all leads answered in the session)
    const totalDialTime = getDialSeconds();

    // Calculate outcome stats from lead statuses
    const statuses = Object.values(leadStatusesRef.current);
    const appointments = statuses.filter(s => s === 'Appt').length;
    const underwriting = statuses.filter(s => s === 'Underwriting').length;
    const sold = statuses.filter(s => s === 'Sold & Approved').length;

    setIsSessionActive(false);
    setIsInMultiLineMode(false); // Allow incoming calls again

    // Persist completion to the PRIMARY session for session history (fire-and-forget)
    const stopTargetId = primarySessionIdRef.current || sessionId;
    if (stopTargetId) {
      dialerSessionApi
        .completeSession(stopTargetId, {
          status: 'stopped',
          currentLeadIndex: processedLeadIdsRef.current.size + resumeOffset,
          completedCalls: processedLeadIdsRef.current.size + resumeOffset,
          totalAnswered: totalAnsweredCountRef.current,
          totalFailed: processedLeadIdsRef.current.size - totalAnsweredCountRef.current,
          totalDialTime,
          outcome: { appointments, underwriting, sold },
        })
        .catch(() => {}); // Silently ignore
    }

    // Clear persisted session stats since session is complete
    if (typeof window !== 'undefined') {
      localStorage.removeItem(MULTILINE_SESSION_STATS_KEY);
    }

    // Call onComplete with wasStopped flag — shows "Session Stopped" UI instead of "Session Complete"
    onComplete({
      answered: totalAnsweredCountRef.current,
      called: processedLeadIdsRef.current.size + resumeOffset,
      totalDialTime,
      talkSeconds: sessionTalkSecondsRef.current,
      conversationSeconds: sessionConversationSecondsRef.current,
      appointments,
      underwriting,
      sold,
      wasStopped: true,
    });
  }, [activeCall, disconnect, stopRingback, onComplete, sessionId, resumeOffset]);

  // Keep handleStopSession ref in sync for use in dialNextLeads (defined before handleStopSession)
  handleStopSessionRef.current = handleStopSession;

  // External force-end listener. TwilioDeviceContext.connect() fires this when
  // the agent initiates a MANUAL outbound call (from lead detail) while a
  // multi-line session is still alive. Unlike emergency-pause which preserves
  // the session for later resume, force-end fully tears down — user has
  // clearly moved on from the dialer. Without this the paused-session widget
  // ghosts over manual calls and looks like the manual call became a dialer
  // session, which was confusing.
  useEffect(() => {
    const onForceEnd = () => {
      if (isSessionActive || sessionId) {
        handleStopSession();
      }
    };
    window.addEventListener('multiline:force-end', onForceEnd);
    return () => window.removeEventListener('multiline:force-end', onForceEnd);
  }, [handleStopSession, isSessionActive, sessionId]);

  // External emergency-pause listener. IncomingCallContext fires this when an
  // incoming call arrives mid-session and the agent clicks Hold & Answer /
  // Merge on the call-waiting widget. Unlike the normal Pause button, this
  // MUST NOT disconnect the agent's active call — the hold/merge flow needs
  // that call alive to put the connected customer on hold music or redirect
  // them into the new conference.
  //
  // Behavior: freeze auto-dialing, cancel non-connected ringing lines only,
  // leave the connected winner's call + agent SDK alive.
  //
  // `emergencyPausedByUsRef` tracks whether THIS listener caused the pause,
  // so `emergency-resume` only unpauses when the call-waiting flow ends —
  // never when the user had already paused manually.
  const emergencyPausedByUsRef = useRef(false);
  useEffect(() => {
    const onEmergencyPause = (event: Event) => {
      // Capture incoming caller info from the CustomEvent detail (sent by
      // IncomingCallContext when the agent answers an inbound call mid-
      // session). Used by the post-call disposition view to show "you just
      // talked to <name>" instead of the literal fallback "Lead". Captured
      // BEFORE the early-return so re-dispatches still update if needed.
      const detail = (event as CustomEvent)?.detail as
        | { incomingCallerName?: string; incomingCallerNumber?: string; incomingLeadId?: string; reason?: string }
        | undefined;
      if (detail?.incomingCallerName || detail?.incomingCallerNumber) {
        setIncomingPauseInfo({
          name: detail.incomingCallerName,
          number: detail.incomingCallerNumber,
          leadId: detail.incomingLeadId,
        });
      }

      if (isPausedRef.current || !isSessionActive) {
        // Already paused — could be our own prior emergency-pause
        // (resumeHeldCall re-dispatches as belt-and-suspenders) OR a
        // user-initiated manual pause. Either way DO NOT touch
        // `emergencyPausedByUsRef`. Overwriting it to false here
        // broke the Hold & Answer → resume → end → advance flow:
        // the re-dispatch flipped our tracking to false, so the
        // later emergency-resume skipped and the session stayed paused.
        return;
      }
      emergencyPausedByUsRef.current = true;

      // Synchronously freeze dialer state so dialNextLeads aborts.
      isPausedRef.current = true;
      setIsPaused(true);
      setIsPreparingNextBatch(false);
      // DELIBERATELY NOT calling setIsInMultiLineMode(false) here. Prior
      // behavior flipped the multi-line UI off mid-pause → user saw the
      // view flash back to the 4-line grid, then to paused state, then
      // back again on resume. Since the winner's call is still alive
      // (held on music, being handled via the call-waiting widget),
      // semantically the session is still multi-line — just paused. The
      // SDK incoming-reject at TwilioDeviceContext.tsx:1086 also checks
      // `currentActiveCall` directly, so we don't need to flip this ref
      // to allow the new incoming through.
      isDialingNextRef.current = false;

      // After Hold & Answer completes, the user will resume the held
      // winner call and eventually end it. When that end-call fires, the
      // batch-completion effect sees all lines terminal (losers canceled
      // + winner just disconnected) and normally auto-dials the next 4.
      // That cascade feels jarring to the agent (Mark reported "ends all
      // calls at once and starts resuming dial"). Treat the Hold & Answer
      // interruption like a 60s-disposition pause — flag
      // awaitingMultiLineDisposition so the batch-completion effect
      // returns early and the agent can click "Resume Multi-Line" when
      // ready. Matches the power-dialer auto-pause-for-disposition
      // pattern.
      awaitingMultiLineDispositionRef.current = true;
      setAwaitingMultiLineDisposition(true);

      // Stop pollers so they don't schedule new batches.
      if (statusPollRef.current) {
        clearInterval(statusPollRef.current);
        statusPollRef.current = null;
      }

      // RE-QUEUE IN-FLIGHT LEADS:
      // The leads currently attached to ringing/dialing lines never had a
      // real chance to be talked to — the system canceled them mid-ring
      // when the agent answered the incoming. Without this re-queue, those
      // leads are skipped entirely (lines get marked no-answer, queue
      // backup only contains UNDIALED leads, so Resume jumps straight to
      // the next batch). Capture them here so Resume re-dials them first.
      //
      // Skip the connected line if any (Hold/Merge case — that lead is
      // actively being talked to, don't re-dial). Also dedup against the
      // existing queue to avoid doubling up if a lead was somehow already
      // re-queued.
      const inFlightLeads = linesRef.current
        .filter(l => (l.status === "dialing" || l.status === "ringing") && l.lead)
        .map(l => l.lead!);
      const existingIds = new Set([
        ...reQueuedLeadsRef.current.map(l => l.id),
        ...leadQueueRef.current.map(l => l.id),
      ]);
      const newReQueued = inFlightLeads.filter(l => !existingIds.has(l.id));

      // Drain queues — same rationale as normal pause: no leads = no new
      // batches. Backup so Resume can restore.
      //
      // NOTE: we deliberately do NOT back up line statuses. Earlier attempt
      // restored them on resume, but that was wrong — the loser legs are
      // actually dead on Twilio's side (race winner already canceled them),
      // so re-marking them as "ringing" would desync frontend from the
      // live call state. Losers staying as "no-answer" is correct. The
      // cascade issue the line-backup was trying to avoid is instead
      // handled by the `awaitingMultiLineDisposition` flag above, which
      // blocks the batch-completion effect.
      //
      // In-flight leads pushed to FRONT of reQueued backup so Resume picks
      // them up first (before pulling new leads from the main queue).
      pausedLeadQueueBackup.current = {
        leads: [...leadQueueRef.current],
        reQueued: [...newReQueued, ...reQueuedLeadsRef.current],
      };
      leadQueueRef.current = [];
      reQueuedLeadsRef.current = [];
      setLeadQueue([]);
      setReQueuedLeads([]);

      // Mark ringing/dialing lines (NOT connected) as no-answer so they stop.
      // The connected line is the one we need to keep alive for hold/merge.
      linesRef.current = linesRef.current.map(l =>
        l.status === "dialing" || l.status === "ringing"
          ? { ...l, status: "no-answer" as const }
          : l
      );
      setLines(prev => prev.map(l =>
        l.status === "dialing" || l.status === "ringing"
          ? { ...l, status: "no-answer" as const }
          : l
      ));

      // Deliberately skip:
      //   - disconnect() on agent SDK  — hold/merge needs it alive
      //   - clearing connectedLineIndex — the connected line stays
      //
      // BACKEND PAUSE: previously skipped on the assumption "hold/merge
      // lifecycle owns it." But for a plain "Answer" click on an incoming
      // there IS no hold/merge lifecycle — backend session stayed ACTIVE
      // and handleRaceAnswerCallEnd auto-progressed batches in the
      // background, racing the incoming-call bridge. Mark's symptom: 4-5s
      // "caller hung up" toasts because the agent's SDK got yanked into
      // a new master conference mid-handoff.
      //
      // Fire-and-forget — frontend is already frozen. pauseSession only
      // cancels ringing/initiated calls (preserving any in-progress
      // connected line, which is what hold/merge cares about), so this
      // is also safe for the Hold & Answer path.
      const sid = sessionIdRef.current;
      if (sid) {
        api.post({
          url: getApiUrl(`/api/communication/multi-line-conference/${sid}/pause-session`),
          body: { reason: detail?.reason === 'transfer' ? 'transfer' : "incoming_call" },
          auth: true,
        }).catch((err) => {
          console.warn('[EmergencyPause] Failed to pause backend session:', err);
        });
      }

      // TRANSFER-OUT (2026-07-23): unlike Hold & Answer, the transferred call
      // is GONE from the agent's perspective — their device leg dropped and
      // the customer talks to the owner now. Keeping the connected card up
      // ("Answered 1:19" with Transfer/End buttons) made the agent think the
      // call never left. Clear the connected line locally; the customer's
      // conference continues untouched, and the lead is deliberately NOT
      // re-queued (it's being handled by the owner).
      if (detail?.reason === 'transfer') {
        linesRef.current = linesRef.current.map(l =>
          l.status === "connected"
            ? { ...l, status: "disconnected" as const }
            : l
        );
        setLines(prev => prev.map(l =>
          l.status === "connected"
            ? { ...l, status: "disconnected" as const }
            : l
        ));
        connectedLineIndexRef.current = null;
        setConnectedLineIndex(null);
      }
    };
    const onEmergencyResume = () => {
      // Only undo if WE paused. If user had paused manually, respect that.
      if (!emergencyPausedByUsRef.current) return;
      emergencyPausedByUsRef.current = false;

      // Restore the dialer back to the state it was in before we froze it.
      // Lead queues need to be restored BEFORE clearing isPaused so the
      // auto-dial effect sees a valid queue when it unblocks.
      if (pausedLeadQueueBackup.current) {
        const backup = pausedLeadQueueBackup.current;
        leadQueueRef.current = [...backup.leads];
        reQueuedLeadsRef.current = [...backup.reQueued];
        setLeadQueue(backup.leads);
        setReQueuedLeads(backup.reQueued);

        // Line statuses are intentionally NOT restored — losers are dead
        // on Twilio's side and the `awaitingMultiLineDisposition` flag
        // (set during emergency-pause) handles preventing the auto-dial
        // cascade. On user-initiated Resume Multi-Line click, that flag
        // is cleared and normal batch progression resumes.

        pausedLeadQueueBackup.current = null;
      }

      isPausedRef.current = false;
      setIsPaused(false);
      // isInMultiLineMode was never flipped to false in emergency-pause
      // (fix above), so no need to restore it here — it's still true.
      // Note: statusPollRef and isDialingNextRef are re-established by the
      // session's normal effects when isPaused flips back to false.
    };
    window.addEventListener('multiline:emergency-pause', onEmergencyPause);
    window.addEventListener('multiline:emergency-resume', onEmergencyResume);
    return () => {
      window.removeEventListener('multiline:emergency-pause', onEmergencyPause);
      window.removeEventListener('multiline:emergency-resume', onEmergencyResume);
    };
  }, [isSessionActive]);

  // Start session on mount
  useEffect(() => {
    if (isDeviceReady && !hasStarted.current) {
      startMultiLineSession();
    }
  }, [isDeviceReady, startMultiLineSession]);

  // Check for batch completion and trigger next batch from queue
  // When all lines in current batch are done, dial next batch if queue has leads
  useEffect(() => {
    // Find lines that are "done" (disconnected, failed, no-answer, voicemail)
    const doneStatuses = ["disconnected", "failed", "no-answer", "voicemail"];
    // Lines that are still active/pending - NOT done
    const activeStatuses = ["dialing", "ringing", "connecting", "connected", "on-hold"];

    const doneLines = lines.filter(l => doneStatuses.includes(l.status));
    const activeLines = lines.filter(l => activeStatuses.includes(l.status));
    const allDone = doneLines.length === lines.length && activeLines.length === 0;

    // If not all lines are done, or there's an active connected call, or paused, or session stopped - do nothing
    // CRITICAL: Check BOTH connectedLineIndex state AND connectedLineIndexRef.current
    // The ref is updated synchronously when customer_answered is received, but state update
    // may not have propagated yet when this effect runs due to the lines state change
    //
    // GUARD AGAINST STUCK STATE: If connectedLineIndex is set but the line's
    // status is terminal (the watchdog will clear it within 2s, but in the
    // meantime don't block batch progression). Treat that case as
    // "no active connection" so we can advance.
    const TERMINAL = ['disconnected','voicemail','no-answer','failed','canceled','completed'];
    const connectedLineStatus = connectedLineIndex !== null
      ? linesRef.current[connectedLineIndex]?.status : null;
    const reallyConnected = connectedLineIndex !== null && !TERMINAL.includes(connectedLineStatus as any);
    const refReallyConnected = connectedLineIndexRef.current !== null && !TERMINAL.includes(
      linesRef.current[connectedLineIndexRef.current]?.status as any,
    );

    if (!allDone || reallyConnected || refReallyConnected || isPaused || !hasStarted.current || sessionStoppedRef.current || awaitingMultiLineDispositionRef.current || isManualCallPauseRequested()) {
      // Clear any pending completion timer if conditions changed
      if (batchCompletionTimerRef.current) {
        clearTimeout(batchCompletionTimerRef.current);
        batchCompletionTimerRef.current = null;
      }
      return;
    }

    // Check if no one in this batch was connected (all failed/voicemail/no-answer)
    // "disconnected" status means the call was answered then ended - that's a successful connection
    const failedStatuses = ["failed", "no-answer", "voicemail"];
    const allFailed = lines.every(l => failedStatuses.includes(l.status));

    if (allFailed) {
      // All lines failed without anyone answering - stop the ringback
      // This prevents the agent from hearing ringing after all calls went to voicemail
      stopRingback();
    }

    // Count terminal leads that won't be re-queued as processed
    // This handles no-answer/voicemail/failed leads that have exhausted attempts
    const terminalStatuses = ["no-answer", "voicemail", "failed"];
    lines.forEach(line => {
      if (line.lead?.id && terminalStatuses.includes(line.status) && !processedLeadIdsRef.current.has(line.lead.id)) {
        // Only count as processed if this lead is NOT in the re-queue
        const isReQueued = reQueuedLeadsRef.current.some(l => l.id === line.lead.id);
        if (!isReQueued) {
          processedLeadIdsRef.current.add(line.lead.id);
        }
      }
    });
    // Also count disconnected leads (answered then ended) that may not have been tracked yet
    lines.forEach(line => {
      if (line.lead?.id && line.status === "disconnected" && !processedLeadIdsRef.current.has(line.lead.id)) {
        processedLeadIdsRef.current.add(line.lead.id);
      }
    });
    setProcessedCount(processedLeadIdsRef.current.size);

    // Check if there are more leads in queue (re-queued or main queue)
    // CRITICAL: Check BOTH state AND refs - refs are updated immediately in handlers
    // while state updates may be batched and not yet visible in this effect
    const totalQueuedLeads = Math.max(
      reQueuedLeads.length + leadQueue.length,
      reQueuedLeadsRef.current.length + leadQueueRef.current.length
    );

    if (totalQueuedLeads > 0) {
      // AUTO-PAUSE FOR DISPOSITION: If a line had a connected call over 60 seconds,
      // pause before dialing next batch so agent can take notes / disposition the lead.
      // Check BOTH lines state AND linesRef (ref is updated synchronously by handleEndCall,
      // state may be batched).
      const hadLongConnectedCall = lines.some(
        line => line.status === "disconnected" && line.duration && line.duration >= 60
      ) || linesRef.current.some(
        line => line.status === "disconnected" && line.duration && line.duration >= 60
      );

      if (hadLongConnectedCall && !awaitingMultiLineDispositionRef.current) {
        awaitingMultiLineDispositionRef.current = true;
        setAwaitingMultiLineDisposition(true);
        return; // Don't dial next batch — wait for agent to click "Resume Multi-Line"
      }

      // Show "preparing next batch" so agent knows it's not stuck
      setIsPreparingNextBatch(true);

      // There are more leads to call - get available line indices
      const availableLines = lines
        .map((line, index) => ({ line, index }))
        .filter(({ line }) => doneStatuses.includes(line.status))
        .map(({ index }) => index);

      // Dial next batch. dialNextLeads has many early-return guards (paused, session stopped,
      // queue empty, etc.) — if any fire, the success path that clears isPreparingNextBatch
      // never runs and the UI gets stuck on "Dialing..." forever. 5s safety timeout flips
      // the button back to "Resume Multi-Line" so the agent can click it manually.
      //
      // A PROMOTION CAN STILL LAND (2026-10-09). When the first answer was a voicemail, the
      // server connects the held second person and sends customer_answered (isPromotion) a
      // few hundred ms after the voicemail line ended. Switching batches first ended that
      // person with the old conference (session 65f77676: promoted 40.66, end-session 41.08,
      // gone 42.22). Same 500 ms as the completion path below; armed once, so polls can't
      // keep pushing it back, and the guard at the top of this effect clears it if a line
      // connects meanwhile. This effect re-runs on every render between batches, so the
      // 5 s indicator reset is armed here once too (a late one cleared the next batch's).
      if (!batchCompletionTimerRef.current) {
        batchCompletionTimerRef.current = setTimeout(() => {
          batchCompletionTimerRef.current = null;
          if (connectedLineIndexRef.current !== null || isPausedRef.current
            || sessionStoppedRef.current || isManualCallPauseRequested()) {
            return;
          }
          dialNextLeads(availableLines);
        }, 500);
        setTimeout(() => setIsPreparingNextBatch(false), 5000);
      }
    } else {
      // CRITICAL: Add a small debounce before completing session
      // This prevents premature completion due to race conditions where customer_disconnected
      // events from cancelled calls arrive before customer_answered due to network latency.
      // The 500ms delay gives time for the customer_answered event to arrive and set
      // connectedLineIndexRef.current, which would then prevent this completion.
      if (batchCompletionTimerRef.current) {
        clearTimeout(batchCompletionTimerRef.current);
      }

      batchCompletionTimerRef.current = setTimeout(() => {
        // Re-check conditions after delay - a customer_answered may have arrived
        if (connectedLineIndexRef.current !== null) {
          return;
        }

        // Session paused during the 500ms delay (e.g., agent clicked pause right as batch ended)
        if (isPausedRef.current || isManualCallPauseRequested()) {
          return;
        }

        // If session was already stopped by user, don't send 'completed' (would overwrite 'stopped')
        if (sessionStoppedRef.current) {
          return;
        }

        // CRITICAL: Re-check queue using refs (not stale closure state)
        // If leads were added to queue during the delay, don't complete
        const currentQueuedLeads = reQueuedLeadsRef.current.length + leadQueueRef.current.length;
        if (currentQueuedLeads > 0) {
          return;
        }

        // All leads processed - session complete
        // CRITICAL: Mark session as stopped FIRST to prevent any pending timers from dialing
        sessionStoppedRef.current = true;

        // Stop ringback audio when session completes
        stopRingback();

        if (statusPollRef.current) {
          clearInterval(statusPollRef.current);
          statusPollRef.current = null;
        }

        // Calculate session stats
        const totalDialTime = getDialSeconds();

        // Calculate outcome stats from lead statuses
        const statuses = Object.values(leadStatusesRef.current);
        const appointments = statuses.filter(s => s === 'Appt').length;
        const underwriting = statuses.filter(s => s === 'Underwriting').length;
        const sold = statuses.filter(s => s === 'Sold & Approved').length;

        setIsSessionActive(false);
        setIsInMultiLineMode(false); // Allow incoming calls again

        // Persist completion to the PRIMARY session for session history (fire-and-forget)
        const completeTargetId = primarySessionIdRef.current || sessionIdRef.current;
        if (completeTargetId) {
          dialerSessionApi
            .completeSession(completeTargetId, {
              status: 'completed',
              currentLeadIndex: processedLeadIdsRef.current.size + resumeOffset,
              completedCalls: processedLeadIdsRef.current.size + resumeOffset,
              totalAnswered: totalAnsweredCountRef.current,
              totalFailed: processedLeadIdsRef.current.size - totalAnsweredCountRef.current,
              totalDialTime,
              outcome: { appointments, underwriting, sold },
            })
            .catch(() => {}); // Silently ignore
        }

        // Clear persisted session stats since session is complete
        if (typeof window !== 'undefined') {
          localStorage.removeItem(MULTILINE_SESSION_STATS_KEY);
        }

        onComplete({
          // Use ref for current value instead of stale closure
          answered: totalAnsweredCountRef.current,
          called: processedLeadIdsRef.current.size + resumeOffset,
          totalDialTime,
          talkSeconds: sessionTalkSecondsRef.current,
          conversationSeconds: sessionConversationSecondsRef.current,
          appointments,
          underwriting,
          sold,
        });
      }, 500); // 500ms debounce to allow customer_answered events to arrive
    }

    // Cleanup function to clear timer if dependencies change
    return () => {
      if (batchCompletionTimerRef.current) {
        clearTimeout(batchCompletionTimerRef.current);
        batchCompletionTimerRef.current = null;
      }
    };
  }, [lines, connectedLineIndex, isPaused, reQueuedLeads, leadQueue, totalLeads, totalAnsweredCount, dialNextLeads, stopRingback, onComplete]);

  // STALE CALL DETECTION: If a call has been in 'ringing' or 'dialing' for >45 seconds,
  // automatically mark it as 'failed' in local state so the UI doesn't freeze.
  // This handles cases where Twilio drops the final webhook and the backend stuck-call
  // timeout (60s) hasn't fired yet or its broadcast was missed.
  useEffect(() => {
    const STALE_CALL_TIMEOUT_MS = 45_000;
    const interval = setInterval(() => {
      const now = Date.now();
      let hasStale = false;
      const updated = linesRef.current.map(line => {
        if (
          (line.status === 'ringing' || line.status === 'dialing') &&
          line.startTime &&
          now - new Date(line.startTime).getTime() > STALE_CALL_TIMEOUT_MS
        ) {
          hasStale = true;
          return { ...line, status: 'failed' as const, error: 'Call timed out' };
        }
        return line;
      });
      if (hasStale) {
        linesRef.current = updated;
        setLines(updated);
      }
    }, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, []);

  // Cleanup on unmount — disconnect Twilio call and clear all timers
  // CRITICAL: Use disconnectRef (not disconnect) to avoid dependency on the unstable function identity.
  // disconnect is a plain arrow function in TwilioDeviceContext (not useCallback-memoized), so it gets
  // a new identity on EVERY render. If we put [disconnect] in deps, this cleanup runs on every render,
  // calling the previous disconnect closure — which captures the CURRENT activeCall and kills it.
  // This was the root cause of the agent's Device call being terminated immediately after connect().
  useEffect(() => {
    return () => {
      if (statusPollRef.current) {
        clearInterval(statusPollRef.current);
      }
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
      if (batchCompletionTimerRef.current) {
        clearTimeout(batchCompletionTimerRef.current);
      }
      // Disconnect Twilio call on unmount to prevent orphaned conference connections
      // Use ref to get the latest disconnect function without adding it as a dependency
      try { if (disconnectRef.current) disconnectRef.current(); } catch (e) { /* may already be disconnected */ }
      // Clear multi-line mode so incoming calls are no longer suppressed
      setIsInMultiLineMode(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps — only run cleanup on actual component unmount

  // Get singleton Supabase client (created at module level to avoid multiple GoTrueClient instances)
  const supabaseClient = getSupabaseClient();

  // Keep sessionIdRef in sync with sessionId state (for Supabase handler stale closure prevention)
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Keep disconnectRef in sync with disconnect (for polling interval stale closure prevention)
  useEffect(() => {
    disconnectRef.current = disconnect;
  }, [disconnect]);

  // Keep linesRef updated whenever lines change (linesRef is declared at top of component)
  useEffect(() => {
    linesRef.current = lines;
  }, [lines]);

  // Subscribe to Supabase Realtime for customer_answered and customer_disconnected events
  // This provides real-time updates without relying on Twilio webhooks reaching localhost
  useEffect(() => {
    if (!user?.user?.id || !isSessionActive || !supabaseClient) {
      return;
    }

    // Use unique channel name with :multiline suffix to avoid conflicts with IncomingCallContext
    // which also subscribes to incoming_calls:{userId}. Duplicate subscriptions cause channel to close.
    const channelName = `incoming_calls:${user.user.id}:multiline`;

    const channel = supabaseClient.channel(channelName);

    // customer_answered is broadcast by AMD webhook when HUMAN or UNKNOWN is detected
    // AMD runs with 'Enable' mode for FAST detection (~1-2s after pickup)
    // If voicemail, AMD broadcasts customer_disconnected instead (no UI shown)
    channel.on('broadcast', { event: 'customer_answered' }, (payload: any) => {
      // Check if this is a multi-line event
      if (payload?.payload?.isMultiLine) {
        // Reject stale events from old sessions (e.g., late AMD confirmation from previous batch)
        const eventSessionId = payload?.payload?.sessionId;
        if (eventSessionId && eventSessionId !== sessionIdRef.current) {
          return;
        }

        const lineNumber = payload?.payload?.lineNumber;
        const leadName = payload?.payload?.leadName;
        const leadId = payload?.payload?.leadId;
        const customerCallSid = payload?.payload?.customerCallSid;

        // Find the line index using leadId (most reliable) or fallback to lineNumber
        let foundLineIndex = -1;
        if (leadId) {
          foundLineIndex = linesRef.current.findIndex(line => line.lead.id === leadId);
        }
        if (foundLineIndex === -1 && lineNumber) {
          foundLineIndex = linesRef.current.findIndex(line => line.lineNumber === lineNumber);
        }

        if (foundLineIndex === -1) {
          return;
        }

        // CRITICAL: Validate the found line's lead ID matches the event's lead ID
        // This prevents showing the wrong lead when batch rotation causes line numbers to shift
        if (leadId && linesRef.current[foundLineIndex]?.lead?.id !== leadId) {
          console.warn('[MultiLine] Lead ID mismatch - rejecting stale event', {
            eventLeadId: leadId,
            foundLeadId: linesRef.current[foundLineIndex]?.lead?.id,
            lineNumber,
          });
          return;
        }

        // Block if session was stopped — prevents late customer_answered from showing a phantom call
        if (sessionStoppedRef.current) {
          return;
        }

        // Block if session is paused — agent may have navigated away to manually dial a lead.
        // Processing this event while paused would update UI state and could cause audio conflicts
        // if the agent's Device is now connected to a different (manual) call.
        if (isPausedRef.current) {
          return;
        }

        // Only update if not already connected — FIRST answerer wins, reject subsequent answers
        // This prevents a second call answering from swapping the CRM lead mid-conversation.
        //
        // EXCEPTION: a PROMOTION (2026-07-25). When the race winner turns out to be a
        // voicemail, the server hangs it up and promotes a confirmed-human loser into
        // the winner slot. That arrives as a second customer_answered, so this guard
        // used to swallow it — the human was live on the call while the UI still showed
        // the voicemail line, then nothing at all.
        //
        // Observed on dev (session dc84a46a): Caius was detected human, promoted, and
        // talked for 51 seconds while never appearing in the active-call UI. The agent
        // hit End because the interface looked stuck.
        //
        // `isPromotion` is set ONLY by the server's promotion paths, so the original
        // protection against a genuine second answerer is unchanged — that case still
        // carries no flag and is still rejected.
        const isPromotion = payload?.payload?.isPromotion === true;
        if (!isPromotion && (connectedLineIndexRef.current !== null || isPostCallRef.current)) {
          console.warn('[MultiLine] Rejecting customer_answered - already connected to line', connectedLineIndexRef.current);
          return;
        }
        if (isPromotion) {
          console.info('[MultiLine] Accepting customer_answered as PROMOTION — switching active line', {
            from: connectedLineIndexRef.current, to: foundLineIndex, wasPostCall: isPostCallRef.current,
          });
          // The promoted line is a live human, so leave post-call state: the old winner
          // was torn down server-side and this call is genuinely in progress.
          isPostCallRef.current = false;
        }

        // CRITICAL: Update connectedLineIndexRef BEFORE setLines
        // This ensures the batch completion effect (which checks this ref) sees the connected state
        // before it evaluates whether all lines are "done"
        connectedLineIndexRef.current = foundLineIndex;

        // Stop ringback IMMEDIATELY — customer answered, conversation is starting.
        // Previously only the polling fallback (line ~1541) called stopRingback, which
        // fired 2-5s after the Supabase event. That delay caused Mark's "ringer crept
        // into a conversation" bug — ringback audio was bleeding into the live call.
        stopRingback();

        // Show Apple Screening indicator if backend flagged this as possible Apple screening
        // (machine_end_silence/other detected — could be Apple or VM without beep)
        setAppleScreeningDetected(!!payload?.payload?.appleScreeningPossible);

        // Set the active conference so addParticipantToCall knows which conference to use
        // The conference name is race-{sessionId}
        // Use sessionIdRef to avoid stale closure (sessionId may have changed since effect mounted)
        if (sessionIdRef.current) {
          setActiveConference(`race-${sessionIdRef.current}`);
        }

        // Update callMetadata so the minimized widget shows the WINNING lead, not the
        // first-dialed placeholder. Without this, expanded view shows caius (correct via
        // connectedLineIndex) while minimized still shows "Walmart" (stale callMetadata).
        const winnerLine = linesRef.current[foundLineIndex];
        const winnerLeadName = leadName || winnerLine?.lead?.name;
        const winnerLeadId = leadId || winnerLine?.lead?.id;
        const winnerPhone = payload?.payload?.customerPhone
          || payload?.payload?.phoneNumber
          || winnerLine?.lead?.phone;
        const metadataUpdates: Record<string, any> = {};
        if (payload?.payload?.callerId) metadataUpdates.outboundCallerId = payload.payload.callerId;
        if (winnerLeadName) metadataUpdates.leadName = winnerLeadName;
        if (winnerLeadId) metadataUpdates.leadId = winnerLeadId;
        if (winnerPhone) metadataUpdates.phoneNumber = winnerPhone;
        if (Object.keys(metadataUpdates).length > 0) {
          updateCallMetadata(metadataUpdates);
        }

        // Increment answered count (both state AND ref for cross-batch accuracy)
        setTotalAnsweredCount((prev: number) => prev + 1);
        totalAnsweredCountRef.current += 1;

        // Update the line status to connected and mark other dialing/ringing lines for re-queue
        let leadsToReQueue: Lead[] = [];
        setLines(prev => {
          // Find leads that should be re-queued: all other lines that have a lead assigned
          // and aren't already in a terminal/connected state.
          // CRITICAL: Must check for "no-answer" and "voicemail" too — not just "dialing"/"ringing".
          // Race condition: the polling handler (800ms interval) can mark canceled losers as "no-answer"
          // BEFORE this Supabase customer_answered event arrives. If we only filter for "dialing"/"ringing",
          // those already-transitioned lines get skipped and their leads are lost from the re-queue.
          const reQueueStatuses = ["dialing", "ringing", "connecting", "no-answer", "voicemail", "failed"];
          leadsToReQueue = prev
            .filter((line, idx) =>
              idx !== foundLineIndex &&
              line.lead &&
              reQueueStatuses.includes(line.status)
            )
            .map(line => line.lead);

          return prev.map((line, index) => {
            if (index === foundLineIndex) {
              // Set status to connected and ensure callSid is set from event (if provided)
              // This ensures disconnect events can be properly matched
              return {
                ...line,
                status: "connected" as const,
                callSid: customerCallSid || line.callSid,
              };
            }
            // Mark other non-terminal lines as "no-answer" (will be re-queued)
            if (reQueueStatuses.includes(line.status)) {
              return { ...line, status: "no-answer" as const };
            }
            return line;
          });
        });

        // Re-queue leads that were dialing when someone else answered
        if (leadsToReQueue.length > 0) {
          addToReQueue(leadsToReQueue);
        }

        // IMPORTANT: Cancel the other ringing Twilio calls so they stop ringing
        // When one person answers, we need to hang up the other lines on Twilio's side
        const otherRingingLines = linesRef.current.filter(
          (line, idx) => idx !== foundLineIndex &&
          (line.status === "dialing" || line.status === "ringing") &&
          line.callSid
        );

        if (otherRingingLines.length > 0) {
          // Cancel all other ringing calls in parallel
          Promise.all(
            otherRingingLines.map(async (line) => {
              if (!line.callSid || !shouldCancelCall(line.callSid)) return;
              try {
                await api.post({
                  url: getApiUrl(`/api/communication/calls/${line.callSid}/cancel`),
                  body: { reason: "another_line_answered" },
                  auth: true,
                });
              } catch (error) {
                console.error(`[MultiLineDialer] Failed to cancel call ${line.callSid}:`, error);
              }
            })
          );
        }

        // Select this line and show controls
        setSelectedLineIndex(foundLineIndex);
        setConnectedLineIndex(foundLineIndex); // Track connected line separately for stable UI
        // Track lead for showing details after call ends
        const leadIdForTracking = linesRef.current[foundLineIndex]?.lead?.id;
        if (leadIdForTracking) setLastActiveLeadId(leadIdForTracking);

        // PICKUP CHIME backup — if polling's chime didn't fire for some reason
        // (e.g., broadcast beat the poll to the "connecting" state), fire here.
        // Dedup guards against double-firing with the polling trigger.
        const winnerCallSid = linesRef.current[foundLineIndex]?.callSid;
        if (winnerCallSid && !chimeFiredForCallSidsRef.current.has(winnerCallSid)) {
          chimeFiredForCallSidsRef.current.add(winnerCallSid);
          try { playPickupChime(); } catch {}
        }

        // BRANDON'S 4-5s DELAY FIX: set `stableConnectedLeadId` SYNCHRONOUSLY
        // alongside `connectedLineIndex` so the UI renders the lead name in
        // the next frame instead of waiting 2 render cycles for the
        // `useEffect` at line 657 to fire.
        //
        // Before this fix: (1) customer_answered arrives → setConnectedLineIndex
        //   → render #1 (stableConnectedLeadId still null, gated UI still empty)
        //   → useEffect runs → setStableConnectedLeadId → render #2 (name shows).
        // Coalesced with the polling loop's lines-array updates, this could
        // add 1-2s of perceived delay on top of the AMD 3s wait. Brandon's
        // customers were hanging up before the name rendered.
        //
        // After: stableConnectedLeadId is set in the same synchronous batch
        // as connectedLineIndex, so the name renders on render #1.
        if (leadIdForTracking) setStableConnectedLeadId(leadIdForTracking);

        // Stop ringback since someone answered
        stopRingback();

        // Start call timer
        setCallTimer(0); callTimerValueRef.current = 0;
        if (callTimerRef.current) clearInterval(callTimerRef.current);
        callTimerRef.current = setInterval(() => {
          setCallTimer(prev => { callTimerValueRef.current = prev + 1; return prev + 1; });
}, 1000);
      }
    });

    channel.on('broadcast', { event: 'customer_disconnected' }, (payload: any) => {
      // Check if this is a multi-line event
      if (payload?.payload?.isMultiLine) {
        // Block if session is paused — when pause kills all calls, Twilio sends
        // disconnect events for each line. Processing them triggers setLines, re-renders,
        // and batch completion checks that cause the UI to "trip out".
        if (isPausedRef.current) {
          return;
        }

        // Reject stale events from old sessions
        const eventSessionId = payload?.payload?.sessionId;
        if (eventSessionId && eventSessionId !== sessionIdRef.current) {
          return;
        }

        const lineNumber = payload?.payload?.lineNumber;
        const callStatus = payload?.payload?.callStatus;
        const leadId = payload?.payload?.leadId;
        const customerCallSid = payload?.payload?.customerCallSid;

        // Find the line that disconnected - check by lineNumber or leadId
        const disconnectedLineIndex = linesRef.current.findIndex(
          l => l.lineNumber === lineNumber || l.lead?.id === leadId
        );
        const disconnectedLine = disconnectedLineIndex >= 0 ? linesRef.current[disconnectedLineIndex] : null;

        // IMPORTANT: Verify the callSid matches to prevent false disconnects from stale events.
        // Cases:
        //   1. Both have callSid → MUST match (rejects stale events from previous batch)
        //   2. Event has callSid, line doesn't → ALLOW (line hasn't gotten callSid from polling yet)
        //   3. Line has callSid, event doesn't → ALLOW (older event format, lineNumber/leadId match is sufficient)
        //   4. Neither has callSid → ALLOW (best effort match by lineNumber/leadId)
        const callSidMatches = (customerCallSid && disconnectedLine?.callSid)
          ? customerCallSid === disconnectedLine.callSid // Case 1: both present, must match
          : true; // Cases 2/3/4: at least one side missing, trust lineNumber/leadId match

        // Check if this was the connected line
        // Either by matching status === "connected" OR by matching the connectedLineIndexRef
        // AND the callSid must match (if provided)
        const wasConnected = callSidMatches && (
          disconnectedLine?.status === "connected" ||
          (connectedLineIndexRef.current !== null && connectedLineIndexRef.current === disconnectedLineIndex)
        );

        // If this was the connected line AND callSid matches, update the UI state
        // CRITICAL: Clear the ref BEFORE setLines so batch completion effect sees call as ended
        if (wasConnected && connectedLineIndexRef.current !== null) {
          // CRITICAL: Capture call duration BEFORE clearing the timer.
          // The batch completion effect needs this to check if the call was 60s+
          // for auto-pause disposition. Must be captured before callTimerValueRef is zeroed.
          const finalCallDuration = callTimerValueRef.current;
          // Accumulate session talk time. Safe at both hangup sites: whichever
          // runs first zeroes callTimerValueRef, so the other adds 0. The 60s
          // threshold matches the dashboard's "real conversation" definition so
          // the two surfaces cannot drift.
          if (finalCallDuration > 0) {
            sessionTalkSecondsRef.current += finalCallDuration;
            if (finalCallDuration >= 60) {
              sessionConversationSecondsRef.current += finalCallDuration;
            }
          }


          // Stop call timer
          if (callTimerRef.current) {
            clearInterval(callTimerRef.current);
            callTimerRef.current = null;
          }
          // CRITICAL: Do NOT disconnect the agent from the conference here.
          // The agent should stay on the conference so the next batch can transition cleanly.
          // The agent's conference leg is properly disconnected by startMultiLineSession()
          // when the next batch starts, or by the session completion handler.

          // Clear active conference (for add party functionality)
          setActiveConference(null);
          // Clear connected line BEFORE setLines (but keep selectedLineIndex to show lead details)
          connectedLineIndexRef.current = null; // Update ref immediately
          setConnectedLineIndex(null); setAppleScreeningDetected(false);
          setCallTimer(0); callTimerValueRef.current = 0;

          // CRITICAL: Update linesRef SYNCHRONOUSLY with duration so the batch completion
          // effect can check if the call was 60s+ for auto-pause.
          if (disconnectedLineIndex >= 0) {
            linesRef.current = linesRef.current.map((line, idx) =>
              idx === disconnectedLineIndex
                ? { ...line, status: "disconnected" as const, duration: finalCallDuration }
                : line
            );
          }

          // Mark this lead as processed (customer hung up on a connected call)
          if (disconnectedLine?.lead?.id && !processedLeadIdsRef.current.has(disconnectedLine.lead.id)) {
            processedLeadIdsRef.current.add(disconnectedLine.lead.id);
            setProcessedCount(processedLeadIdsRef.current.size);
          }

          // Hangup attribution toast — the lead the agent was talking to ended the call.
          // Only fires when the agent had an active connected call (wasConnected = true).
          // Skipped during batch transitions or when canceled losers disconnect.
          if (callStatus !== 'voicemail') {
            const displayName = disconnectedLine?.lead?.name?.trim()
              || disconnectedLine?.lead?.phone
              || `Line ${disconnectedLine?.lineNumber ?? ''}`.trim();
            const dur = finalCallDuration;
            const durLabel = formatDurationShort(dur);
            const isInstantDrop = dur === 0;
            const isQuickDrop = dur > 0 && dur < 4;
            toast({
              title: isInstantDrop
                ? `${displayName} dropped instantly`
                : isQuickDrop
                  ? `${displayName} dropped after ${durLabel}`
                  : `${displayName} hung up`,
              description: dur >= 4
                ? `They ended the call after ${durLabel}.`
                : 'They picked up and ended the call right away.',
              variant: "destructive",
              duration: 5000,
              // Red stripe — lead disconnected, agent shouldn't take blame.
              className: 'border-l-4 border-l-red-500',
            });
          }
        }

        // Only update line status if callSid matches (or no callSid to compare)
        // This prevents cancelled calls from overwriting the status of active calls
        if (callSidMatches || disconnectedLine?.status !== "connected") {
          // Update the line status based on callStatus from backend
          // "voicemail" status means AMD detected voicemail - show as voicemail not disconnected
          const newStatus = callStatus === 'voicemail' ? 'voicemail' as const : 'disconnected' as const;
          setLines(prev => prev.map((line, index) => {
            if (index === disconnectedLineIndex || line.lineNumber === lineNumber) {
              // Don't downgrade a connected line to disconnected unless callSid matches
              if (line.status === "connected" && !callSidMatches) {
                return line;
              }
              // Include duration from linesRef (set synchronously above for 60s+ pause check)
              const refLine = linesRef.current[index];
              const duration = refLine?.duration || line.duration;
              return { ...line, status: newStatus, ...(duration ? { duration } : {}) };
            }
            return line;
          }));
        }

        if (!wasConnected && connectedLineIndexRef.current === null) {
          // All lines failed without anyone answering - stop ringback but don't disconnect agent yet
          // This handles the case where all outbound calls fail/go to voicemail
          const allLinesFailed = linesRef.current.every(
            l => ['disconnected', 'failed', 'no-answer', 'voicemail'].includes(l.status)
          );
          if (allLinesFailed) {
            stopRingback();
          }
        }
      }
    });

    channel.subscribe(handleStatus);

    return () => {
      removeRealtimeChannel(channel);
    };
  // IMPORTANT: Do NOT include 'lines' in deps - use linesRef instead to prevent infinite re-subscriptions
  // IMPORTANT: Do NOT include 'disconnect' in deps - it's a plain arrow function (not useCallback-memoized)
  // that gets a new identity every render, which would cause the Supabase channel to unsubscribe/resubscribe
  // on every render (creating a gap where customer_answered events could be missed).
  // disconnect is NOT used inside this effect — all disconnect() calls were removed from these handlers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user?.id, isSessionActive, supabaseClient, addToReQueue, stopRingback, rejoinToken]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "idle":
        return "bg-gray-200 text-gray-700";
      case "dialing":
        return "bg-yellow-500 text-white animate-pulse";
      case "ringing":
        return "bg-blue-500 text-white animate-pulse";
      case "connecting":
        return "bg-cyan-500 text-white animate-pulse";
      case "connected":
        return "bg-green-600 text-white";
      case "on-hold":
        return "bg-purple-500 text-white";
      case "disconnected":
        return "bg-gray-300 text-gray-700";
      case "failed":
        return "bg-red-600 text-white";
      case "no-answer":
      case "voicemail":
        return "bg-orange-500 text-white";
      default:
        return "bg-gray-200 text-gray-700";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "dialing":
      case "ringing":
        return <PhoneOutgoing className="h-3.5 w-3.5" />;
      case "connecting":
        return <PhoneCall className="h-3.5 w-3.5" />;
      case "connected":
        return <PhoneCall className="h-3.5 w-3.5" />;
      case "on-hold":
        return <Volume2 className="h-3.5 w-3.5" />;
      case "disconnected":
        return <CheckCircle className="h-3.5 w-3.5" />;
      case "failed":
        return <PhoneOff className="h-3.5 w-3.5" />;
      case "no-answer":
      case "voicemail":
        return <Clock className="h-3.5 w-3.5" />;
      default:
        return <Phone className="h-3.5 w-3.5" />;
    }
  };

  // Format call duration
  const formatDuration = (seconds: number) => {
    const secs = seconds % 60;
    const mins = Math.floor(seconds / 60) % 60;
    const hrs = Math.floor(seconds / 3600);
    // Roll into hours past 60 min so a long call reads 2:16:19, not 136:19
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Show loading if device not ready
  if (isDeviceRegistering || !isDeviceReady) {
    return (
      <Card className="border-2 border-primary/20 bg-primary/5">
        <CardContent className="py-8 text-center">
          <div className="animate-pulse">
            <Phone className="h-8 w-8 mx-auto text-primary mb-3" />
            <p className="text-muted-foreground">Initializing phone system...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Compact Widget View for UnifiedDialerWidget
  if (hideLeadDetails) {
    // Calculate progress (processedCount state tracks unique processed lead IDs)
    const progressPercent = totalLeads > 0 ? Math.round((processedCount / totalLeads) * 100) : 0;
    const queueCount = reQueuedLeads.length + leadQueue.length;

    // Compact status colors
    const getCompactStatusColor = (status: string) => {
      switch (status) {
        case "dialing":
        case "ringing":
          return "bg-blue-500";
        case "connecting":
          // Bug fix: previously fell through to gray-400, which made the
          // compact status dot look like "disconnected" during the AMD
          // wait window. Match the full view's cyan pulse so agents know
          // a line just picked up even from the collapsed widget.
          return "bg-cyan-500";
        case "connected":
          return "bg-green-500";
        case "disconnected":
          return "bg-gray-400";
        case "failed":
          return "bg-red-500";
        case "no-answer":
        case "voicemail":
          // Both mean "didn't connect" - use muted color
          return "bg-gray-400";
        default:
          return "bg-gray-400";
      }
    };

    return (
      <div className="p-3 space-y-3">
        {/* Hidden audio element for remote audio */}
        <audio ref={remoteAudioRef} autoPlay />

        {/* Compact Progress Header - Primary theme */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Users className="h-3.5 w-3.5 text-primary" />
              <span className="font-semibold text-xs text-primary">Live Session</span>
              {isSessionActive && (
                <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
              )}
              {isPaused && <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 bg-amber-100 text-amber-700 border-0">Paused</Badge>}
            </div>
            <div className="flex items-center gap-1">
              <Button
                onClick={isPaused ? handleResume : handlePause}
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
              >
                {isPaused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
              </Button>
              <Button
                onClick={handleStopSession}
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <StopCircle className="h-3.5 w-3.5" />
              </Button>
              <span className="text-sm font-bold text-primary ml-1">{progressPercent}%</span>
            </div>
          </div>
          <Progress className="h-1.5 bg-primary/20" value={progressPercent} />
          {/* Compact Stats Row */}
          <div className="grid grid-cols-5 gap-2 mt-2 text-xs text-center">
            <div>
              <div className="font-bold text-gray-900">{dialingCount}</div>
              <div className="text-muted-foreground">Dialing</div>
            </div>
            <div>
              <div className="font-bold text-gray-900">{connectedCount}</div>
              <div className="text-muted-foreground">Connected</div>
            </div>
            <div>
              <div className="font-bold text-gray-900">{totalAnsweredCount}</div>
              <div className="text-muted-foreground">Answered</div>
            </div>
            <div>
              <div className="font-bold text-gray-900">{queueCount}</div>
              <div className="text-muted-foreground">Queue</div>
            </div>
            <div>
              <div className="font-bold text-gray-900">{Math.max(0, totalLeads - processedCount - resumeOffset)}</div>
              <div className="text-muted-foreground">Left</div>
            </div>
          </div>
        </div>

        {/* Connected Call View */}
        {stableConnectedLine ? (
          <div className="bg-white rounded-lg border border-primary/30 shadow-sm overflow-hidden">
            {/* Connected Lead Header */}
            <div className="bg-gradient-to-r from-primary to-primary/90 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white">
                    <User className="h-5 w-5" />
                  </div>
                  <div className="text-white">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{stableConnectedLine.lead.name}</span>
                      <Badge className="bg-green-500 text-white text-[9px] px-1.5 py-0 h-4 border-0 font-semibold">
                        Answered
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="text-xs text-white/70">
                        {formatPhoneNumberDisplay(stableConnectedLine.lead.phone || "")}
                      </div>
                      {callMetadata?.outboundCallerId && (
                        <button
                          onClick={() => setShowCallerId(!showCallerId)}
                          className={`p-0.5 rounded ${showCallerId ? 'text-emerald-300 hover:text-emerald-200' : 'text-white/60 hover:text-emerald-300'} transition-colors`}
                          title={showCallerId ? 'Hide outbound caller ID' : 'Show outbound caller ID'}
                        >
                          {showCallerId ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </button>
                      )}
                    </div>
                    {showCallerId && callMetadata?.outboundCallerId && (
                      <div className="text-xs text-emerald-300 font-semibold">From: {formatPhoneNumberDisplay(callMetadata.outboundCallerId)}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-white/20 text-white text-xs">
                    <Clock className="h-3 w-3 mr-1" />
                    {formatDuration(callTimer)}
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    className={`h-7 w-7 p-0 ${showQuickNotes ? 'text-amber-300 bg-white/20' : 'text-white hover:bg-white/20'}`}
                    onClick={() => setShowQuickNotes(!showQuickNotes)}
                    title="Quick Notes"
                  >
                    <StickyNote className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Quick Notes Panel */}
            {showQuickNotes && stableConnectedLine && (
              <div className="px-3 py-2 border-b border-gray-100">
                <QuickNotesPanel
                  leadId={stableConnectedLine.lead.id}
                  leadName={stableConnectedLine.lead.name}
                  onClose={() => setShowQuickNotes(false)}
                />
              </div>
            )}

            {/* Hardware Mute Warning */}
            {isHardwareMuted && !isMuted && (
              <div className="bg-red-100 border-b border-red-200 p-2 flex items-center gap-2">
                <MicOff className="h-4 w-4 text-red-600 animate-pulse" />
                <span className="text-xs text-red-800">Microphone muted by headset - client cannot hear you</span>
              </div>
            )}

            {/* Lead Details - expandable toggle */}
            <button
              onClick={() => setShowLeadInfo(!showLeadInfo)}
              className="w-full px-3 py-1.5 flex items-center gap-1.5 text-xs text-gray-500 hover:bg-gray-50 border-b border-gray-100 transition-colors"
            >
              {showLeadInfo ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              <span>Lead Info</span>
            </button>

            {showLeadInfo && connectedLeadDetails && (
              <div className="px-3 py-2 border-b border-gray-100">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {/* Email */}
                  {connectedLeadDetails.email && (
                    <div className="text-center">
                      <span className="text-gray-400">Email</span>
                      <p className="text-gray-700 truncate">{connectedLeadDetails.email}</p>
                    </div>
                  )}

                  {/* Age */}
                  {connectedLeadDetails.age && (
                    <div className="text-center">
                      <span className="text-gray-400">Age</span>
                      <p className="text-gray-700">{connectedLeadDetails.age} years</p>
                    </div>
                  )}

                  {/* DOB */}
                  {(connectedLeadDetails.dob || connectedLeadDetails.birthday) && (
                    <div className="text-center">
                      <span className="text-gray-400">DOB</span>
                      <p className="text-gray-700">
                        {(() => {
                          const dobStr = connectedLeadDetails.dob || connectedLeadDetails.birthday;
                          try {
                            const [year, month, day] = dobStr!.split('T')[0].split('-').map(Number);
                            return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
                          } catch {
                            return dobStr;
                          }
                        })()}
                      </p>
                    </div>
                  )}

                  {/* Gender */}
                  {connectedLeadDetails.gender && (
                    <div className="text-center">
                      <span className="text-gray-400">Gender</span>
                      <p className="text-gray-700 capitalize">{connectedLeadDetails.gender}</p>
                    </div>
                  )}

                  {/* Marital Status */}
                  {(connectedLeadDetails.marital || connectedLeadDetails.maritalStatus) && (
                    <div className="text-center">
                      <span className="text-gray-400">Marital</span>
                      <p className="text-gray-700 capitalize">{connectedLeadDetails.marital || connectedLeadDetails.maritalStatus}</p>
                    </div>
                  )}

                  {/* Height */}
                  {connectedLeadDetails.height && (
                    <div className="text-center">
                      <span className="text-gray-400">Height</span>
                      <p className="text-gray-700">{connectedLeadDetails.height}</p>
                    </div>
                  )}

                  {/* Weight */}
                  {connectedLeadDetails.weight && (
                    <div className="text-center">
                      <span className="text-gray-400">Weight</span>
                      <p className="text-gray-700">{connectedLeadDetails.weight} lbs</p>
                    </div>
                  )}

                  {/* Address */}
                  {connectedLeadDetails.address && (
                    <div className="text-center">
                      <span className="text-gray-400">Address</span>
                      <p className="text-gray-700 break-words">{connectedLeadDetails.address}</p>
                    </div>
                  )}

                  {/* State/Zip */}
                  {(connectedLeadDetails.state || connectedLeadDetails.zipCode) && (
                    <div className="text-center">
                      <span className="text-gray-400">State/Zip</span>
                      <p className="text-gray-700">{[connectedLeadDetails.state, connectedLeadDetails.zipCode].filter(Boolean).join(' ')}</p>
                    </div>
                  )}

                  {/* City - full width centered */}
                  {connectedLeadDetails.city && (
                    <div className="col-span-2 text-center">
                      <span className="text-gray-400">City</span>
                      <p className="text-gray-700">{connectedLeadDetails.city}</p>
                    </div>
                  )}

                  {/* Employment - full width centered */}
                  {(connectedLeadDetails.employer || connectedLeadDetails.employmentStatus) && (
                    <div className="col-span-2 text-center">
                      <span className="text-gray-400">Employment</span>
                      <p className="text-gray-700 break-words">{connectedLeadDetails.employer || connectedLeadDetails.employmentStatus}</p>
                    </div>
                  )}
                </div>

                {/* List name badge */}
                {connectedLeadDetails.listName && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      {connectedLeadDetails.listName}
                    </span>
                  </div>
                )}

                {/* Fallback when no fields available */}
                {!connectedLeadDetails.email &&
                 !connectedLeadDetails.dob &&
                 !connectedLeadDetails.birthday &&
                 !connectedLeadDetails.age &&
                 !connectedLeadDetails.gender &&
                 !connectedLeadDetails.marital &&
                 !connectedLeadDetails.maritalStatus &&
                 !connectedLeadDetails.height &&
                 !connectedLeadDetails.weight &&
                 !connectedLeadDetails.address &&
                 !connectedLeadDetails.city &&
                 !connectedLeadDetails.state &&
                 !connectedLeadDetails.zipCode &&
                 !connectedLeadDetails.employer &&
                 !connectedLeadDetails.employmentStatus &&
                 !connectedLeadDetails.listName && (
                  <p className="text-xs text-gray-400 italic">No additional details available</p>
                )}
              </div>
            )}

            {/* Call Controls */}
            <div className="p-3 space-y-2">
              <div className="grid grid-cols-4 gap-2">
                <Button
                  onClick={() => (isMuted ? unmute() : mute())}
                  size="sm"
                  variant={isMuted ? "default" : "outline"}
                  className={`h-8 text-xs ${isMuted ? "bg-yellow-600 hover:bg-yellow-700" : ""}`}
                >
                  {isMuted ? <MicOff className="h-3 w-3" /> : <Mic className="h-3 w-3" />}
                </Button>
                <Button
                  onClick={handleHoldToggle}
                  size="sm"
                  variant={isOnHold ? "default" : "outline"}
                  className={`h-8 text-xs ${isOnHold ? "bg-orange-600 hover:bg-orange-700" : ""}`}
                >
                  {isOnHold ? (
                    <Play className="h-3 w-3" />
                  ) : (
                    <Pause className="h-3 w-3" />
                  )}
                </Button>
                {/* Dial Pad Button */}
                <Button
                  size="sm"
                  variant={showDialPad ? "default" : "outline"}
                  onClick={() => setShowDialPad(!showDialPad)}
                  className={`h-8 text-xs ${showDialPad ? "bg-primary hover:bg-primary/90" : ""}`}
                  title="Dial Pad"
                >
                  <Hash className="h-3 w-3" />
                </Button>
                {/* 3-Way Calling Button */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowAddPartyPanel(!showAddPartyPanel)}
                  className={`h-8 text-xs ${showAddPartyPanel ? "bg-primary hover:bg-primary/90 text-white" : ""}`}
                  title="Add Party"
                  disabled={!activeCallSid}
                >
                  <UserPlus className="h-3 w-3" />
                </Button>
              </div>
              {hasVoicemailDrops && (
                <Button
                  onClick={handleDropVoicemail}
                  size="sm"
                  variant="outline"
                  className="w-full h-8 text-xs font-medium border-amber-300 text-amber-700 hover:bg-amber-50"
                  disabled={droppingVm || (connectedLineIndex === null && !activeCallSid)}
                  title="Leave your pre-recorded voicemail and end the call"
                >
                  {droppingVm ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      Dropping…
                    </>
                  ) : (
                    <>
                      <Voicemail className="h-3.5 w-3.5 mr-1.5" />
                      Drop Voicemail
                    </>
                  )}
                </Button>
              )}
              {!onDirect130 && (
                <Button
                  onClick={() => setShowTransferPanel((v) => !v)}
                  size="sm"
                  variant="outline"
                  className={`w-full h-8 text-xs font-medium border-emerald-300 text-emerald-700 hover:bg-emerald-50 ${showTransferPanel ? 'bg-emerald-50' : ''}`}
                  disabled={connectedLineIndex === null}
                  title="Warm-transfer this call to an agency owner"
                >
                  <PhoneForwarded className="h-3.5 w-3.5 mr-1.5" />
                  Transfer Call
                </Button>
              )}
              {/* Inline dropdown — same in-place pattern as the manual widget
                  (2026-07-23, replaced the floating popup). Offline/busy
                  owners are disabled inside the panel. */}
              {showTransferPanel && connectedLineIndex !== null && (() => {
                const idx = connectedLineIndexRef.current;
                const line = idx !== null ? linesRef.current[idx] : undefined;
                return line?.callSid ? (
                  <InlineTransferPanel
                    callSid={line.callSid}
                    leadId={line.lead?.id}
                    leadName={line.lead?.name}
                    onClose={() => setShowTransferPanel(false)}
                  />
                ) : null;
              })()}
              <Button
                onClick={() => handleEndCall()}
                size="sm"
                variant="destructive"
                className="w-full h-9 text-xs font-semibold"
                disabled={endCooldownActive}
              >
                {endCooldownActive ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    Ending...
                  </>
                ) : (
                  <>
                    <PhoneOff className="h-3.5 w-3.5 mr-1.5" />
                    End Call
                  </>
                )}
              </Button>

              {/* Quick disposition row - Bad #, DNC, No Sale */}
              <div className="grid grid-cols-3 gap-1.5">
                <Button
                  onClick={handleMarkBadNumber}
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                >
                  <PhoneOff className="h-3 w-3 mr-1" />
                  Bad #
                </Button>
                <Button
                  onClick={handleMarkDNC}
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs text-red-700 border-red-200 hover:bg-red-50"
                >
                  <Ban className="h-3 w-3 mr-1" />
                  DNC
                </Button>
                <Button
                  onClick={() => handleStatusUpdate(LeadStatus.NO_SALE)}
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                >
                  <XCircle className="h-3 w-3 mr-1" />
                  No Sale
                </Button>
              </div>
              <QuickDispositionButtons onPick={handleQuickDisposition} />

              {/* Dial Pad */}
              {showDialPad && (
                <div className="mt-2 p-2 border border-gray-200 rounded-lg bg-white">
                  <div className="grid grid-cols-3 gap-1">
                    {[
                      { digit: "1", letters: "" },
                      { digit: "2", letters: "ABC" },
                      { digit: "3", letters: "DEF" },
                      { digit: "4", letters: "GHI" },
                      { digit: "5", letters: "JKL" },
                      { digit: "6", letters: "MNO" },
                      { digit: "7", letters: "PQRS" },
                      { digit: "8", letters: "TUV" },
                      { digit: "9", letters: "WXYZ" },
                      { digit: "*", letters: "" },
                      { digit: "0", letters: "+" },
                      { digit: "#", letters: "" },
                    ].map(({ digit, letters }) => (
                      <Button
                        key={digit}
                        variant="outline"
                        size="sm"
                        className="h-9 flex flex-col justify-center hover:bg-gray-100"
                        onClick={() => sendDigits(digit)}
                      >
                        <span className="text-sm font-semibold">{digit}</span>
                        {letters && <span className="text-[7px] text-gray-500">{letters}</span>}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Inline Add Party Panel for 3-Way Calling (Compact) */}
              {showAddPartyPanel && (
                <InlineAddPartyPanel
                  participants={conferenceParticipants}
                  onMuteToggle={handleMuteToggle}
                  onHoldToggle={handleParticipantHoldToggle}
                  onRemoveParticipant={handleRemoveParticipant}
                  onAddParticipant={handleAddParticipant}
                  isAddingParticipant={isAddingParticipant}
                  loadingParticipantSid={loadingParticipantSid || undefined}
                  onClose={() => setShowAddPartyPanel(false)}
                  className="mt-2"
                />
              )}
            </div>

            {/* InsuraBot Key Grabber */}
            <div className="border-t p-3">
              <InsuraABotKeyGrabber
                codes={insuraABotKeyCodes}
                isDTMFListening={isDTMFListening}
                setIsDTMFListening={setIsDTMFListening}
                callId={stableConnectedLine.callSid || activeCallSid}
                isSpeechProcessing={isSpeechProcessing}
                onClearCodes={handleClearCodes}
              />
              <DTMFPollingWrapper
                ref={dtmfPollingRef}
                callSid={stableConnectedLine.callSid || activeCallSid || null}
                enabled={isDTMFListening && !!(stableConnectedLine.callSid || activeCallSid)}
                userId={user?.user?.id || null}
                onDigitsUpdate={handleDTMFUpdate}
                onSpeechProcessingUpdate={setIsSpeechProcessing}
              />
            </div>

            {/* View Lead Details Button */}
            <div className="p-3 border-t bg-gray-50">
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs h-8"
                onClick={() => router.push(`/leads/${stableConnectedLine.lead.id}`)}
              >
                <ExternalLink className="h-3 w-3 mr-2" />
                View Full Lead Details
              </Button>
            </div>
          </div>
        ) : awaitingMultiLineDisposition ? (
          /* Post-Call Disposition View — agent had a 60s+ call, shows who they talked to */
          (() => {
            const lastLead = lastActiveLeadId ? leads.find(l => l.id === lastActiveLeadId) : null;
            const lastLine = lines.find(l => l.lead?.id === lastActiveLeadId);
            // Incoming-call fallback: when the agent answered an inbound
            // call mid-multi-line, the caller isn't in the queue, so the
            // multi-line lookups above return null. Use the info captured
            // from the emergency-pause CustomEvent detail so the card shows
            // "you just talked to <incoming caller>" instead of literal "Lead".
            const displayName = lastLead?.name
              || lastLine?.lead?.name
              || incomingPauseInfo?.name
              || 'Lead';
            const displayPhone = lastLead?.phone
              || lastLine?.lead?.phone
              || incomingPauseInfo?.number
              || "";
            return (
              <div className="bg-white rounded-lg border border-primary/30 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-gray-600 to-gray-700 p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white">
                        <User className="h-5 w-5" />
                      </div>
                      <div className="text-white">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{displayName}</span>
                          <Badge className="bg-gray-500 text-white text-[9px] px-1.5 py-0 h-4 border-0 font-semibold">
                            Call Ended
                          </Badge>
                        </div>
                        <div className="text-xs text-white/70">
                          {formatPhoneNumberDisplay(displayPhone)}
                          {lastLine?.duration ? ` · ${Math.floor(lastLine.duration / 60)}:${String(lastLine.duration % 60).padStart(2, '0')}` : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-3 space-y-2">
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700 h-10 text-sm font-medium"
                    disabled={isPreparingNextBatch}
                    onClick={() => {
                      // Prevent rage-clicking — disable immediately via isPreparingNextBatch
                      if (isPreparingNextBatch) return;
                      // Clear ALL guards that could block dialNextLeads
                      awaitingMultiLineDispositionRef.current = false;
                      setAwaitingMultiLineDisposition(false);
                      isDialingNextRef.current = false;
                      lastDialTimeRef.current = 0;
                      isPostCallRef.current = false;
                      connectedLineIndexRef.current = null;
                      setConnectedLineIndex(null);
                      setIsPreparingNextBatch(true);
                      // Drop the incoming-caller info now that the agent
                      // is moving on — next batch's disposition should show
                      // the actual multi-line lead, not the prior caller.
                      setIncomingPauseInfo(null);
                      // Force all lines to idle so dialNextLeads sees them as available
                      linesRef.current = linesRef.current.map(l => ({ ...l, status: "idle" as const, duration: undefined }));
                      setLines(prev => prev.map(l => ({ ...l, status: "idle" as const, duration: undefined })));
                      // Dial immediately — guards are cleared, lines are idle
                      const availableIndices = linesRef.current.map((_, i) => i);
                      dialNextLeads(availableIndices);
                      // Safety: if dialNextLeads fails silently, re-enable button after 3 seconds
                      setTimeout(() => {
                        setIsPreparingNextBatch(false);
                      }, 3000);
                    }}
                  >
                    {isPreparingNextBatch ? "Dialing..." : "Resume Multi-Line"}
                  </Button>
                  {/* Quick Disposition Buttons */}
                  {(lastLead?.id || lastLine?.lead?.id) && (
                    <div className="grid grid-cols-3 gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-8 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={async () => {
                          const lid = lastLead?.id || lastLine?.lead?.id;
                          if (!lid) return;
                          try {
                            await updateLeadStatusMutation.mutateAsync({ leadId: lid, status: LeadStatus.BAD_NUMBER });
                          } catch {}
                        }}
                      >
                        Bad #
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-8 border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                        onClick={() => fileLastLead(lastLead?.id || lastLine?.lead?.id, LeadStatus.DO_NOT_CALL)}
                      >
                        DNC
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-8 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => {
                          const lid = lastLead?.id || lastLine?.lead?.id;
                          if (lid) noSaleLeadIdRef.current = lid;
                          setShowNoSaleModal(true);
                        }}
                      >
                        No Sale
                      </Button>
                    </div>
                  )}
                  {(lastLead?.id || lastLine?.lead?.id) ? (
                    <QuickDispositionButtons onPick={(status) => fileLastLead(lastLead?.id || lastLine?.lead?.id, status)} />
                  ) : null}
                  {(lastLead?.id || lastLine?.lead?.id) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-xs h-8"
                      onClick={() => router.push(`/leads/${lastLead?.id || lastLine?.lead?.id}`)}
                    >
                      <ExternalLink className="h-3 w-3 mr-2" />
                      View Full Lead Details
                    </Button>
                  )}
                </div>
              </div>
            );
          })()
        ) : (
          /* Dialing/Ringing View - Compact Grid of Lines */
          <div className="space-y-2">

            {/* Preparing next batch indicator — shows between batches so agent knows it's not stuck */}
            <PreparingBatchIndicator
              isPreparingNextBatch={isPreparingNextBatch}
              preparingBatchTooLong={preparingBatchTooLong}
              dialingCount={dialingCount}
              onSkipRetry={() => {
                setIsPreparingNextBatch(false);
                isDialingNextRef.current = false;
                handleSkipAllRinging();
              }}
              compact
            />

            {/* Skip All Button - shows when any lines are ringing */}
            {dialingCount > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="w-full h-7 text-xs bg-white hover:bg-red-50 border-red-200 text-red-600"
                onClick={handleSkipAllRinging}
                disabled={skipCooldownActive}
              >
                {skipCooldownActive ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Skipping...
                  </>
                ) : (
                  <>
                    <SkipForward className="h-3 w-3 mr-1" />
                    {dialingCount === 1 ? "Skip Ringing" : `Skip All Ringing (${dialingCount})`}
                  </>
                )}
              </Button>
            )}

            {/* Reassuring message while lines are ringing */}
            {dialingCount > 0 && (
              <div className="flex items-center justify-center gap-2 py-1.5 px-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700">
                <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <span>Ringing {dialingCount} {dialingCount === 1 ? "line" : "lines"} — first person to answer connects to you</span>
              </div>
            )}

            {/* Lines Grid */}
            <div className={`grid gap-2 ${lines.length <= 2 ? "grid-cols-1" : "grid-cols-2"}`}>
              {lines.map((line, index) => (
                <div
                  key={`widget-line-${line.lead.id}`}
                  className={`bg-white rounded-lg border shadow-sm overflow-hidden ${
                    line.status === "ringing" || line.status === "dialing"
                      ? "border-blue-300"
                      : line.status === "connected"
                        ? "border-green-300"
                        : line.status === "no-answer"
                          ? "border-orange-300"
                          : line.status === "voicemail"
                            ? "border-amber-300"
                            : line.status === "failed"
                              ? "border-red-300"
                              : "border-gray-200"
                  }`}
                >
                  <div className="p-2">
                    <div className="flex items-center gap-2">
                      {/* Avatar with status indicator */}
                      <div className="relative">
                        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white">
                          <User className="h-4 w-4" />
                        </div>
                        <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${getCompactStatusColor(line.status)}`} />
                      </div>
                      {/* Lead Info */}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-xs truncate">{line.lead.name}</div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {formatPhoneNumberDisplay(line.lead.phone || "")}
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        {/* Individual skip buttons removed - use "Skip All Ringing" at top instead */}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
                          onClick={() => router.push(`/leads/${line.lead.id}`)}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    {/* Lead Details - Shown for idle/ringing lines */}
                    {(line.status === "idle" || line.status === "dialing" || line.status === "ringing") && (
                      <div className="mt-2 pt-2 border-t space-y-1">
                        {(line.lead.city || line.lead.state) && (
                          <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                            <MapPin className="h-2.5 w-2.5" />
                            <span className="truncate">{[line.lead.city, line.lead.state].filter(Boolean).join(', ')}</span>
                          </div>
                        )}
                        {line.lead.age && (
                          <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                            <User className="h-2.5 w-2.5" />
                            <span>{line.lead.age} yrs{line.lead.gender ? ` • ${line.lead.gender}` : ''}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Status Badge */}
                    <div className="mt-1">
                      <Badge className={`text-[10px] px-1.5 py-0 ${getStatusColor(line.status)}`}>
                        {getStatusIcon(line.status)}
                        <span className="ml-1">
                          {line.status === "dialing" || line.status === "ringing" ? "Ringing..." : line.status === "no-answer" ? "No Answer" : line.status === "connected" ? "Answered" : line.status === "disconnected" ? "Ended" : line.status === "voicemail" ? "Voicemail" : line.status === "failed" ? "No Answer" : line.status === "on-hold" ? "On Hold" : line.status === "connecting" ? "Ringing..." : line.status === "idle" ? "Ready" : "Unknown"}
                        </span>
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Session Controls */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={isPaused ? handleResume : handlePause}
                size="sm"
                variant={isPaused ? "default" : "outline"}
                className={`h-8 text-xs ${isPaused ? "bg-yellow-600 hover:bg-yellow-700" : ""}`}
              >
                {isPaused ? (
                  <>
                    <Play className="h-3 w-3 mr-1" />
                    Resume Queue
                  </>
                ) : (
                  <>
                    <Pause className="h-3 w-3 mr-1" />
                    Pause Queue
                  </>
                )}
              </Button>
              <Button
                onClick={handleStopSession}
                size="sm"
                variant="destructive"
                className="h-8 text-xs"
              >
                <StopCircle className="h-3 w-3 mr-1" />
                Stop Session
              </Button>
            </div>
          </div>
        )}

        {/* Modals for Quick Actions */}
        {selectedLead && (
          <>
            <SendSmsModal
              lead={selectedLead}
              open={showSmsModal}
              onOpenChange={setShowSmsModal}
            />
            <SendEmailModal
              lead={selectedLead}
              open={showEmailModal}
              onOpenChange={setShowEmailModal}
            />
            <NewAppointmentDialog
              open={showAppointmentDialog}
              onOpenChange={setShowAppointmentDialog}
              preselectedLeadId={selectedLead.id}
              preselectedLeadName={selectedLead.name}
              preselectedLeadState={selectedLead.state}
              onStatusChange={(leadId, newStatus) => {
                // Track lead status for session stats when appointment is created
                setLeadStatuses(prev => ({
                  ...prev,
                  [leadId]: newStatus,
                }));
                // Also update the ref immediately to ensure it's captured in completion timeout
                leadStatusesRef.current = { ...leadStatusesRef.current, [leadId]: newStatus };
              }}
            />
            <NewReminderDialog
              open={showReminderDialog}
              onOpenChange={setShowReminderDialog}
            />
          </>
        )}

        <NoSaleReasonModal
          isOpen={showNoSaleModal}
          onOpenChange={setShowNoSaleModal}
          onConfirm={handleNoSaleConfirm}
        />
        {dncModal}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Hidden audio element for remote audio */}
      <audio ref={remoteAudioRef} autoPlay />

      {/* Session Header */}
      <Card className="border-2 border-primary/20 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <span>Live Session</span>
              {isSessionActive && (
                <div className="flex items-center gap-1 ml-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-sm text-muted-foreground">Active</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Audio Device Selector */}
              <AudioDeviceSelector
                audioInputDevices={audioInputDevices}
                audioOutputDevices={audioOutputDevices}
                selectedInputDeviceId={selectedInputDeviceId}
                selectedOutputDeviceId={selectedOutputDeviceId}
                onAudioInputChange={setAudioInputDevice}
                onAudioOutputChange={setAudioOutputDevice}
                onRefreshDevices={refreshAudioDevices}
                microphoneAudioLevel={microphoneAudioLevel}
                isHardwareMuted={isHardwareMuted}
                isMuted={isMuted}
                compact={true}
                speakerVolume={speakerVolume}
                onSpeakerVolumeChange={setSpeakerVolume}
                onStartAudioMonitoring={startAudioMonitoring}
                onStopAudioMonitoring={stopAudioMonitoring}
              />
              {/* Pause/Resume Button */}
              <Button
                onClick={isPaused ? handleResume : handlePause}
                size="sm"
                variant={isPaused ? "default" : "outline"}
                className={isPaused ? "bg-yellow-600 hover:bg-yellow-700" : ""}
              >
                {isPaused ? (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Resume
                  </>
                ) : (
                  <>
                    <Pause className="h-4 w-4 mr-2" />
                    Pause Queue
                  </>
                )}
              </Button>
              <Button onClick={handleStopSession} size="sm" variant="destructive">
                <StopCircle className="h-4 w-4 mr-2" />
                Stop Session
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress - Total leads including queue */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>
                Progress: {processedCount + resumeOffset} of {totalLeads} leads
                {(reQueuedLeads.length + leadQueue.length) > 0 && (
                  <span className="text-muted-foreground ml-2">
                    ({reQueuedLeads.length + leadQueue.length} in queue)
                  </span>
                )}
              </span>
              <span>{totalLeads > 0 ? Math.round(((processedCount + resumeOffset) / totalLeads) * 100) : 0}%</span>
            </div>
            <Progress className="h-2" value={totalLeads > 0 ? ((processedCount + resumeOffset) / totalLeads) * 100 : 0} />
            {isPaused && (
              <div className="text-sm text-yellow-600 flex items-center gap-1">
                <Pause className="h-3 w-3" />
                Queue paused - current batch will finish, next batch won't start automatically
              </div>
            )}
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-5 gap-3 text-center">
            <div className="bg-white rounded-lg p-3 border">
              <div className="text-2xl font-bold text-yellow-600">{dialingCount}</div>
              <div className="text-xs text-muted-foreground">Dialing</div>
            </div>
            <div className="bg-white rounded-lg p-3 border">
              <div className="text-2xl font-bold text-green-600">{connectedCount}</div>
              <div className="text-xs text-muted-foreground">Connected</div>
            </div>
            <div className="bg-white rounded-lg p-3 border">
              <div className="text-2xl font-bold text-blue-600">{totalAnsweredCount}</div>
              <div className="text-xs text-muted-foreground">Answered</div>
            </div>
            <div className="bg-white rounded-lg p-3 border">
              <div className="text-2xl font-bold text-primary">{reQueuedLeads.length + leadQueue.length}</div>
              <div className="text-xs text-muted-foreground">In Queue</div>
            </div>
            <div className="bg-white rounded-lg p-3 border">
              <div className="text-2xl font-bold text-gray-600">{Math.max(0, totalLeads - processedCount - resumeOffset)}</div>
              <div className="text-xs text-muted-foreground">Remaining</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lead Display - Different layouts based on call state */}
      {/* Use stableConnectedLine (ref-based) for conditional rendering to prevent mount/unmount cycles */}
      {stableConnectedLine ? (
        /* When someone is connected - show connected lead with call controls above details */
        <Card className="border-2 border-green-400 bg-green-50/50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge className="bg-green-100 text-green-800">
                  <PhoneCall className="h-4 w-4 mr-1" />
                  Connected - Line {stableConnectedLine.lineNumber}
                </Badge>
                {appleScreeningDetected && (
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-300 animate-pulse">
                    <Shield className="h-3 w-3 mr-1" />
                    Potential Call Screening
                  </Badge>
                )}
                <Badge className="bg-green-100 text-green-800">
                  <Clock className="h-3 w-3 mr-1" />
                  {formatDuration(callTimer)}
                </Badge>
                {/* Audio Device Selector */}
                <AudioDeviceSelector
                  audioInputDevices={audioInputDevices}
                  audioOutputDevices={audioOutputDevices}
                  selectedInputDeviceId={selectedInputDeviceId}
                  selectedOutputDeviceId={selectedOutputDeviceId}
                  onAudioInputChange={setAudioInputDevice}
                  onAudioOutputChange={setAudioOutputDevice}
                  onRefreshDevices={refreshAudioDevices}
                  microphoneAudioLevel={microphoneAudioLevel}
                  isHardwareMuted={isHardwareMuted}
                  isMuted={isMuted}
                  compact={true}
                  speakerVolume={speakerVolume}
                  onSpeakerVolumeChange={setSpeakerVolume}
                  onStartAudioMonitoring={startAudioMonitoring}
                  onStopAudioMonitoring={stopAudioMonitoring}
                />
              </div>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleEndCall()}
                disabled={endCooldownActive}
              >
                {endCooldownActive ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Ending...
                  </>
                ) : (
                  <>
                    <PhoneOff className="h-4 w-4 mr-1" />
                    End Call
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {/* Hardware Mute Warning */}
            {isHardwareMuted && !isMuted && (
              <div className="bg-red-100 border border-red-300 rounded-md p-3 flex items-center gap-2">
                <MicOff className="h-5 w-5 text-red-600 animate-pulse" />
                <div>
                  <span className="text-sm font-medium text-red-800">
                    Microphone Muted by Headset
                  </span>
                  <p className="text-xs text-red-600">
                    The client cannot hear you. Check your headset mute button.
                  </p>
                </div>
              </div>
            )}

            {/* Call Control Buttons - integrated above lead details */}
            <div className="grid grid-cols-4 gap-2 p-3 bg-white/50 rounded-lg border border-green-200">
              {/* Mute Button */}
              <Button
                onClick={() => (isMuted ? unmute() : mute())}
                size="sm"
                variant={isMuted ? "default" : "outline"}
                className={isMuted ? "bg-yellow-600 hover:bg-yellow-700" : ""}
              >
                {isMuted ? (
                  <>
                    <MicOff className="h-4 w-4 mr-1" />
                    Unmute
                  </>
                ) : (
                  <>
                    <Mic className="h-4 w-4 mr-1" />
                    Mute
                  </>
                )}
              </Button>

              {/* Hold Button */}
              <Button
                onClick={handleHoldToggle}
                size="sm"
                variant={isOnHold ? "default" : "outline"}
                className={isOnHold ? "bg-orange-600 hover:bg-orange-700" : ""}
                disabled={!activeCallSid}
              >
                {isOnHold ? (
                  <>
                    <Play className="h-4 w-4 mr-1" />
                    Resume
                  </>
                ) : (
                  <>
                    <Pause className="h-4 w-4 mr-1" />
                    Hold
                  </>
                )}
              </Button>

              {/* Dial Pad Button */}
              <Button
                size="sm"
                variant={showDialPad ? "default" : "outline"}
                onClick={() => setShowDialPad(!showDialPad)}
                className={showDialPad ? "bg-primary hover:bg-primary/90" : ""}
              >
                <Hash className="h-4 w-4 mr-1" />
                Dial Pad
              </Button>

              {/* 3-Way Calling Button - Temporarily Disabled */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="relative opacity-50 cursor-not-allowed bg-gray-100 text-gray-400"
                      disabled
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      Add Party
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-medium">Add Party - Temporarily Disabled</p>
                    <p className="text-xs text-gray-400">This feature is currently unavailable</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Quick disposition row - Bad #, DNC, No Sale */}
            <div className="grid grid-cols-3 gap-2">
              <Button
                onClick={handleMarkBadNumber}
                size="sm"
                variant="outline"
                className="h-9 text-red-600 border-red-200 hover:bg-red-50"
              >
                <PhoneOff className="h-4 w-4 mr-1" />
                Bad #
              </Button>
              <Button
                onClick={handleMarkDNC}
                size="sm"
                variant="outline"
                className="h-9 text-red-700 border-red-200 hover:bg-red-50"
              >
                <Ban className="h-4 w-4 mr-1" />
                DNC
              </Button>
              <Button
                onClick={() => handleStatusUpdate(LeadStatus.NO_SALE)}
                size="sm"
                variant="outline"
                className="h-9 text-red-600 border-red-200 hover:bg-red-50"
              >
                <XCircle className="h-4 w-4 mr-1" />
                No Sale
              </Button>
            </div>
            <QuickDispositionButtons buttonClassName="min-h-9 text-sm" onPick={handleQuickDisposition} />

            {/* Dial Pad */}
            {showDialPad && (
              <div className="p-3 border border-gray-200 rounded-lg bg-white">
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { digit: "1", letters: "" },
                    { digit: "2", letters: "ABC" },
                    { digit: "3", letters: "DEF" },
                    { digit: "4", letters: "GHI" },
                    { digit: "5", letters: "JKL" },
                    { digit: "6", letters: "MNO" },
                    { digit: "7", letters: "PQRS" },
                    { digit: "8", letters: "TUV" },
                    { digit: "9", letters: "WXYZ" },
                    { digit: "*", letters: "" },
                    { digit: "0", letters: "+" },
                    { digit: "#", letters: "" },
                  ].map(({ digit, letters }) => (
                    <Button
                      key={digit}
                      variant="outline"
                      className="h-14 flex flex-col justify-center hover:bg-gray-100"
                      onClick={() => sendDigits(digit)}
                    >
                      <span className="text-xl font-semibold">{digit}</span>
                      {letters && <span className="text-xs text-gray-500">{letters}</span>}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Inline Add Party Panel for 3-Way Calling */}
            {showAddPartyPanel && (
              <InlineAddPartyPanel
                participants={conferenceParticipants}
                onMuteToggle={handleMuteToggle}
                onHoldToggle={handleParticipantHoldToggle}
                onRemoveParticipant={handleRemoveParticipant}
                onAddParticipant={handleAddParticipant}
                isAddingParticipant={isAddingParticipant}
                loadingParticipantSid={loadingParticipantSid || undefined}
                onClose={() => setShowAddPartyPanel(false)}
                className="mt-3"
              />
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-4 gap-2">
              <Button
                onClick={() => setShowSmsModal(true)}
                disabled={!stableConnectedLine.lead.phone}
                size="sm"
                variant="outline"
                className="border-blue-200 hover:bg-blue-50"
              >
                <MessageSquare className="h-4 w-4 mr-1" />
                SMS
              </Button>
              <Button
                onClick={() => setShowEmailModal(true)}
                disabled={!stableConnectedLine.lead.email}
                size="sm"
                variant="outline"
                className="border-primary/20 hover:bg-primary/5"
              >
                <Mail className="h-4 w-4 mr-1" />
                Email
              </Button>
              <Button
                onClick={() => setShowAppointmentDialog(true)}
                size="sm"
                variant="outline"
                className="border-orange-200 hover:bg-orange-50"
              >
                <Calendar className="h-4 w-4 mr-1" />
                Appt
              </Button>
              <Button
                onClick={() => setShowReminderDialog(true)}
                size="sm"
                variant="outline"
                className="border-yellow-200 hover:bg-yellow-50"
              >
                <Bell className="h-4 w-4 mr-1" />
                Remind
              </Button>
            </div>

            {/* Insura-A-Bot Key Grabber and DTMF Polling */}
            {/* Use customer's callSid for InsuraABotKeyGrabber to find correct conference */}
            <InsuraABotKeyGrabber
              codes={insuraABotKeyCodes}
              isDTMFListening={isDTMFListening}
              setIsDTMFListening={setIsDTMFListening}
              callId={stableConnectedLine.callSid || activeCallSid}
              isSpeechProcessing={isSpeechProcessing}
              onClearCodes={handleClearCodes}
            />
            {/* DTMF Realtime - invisible component that receives customer keypresses via Supabase Realtime */}
            <DTMFPollingWrapper
              ref={dtmfPollingRef}
              callSid={stableConnectedLine.callSid || activeCallSid || null}
              enabled={isDTMFListening && !!(stableConnectedLine.callSid || activeCallSid)}
              userId={user?.user?.id || null}
              onDigitsUpdate={handleDTMFUpdate}
              onSpeechProcessingUpdate={setIsSpeechProcessing}
            />

            {/* Lead Details Panel - use stable key to prevent remounts during polling */}
            <LeadDetailsPanel
              key={`connected-panel-${stableConnectedLeadId}`}
              leadId={stableConnectedLine.lead.id}
              showCloseButton={false}
              compact={false}
              embedded={true}
              hideCallButton={true}
              onStatusChange={(leadId, status) => {
                // Track lead status for session stats (Appt, Underwriting, Sold & Approved)
                setLeadStatuses(prev => ({
                  ...prev,
                  [leadId]: status,
                }));
                // Also update the ref immediately to ensure it's captured in completion timeout
                leadStatusesRef.current = { ...leadStatusesRef.current, [leadId]: status };
              }}
            />
          </CardContent>
        </Card>
      ) : selectedLineIndex !== null && lines[selectedLineIndex] ? (
        /* After call ends - show lead details for the last active lead */
        <Card className="border-2 border-gray-300 bg-gray-50/50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge className="bg-gray-100 text-gray-800">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Call Ended - {lines[selectedLineIndex].lead.name}
                </Badge>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedLineIndex(null)}
              >
                Close Details
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <LeadDetailsPanel
              leadId={lines[selectedLineIndex].lead.id}
              showCloseButton={false}
              compact={false}
              embedded={true}
              hideCallButton={true}
              onStatusChange={(leadId, status) => {
                // Track lead status for session stats (Appt, Underwriting, Sold & Approved)
                setLeadStatuses(prev => ({
                  ...prev,
                  [leadId]: status,
                }));
                // Also update the ref immediately to ensure it's captured in completion timeout
                leadStatusesRef.current = { ...leadStatusesRef.current, [leadId]: status };
              }}
            />
          </CardContent>
        </Card>
      ) : awaitingMultiLineDisposition ? (
        /* Post-Call Disposition View (widget) — shows who they talked to */
        (() => {
          const lastLead = lastActiveLeadId ? leads.find(l => l.id === lastActiveLeadId) : null;
          const lastLine = lines.find(l => l.lead?.id === lastActiveLeadId);
          // Same incoming-call fallback as the panel view above.
          const displayName = lastLead?.name
            || lastLine?.lead?.name
            || incomingPauseInfo?.name
            || 'Lead';
          const displayPhone = lastLead?.phone
            || lastLine?.lead?.phone
            || incomingPauseInfo?.number
            || "";
          return (
            <div className="bg-white rounded-lg border border-primary/30 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-gray-600 to-gray-700 p-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white">
                    <User className="h-4 w-4" />
                  </div>
                  <div className="text-white">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{displayName}</span>
                      <Badge className="bg-gray-500 text-white text-[9px] px-1.5 py-0 h-4 border-0 font-semibold">
                        Call Ended
                      </Badge>
                    </div>
                    <div className="text-xs text-white/70">
                      {formatPhoneNumberDisplay(displayPhone)}
                      {lastLine?.duration ? ` · ${Math.floor(lastLine.duration / 60)}:${String(lastLine.duration % 60).padStart(2, '0')}` : ''}
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-3 space-y-2">
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 h-9 text-sm font-medium"
                  disabled={isPreparingNextBatch}
                  onClick={() => {
                    if (isPreparingNextBatch) return;
                    awaitingMultiLineDispositionRef.current = false;
                    setAwaitingMultiLineDisposition(false);
                    isDialingNextRef.current = false;
                    lastDialTimeRef.current = 0;
                    isPostCallRef.current = false;
                    connectedLineIndexRef.current = null;
                    setConnectedLineIndex(null);
                    setIsPreparingNextBatch(true);
                    setIncomingPauseInfo(null);
                    linesRef.current = linesRef.current.map(l => ({ ...l, status: "idle" as const, duration: undefined }));
                    setLines(prev => prev.map(l => ({ ...l, status: "idle" as const, duration: undefined })));
                    const availableIndices = linesRef.current.map((_, i) => i);
                    dialNextLeads(availableIndices);
                    setTimeout(() => {
                      setIsPreparingNextBatch(false);
                    }, 3000);
                  }}
                >
                  {isPreparingNextBatch ? "Dialing..." : "Resume Multi-Line"}
                </Button>
                {/* Quick Disposition Buttons */}
                {(lastLead?.id || lastLine?.lead?.id) && (
                  <div className="grid grid-cols-3 gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-8 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={async () => {
                        const lid = lastLead?.id || lastLine?.lead?.id;
                        if (!lid) return;
                        try {
                          await updateLeadStatusMutation.mutateAsync({ leadId: lid, status: LeadStatus.BAD_NUMBER });
                        } catch {}
                      }}
                    >
                      Bad #
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-8 border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                      onClick={() => fileLastLead(lastLead?.id || lastLine?.lead?.id, LeadStatus.DO_NOT_CALL)}
                    >
                      DNC
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-8 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => {
                        const lid = lastLead?.id || lastLine?.lead?.id;
                        if (lid) noSaleLeadIdRef.current = lid;
                        setShowNoSaleModal(true);
                      }}
                    >
                      No Sale
                    </Button>
                  </div>
                )}
                {(lastLead?.id || lastLine?.lead?.id) ? (
                  <QuickDispositionButtons onPick={(status) => fileLastLead(lastLead?.id || lastLine?.lead?.id, status)} />
                ) : null}
                {(lastLead?.id || lastLine?.lead?.id) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs h-8"
                    onClick={() => router.push(`/leads/${lastLead?.id || lastLine?.lead?.id}`)}
                  >
                    <ExternalLink className="h-3 w-3 mr-2" />
                    View Lead Details
                  </Button>
                )}
              </div>
            </div>
          );
        })()
      ) : lines.length > 0 ? (
        /* When dialing/ringing - show compact grid of all leads (responsive: 1 col for 1 lead, 2 cols for 2+) */
        <>
          {/* Preparing next batch indicator */}
          <PreparingBatchIndicator
            isPreparingNextBatch={isPreparingNextBatch}
            preparingBatchTooLong={preparingBatchTooLong}
            dialingCount={dialingCount}
            onSkipRetry={() => {
              setIsPreparingNextBatch(false);
              isDialingNextRef.current = false;
              handleSkipAllRinging();
            }}
          />

          {/* Skip All button - shows when any lines are ringing */}
          {dialingCount > 0 && (
            <div className="flex justify-end mb-3">
              <Button
                size="sm"
                variant="outline"
                className="bg-white hover:bg-red-50 border-red-300 text-red-600 hover:text-red-700"
                onClick={handleSkipAllRinging}
                disabled={skipCooldownActive}
              >
                {skipCooldownActive ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Skipping...
                  </>
                ) : (
                  <>
                    <SkipForward className="h-4 w-4 mr-1" />
                    {dialingCount === 1 ? "Skip Ringing" : `Skip All Ringing (${dialingCount})`}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Reassuring message while lines are ringing */}
          {dialingCount > 0 && (
            <div className="flex items-center justify-center gap-2 py-2 px-3 mb-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-700">
              <div className="w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <span>Ringing {dialingCount} {dialingCount === 1 ? "line" : "lines"} — first person to answer connects to you</span>
            </div>
          )}

          {/* Lead Cards - show LeadDetailsCard for all dialing/ringing leads (compact view) */}
          {lines.length === 1 ? (
            /* Single lead - show LeadDetailsCard (compact) during dialing, NOT full panel */
            <div className="relative">
              {/* Status badge overlay - skip button removed, use "Skip All Ringing" at top instead */}
              <div className="absolute top-2 left-2 right-2 z-10 flex items-center justify-between">
                <Badge className={`text-xs ${getStatusColor(lines[0].status)}`}>
                  {getStatusIcon(lines[0].status)}
                  <span className="ml-1">Calling {lines[0].lead.name}</span>
                </Badge>
              </div>
              {/* Lead Details Card with status-based border */}
              <div className={`rounded-lg border-2 ${
                lines[0].status === "ringing" || lines[0].status === "dialing"
                  ? "border-blue-300"
                  : lines[0].status === "no-answer"
                    ? "border-orange-300"
                    : lines[0].status === "failed"
                      ? "border-red-300"
                      : "border-gray-200"
              }`}>
                <LeadDetailsCard lead={lines[0].lead} />
              </div>
            </div>
          ) : lines.length > 1 && (
            /* Multiple leads - show LeadDetailsCard for each */
            <div className={`grid gap-3 ${lines.length === 2 ? "grid-cols-1 md:grid-cols-2" : lines.length === 3 ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2"}`}>
              {lines.map((line, index) => (
                <div
                  key={`lead-details-${line.lead.id}`}
                  className="relative"
                >
                  {/* Status badge overlay - skip buttons removed, use "Skip All Ringing" at top instead */}
                  <div className="absolute top-2 left-2 right-2 z-10 flex items-center justify-between">
                    <Badge className={`text-xs ${getStatusColor(line.status)}`}>
                      {getStatusIcon(line.status)}
                      <span className="ml-1">Line {line.lineNumber}</span>
                    </Badge>
                  </div>
                  {/* Lead Details Card with status-based border */}
                  <div className={`rounded-lg border-2 ${
                    line.status === "ringing" || line.status === "dialing"
                      ? "border-blue-300"
                      : line.status === "no-answer"
                        ? "border-orange-300"
                        : line.status === "voicemail"
                          ? "border-amber-300"
                          : line.status === "failed"
                            ? "border-red-300"
                            : "border-gray-200"
                  }`}>
                    <LeadDetailsCard lead={line.lead} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        /* No leads */
        <Card className="border-2 border-gray-200">
          <CardContent className="p-4 text-center text-muted-foreground">
            No leads selected
          </CardContent>
        </Card>
      )}

      {/* Call Summary - Only show after all calls are done */}
      {completedCount === lines.length && (
        <Card className="border-2 border-green-200 bg-green-50/30">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Call Summary - All Calls Complete
            </h3>
            <ScrollArea className="h-48">
              <div className="space-y-2">
                {lines.map((line) => (
                  <div
                    key={line.lead.id}
                    className={`flex items-center justify-between p-3 rounded border ${
                      line.status === "disconnected"
                        ? "bg-green-50 border-green-200"
                        : line.status === "no-answer"
                          ? "bg-orange-50 border-orange-200"
                          : line.status === "voicemail"
                            ? "bg-amber-50 border-amber-200"
                            : line.status === "failed"
                              ? "bg-red-50 border-red-200"
                              : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {getStatusIcon(line.status)}
                      <div className="flex-1">
                        <div className="font-medium text-sm">{line.lead.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {formatPhoneNumberDisplay(line.lead.phone || "")}
                        </div>
                      </div>
                    </div>
                    <Badge className={`text-xs ${getStatusColor(line.status)}`}>
                      {line.status === "disconnected" ? "Ended" : line.status === "voicemail" ? "Voicemail" : line.status === "no-answer" ? "No Answer" : line.status === "failed" ? "No Answer" : line.status === "connected" ? "Answered" : line.status === "on-hold" ? "On Hold" : line.status === "idle" ? "Ready" : "Unknown"}
                    </Badge>
                  </div>
                ))}
              </div>
            </ScrollArea>
            <div className="mt-4 pt-3 border-t flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                Session complete. Click "Stop Session" to select more leads.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modals for Quick Actions */}
      {selectedLead && (
        <>
          <SendSmsModal
            lead={selectedLead}
            open={showSmsModal}
            onOpenChange={setShowSmsModal}
          />

          <SendEmailModal
            lead={selectedLead}
            open={showEmailModal}
            onOpenChange={setShowEmailModal}
          />

          <NewAppointmentDialog
            open={showAppointmentDialog}
            onOpenChange={setShowAppointmentDialog}
            preselectedLeadId={selectedLead.id}
            preselectedLeadName={selectedLead.name}
            preselectedLeadState={selectedLead.state}
            onStatusChange={(leadId, newStatus) => {
              // Track lead status for session stats when appointment is created
              setLeadStatuses(prev => ({
                ...prev,
                [leadId]: newStatus,
              }));
              // Also update the ref immediately to ensure it's captured in completion timeout
              leadStatusesRef.current = { ...leadStatusesRef.current, [leadId]: newStatus };
            }}
          />

          <NewReminderDialog
            open={showReminderDialog}
            onOpenChange={setShowReminderDialog}
          />
        </>
      )}

      {/* Missing Area Codes Notification - Bottom Right */}
      <BulkAreaCodeRecommendationNotification
        leads={leads}
        userAreaCodes={userAreaCodes}
        onPurchaseSuccess={() => {
          // Area codes purchased successfully
        }}
      />

      <NoSaleReasonModal
        isOpen={showNoSaleModal}
        onOpenChange={setShowNoSaleModal}
        onConfirm={handleNoSaleConfirm}
      />
      {dncModal}
    </div>
  );
};
